import axios from 'axios'
import { ThunkAction, ThunkDispatch } from 'redux-thunk'
import { VirtualCardTypes } from 'src/enums'
import {
  CreditCardDisplayStatus,
  CreditCardStatus,
  CreditCardType,
} from 'src/models/creditCard.models'
import { CreditCardPermissions } from 'src/models/creditCardPermissions.models'
import { VirtualCardStatus } from 'src/models/virtualCard.models'
import { VirtualCardPermissions } from 'src/models/virtualCardPermissions.models'
import { RootState } from 'src/redux/createRootReducer'
import { selectAuthedJsonApiRequestConfig } from 'src/redux/selectors/axios.selectors'
import {
  StatisticAggregateType,
  StatisticModelType,
} from 'src/requests/statistics.request'

export const GET_CREDIT_CARDS_ACTIVE_CAN_ISSUE_VIRTUAL_CARDS_COUNT_STATISTICS_INIT =
  'data/statistics/GET_CREDIT_CARDS_ACTIVE_CAN_ISSUE_VIRTUAL_CARDS_COUNT_STATISTICS_INIT'
export const GET_CREDIT_CARDS_ACTIVE_CAN_ISSUE_VIRTUAL_CARDS_COUNT_STATISTICS_SUCCESS =
  'data/statistics/GET_CREDIT_CARDS_ACTIVE_CAN_ISSUE_VIRTUAL_CARDS_COUNT_STATISTICS_SUCCESS'
export const GET_CREDIT_CARDS_ACTIVE_CAN_ISSUE_VIRTUAL_CARDS_COUNT_STATISTICS_FAILURE =
  'data/statistics/GET_CREDIT_CARDS_ACTIVE_CAN_ISSUE_VIRTUAL_CARDS_COUNT_STATISTICS_FAILURE'

export interface GetCreditCardsActiveCanIssueVirtualCardsCountStatisticsSuccessPayload {
  statistics: Array<{ value: number }>
  statisticNames: string[]
}

export interface GetCreditCardsActiveCanIssueVirtualCardsCountStatisticsInitAction {
  type: typeof GET_CREDIT_CARDS_ACTIVE_CAN_ISSUE_VIRTUAL_CARDS_COUNT_STATISTICS_INIT
}
export interface GetCreditCardsActiveCanIssueVirtualCardsCountStatisticsSuccessAction {
  type: typeof GET_CREDIT_CARDS_ACTIVE_CAN_ISSUE_VIRTUAL_CARDS_COUNT_STATISTICS_SUCCESS
  payload: GetCreditCardsActiveCanIssueVirtualCardsCountStatisticsSuccessPayload
}
export interface GetCreditCardsActiveCanIssueVirtualCardsCountStatisticsFailureAction {
  type: typeof GET_CREDIT_CARDS_ACTIVE_CAN_ISSUE_VIRTUAL_CARDS_COUNT_STATISTICS_FAILURE
  payload: unknown
}

export type GetCreditCardsActiveCanIssueVirtualCardsCountStatisticsActions =
  | GetCreditCardsActiveCanIssueVirtualCardsCountStatisticsInitAction
  | GetCreditCardsActiveCanIssueVirtualCardsCountStatisticsSuccessAction
  | GetCreditCardsActiveCanIssueVirtualCardsCountStatisticsFailureAction

const createGetCreditCardsActiveCanIssueVirtualCardsCountStatisticsInitAction =
  (): GetCreditCardsActiveCanIssueVirtualCardsCountStatisticsInitAction => ({
    type: GET_CREDIT_CARDS_ACTIVE_CAN_ISSUE_VIRTUAL_CARDS_COUNT_STATISTICS_INIT,
  })
const createGetCreditCardsActiveCanIssueVirtualCardsCountStatisticsSuccessAction =
  (
    payload: GetCreditCardsActiveCanIssueVirtualCardsCountStatisticsSuccessPayload,
  ): GetCreditCardsActiveCanIssueVirtualCardsCountStatisticsSuccessAction => ({
    type: GET_CREDIT_CARDS_ACTIVE_CAN_ISSUE_VIRTUAL_CARDS_COUNT_STATISTICS_SUCCESS,
    payload,
  })
const createGetCreditCardsActiveCanIssueVirtualCardsCountStatisticsFailureAction =
  (
    response: unknown,
  ): GetCreditCardsActiveCanIssueVirtualCardsCountStatisticsFailureAction => ({
    type: GET_CREDIT_CARDS_ACTIVE_CAN_ISSUE_VIRTUAL_CARDS_COUNT_STATISTICS_FAILURE,
    payload: response,
  })

export const getCreditCardsActiveCanIssueVirtualCardsCountStatistics =
  (): ThunkAction<
    Promise<
      | GetCreditCardsActiveCanIssueVirtualCardsCountStatisticsFailureAction
      | GetCreditCardsActiveCanIssueVirtualCardsCountStatisticsSuccessAction
    >,
    RootState,
    void,
    GetCreditCardsActiveCanIssueVirtualCardsCountStatisticsActions
  > => {
    return async (
      dispatch: ThunkDispatch<
        RootState,
        void,
        GetCreditCardsActiveCanIssueVirtualCardsCountStatisticsActions
      >,
      getState: () => RootState,
    ): Promise<
      | GetCreditCardsActiveCanIssueVirtualCardsCountStatisticsFailureAction
      | GetCreditCardsActiveCanIssueVirtualCardsCountStatisticsSuccessAction
    > => {
      dispatch(
        createGetCreditCardsActiveCanIssueVirtualCardsCountStatisticsInitAction(),
      )

      return axios
        .post<GetCreditCardsActiveCanIssueVirtualCardsCountStatisticsSuccessPayload>(
          `/statistics`,
          {
            statistics: [
              {
                agg: StatisticAggregateType.Count,
                type: StatisticModelType.CreditCard,
                query: {
                  status: CreditCardStatus.Active,
                  withPermission: CreditCardPermissions.IssueVirtualCards,
                },
              },
            ],
          },
          selectAuthedJsonApiRequestConfig(getState()),
        )
        .then(
          async (response) => {
            return dispatch(
              createGetCreditCardsActiveCanIssueVirtualCardsCountStatisticsSuccessAction(
                { ...response.data },
              ),
            )
          },
          (err) => {
            return dispatch(
              createGetCreditCardsActiveCanIssueVirtualCardsCountStatisticsFailureAction(
                err.response.data,
              ),
            )
          },
        )
    }
  }

export const GET_CREDIT_CARDS_ACTIVE_RECURRENCE_ENABLED_CAN_ISSUE_VIRTUAL_CARDS_COUNT_STATISTICS_INIT =
  'data/statistics/GET_CREDIT_CARDS_ACTIVE_RECURRENCE_ENABLED_CAN_ISSUE_VIRTUAL_CARDS_COUNT_STATISTICS_INIT'
export const GET_CREDIT_CARDS_ACTIVE_RECURRENCE_ENABLED_CAN_ISSUE_VIRTUAL_CARDS_COUNT_STATISTICS_SUCCESS =
  'data/statistics/GET_CREDIT_CARDS_ACTIVE_RECURRENCE_ENABLED_CAN_ISSUE_VIRTUAL_CARDS_COUNT_STATISTICS_SUCCESS'
export const GET_CREDIT_CARDS_ACTIVE_RECURRENCE_ENABLED_CAN_ISSUE_VIRTUAL_CARDS_COUNT_STATISTICS_FAILURE =
  'data/statistics/GET_CREDIT_CARDS_ACTIVE_RECURRENCE_ENABLED_CAN_ISSUE_VIRTUAL_CARDS_COUNT_STATISTICS_FAILURE'

export interface GetCreditCardsActiveRecurrenceEnabledCanIssueVirtualCardsCountStatisticsSuccessPayload {
  statistics: Array<{ value: number }>
  statisticNames: string[]
}

export interface GetCreditCardsActiveRecurrenceEnabledCanIssueVirtualCardsCountStatisticsInitAction {
  type: typeof GET_CREDIT_CARDS_ACTIVE_RECURRENCE_ENABLED_CAN_ISSUE_VIRTUAL_CARDS_COUNT_STATISTICS_INIT
}
export interface GetCreditCardsActiveRecurrenceEnabledCanIssueVirtualCardsCountStatisticsSuccessAction {
  type: typeof GET_CREDIT_CARDS_ACTIVE_RECURRENCE_ENABLED_CAN_ISSUE_VIRTUAL_CARDS_COUNT_STATISTICS_SUCCESS
  payload: GetCreditCardsActiveRecurrenceEnabledCanIssueVirtualCardsCountStatisticsSuccessPayload
}
export interface GetCreditCardsActiveRecurrenceEnabledCanIssueVirtualCardsCountStatisticsFailureAction {
  type: typeof GET_CREDIT_CARDS_ACTIVE_RECURRENCE_ENABLED_CAN_ISSUE_VIRTUAL_CARDS_COUNT_STATISTICS_FAILURE
  payload: unknown
}

export type GetCreditCardsActiveRecurrenceEnabledCanIssueVirtualCardsCountStatisticsActions =

    | GetCreditCardsActiveRecurrenceEnabledCanIssueVirtualCardsCountStatisticsInitAction
    | GetCreditCardsActiveRecurrenceEnabledCanIssueVirtualCardsCountStatisticsSuccessAction
    | GetCreditCardsActiveRecurrenceEnabledCanIssueVirtualCardsCountStatisticsFailureAction

const createGetCreditCardsActiveRecurrenceEnabledCanIssueVirtualCardsCountStatisticsInitAction =
  (): GetCreditCardsActiveRecurrenceEnabledCanIssueVirtualCardsCountStatisticsInitAction => ({
    type: GET_CREDIT_CARDS_ACTIVE_RECURRENCE_ENABLED_CAN_ISSUE_VIRTUAL_CARDS_COUNT_STATISTICS_INIT,
  })
const createGetCreditCardsActiveRecurrenceEnabledCanIssueVirtualCardsCountStatisticsSuccessAction =
  (
    payload: GetCreditCardsActiveRecurrenceEnabledCanIssueVirtualCardsCountStatisticsSuccessPayload,
  ): GetCreditCardsActiveRecurrenceEnabledCanIssueVirtualCardsCountStatisticsSuccessAction => ({
    type: GET_CREDIT_CARDS_ACTIVE_RECURRENCE_ENABLED_CAN_ISSUE_VIRTUAL_CARDS_COUNT_STATISTICS_SUCCESS,
    payload,
  })
const createGetCreditCardsActiveRecurrenceEnabledCanIssueVirtualCardsCountStatisticsFailureAction =
  (
    response: unknown,
  ): GetCreditCardsActiveRecurrenceEnabledCanIssueVirtualCardsCountStatisticsFailureAction => ({
    type: GET_CREDIT_CARDS_ACTIVE_RECURRENCE_ENABLED_CAN_ISSUE_VIRTUAL_CARDS_COUNT_STATISTICS_FAILURE,
    payload: response,
  })

export const getCreditCardsActiveRecurrenceEnabledCanIssueVirtualCardsCountStatistics =
  (): ThunkAction<
    Promise<
      | GetCreditCardsActiveRecurrenceEnabledCanIssueVirtualCardsCountStatisticsFailureAction
      | GetCreditCardsActiveRecurrenceEnabledCanIssueVirtualCardsCountStatisticsSuccessAction
    >,
    RootState,
    void,
    GetCreditCardsActiveRecurrenceEnabledCanIssueVirtualCardsCountStatisticsActions
  > => {
    return async (
      dispatch: ThunkDispatch<
        RootState,
        void,
        GetCreditCardsActiveRecurrenceEnabledCanIssueVirtualCardsCountStatisticsActions
      >,
      getState: () => RootState,
    ): Promise<
      | GetCreditCardsActiveRecurrenceEnabledCanIssueVirtualCardsCountStatisticsFailureAction
      | GetCreditCardsActiveRecurrenceEnabledCanIssueVirtualCardsCountStatisticsSuccessAction
    > => {
      dispatch(
        createGetCreditCardsActiveRecurrenceEnabledCanIssueVirtualCardsCountStatisticsInitAction(),
      )

      return axios
        .post<GetCreditCardsActiveRecurrenceEnabledCanIssueVirtualCardsCountStatisticsSuccessPayload>(
          `/statistics`,
          {
            statistics: [
              {
                agg: StatisticAggregateType.Count,
                type: StatisticModelType.CreditCard,
                query: {
                  recurrenceEnabled: true,
                  status: CreditCardStatus.Active,
                  withPermission: CreditCardPermissions.IssueVirtualCards,
                },
              },
            ],
          },
          selectAuthedJsonApiRequestConfig(getState()),
        )
        .then(
          async (response) => {
            return dispatch(
              createGetCreditCardsActiveRecurrenceEnabledCanIssueVirtualCardsCountStatisticsSuccessAction(
                { ...response.data },
              ),
            )
          },
          (err) => {
            return dispatch(
              createGetCreditCardsActiveRecurrenceEnabledCanIssueVirtualCardsCountStatisticsFailureAction(
                err.response.data,
              ),
            )
          },
        )
    }
  }

export const GET_CREDIT_CARDS_ACTIVE_BALANCE_LOCK_ENABLED_CAN_ISSUE_VIRTUAL_CARDS_COUNT_STATISTICS_INIT =
  'data/statistics/GET_CREDIT_CARDS_ACTIVE_BALANCE_LOCK_ENABLED_CAN_ISSUE_VIRTUAL_CARDS_COUNT_STATISTICS_INIT'
export const GET_CREDIT_CARDS_ACTIVE_BALANCE_LOCK_ENABLED_CAN_ISSUE_VIRTUAL_CARDS_COUNT_STATISTICS_SUCCESS =
  'data/statistics/GET_CREDIT_CARDS_ACTIVE_BALANCE_LOCK_ENABLED_CAN_ISSUE_VIRTUAL_CARDS_COUNT_STATISTICS_SUCCESS'
export const GET_CREDIT_CARDS_ACTIVE_BALANCE_LOCK_ENABLED_CAN_ISSUE_VIRTUAL_CARDS_COUNT_STATISTICS_FAILURE =
  'data/statistics/GET_CREDIT_CARDS_ACTIVE_BALANCE_LOCK_ENABLED_CAN_ISSUE_VIRTUAL_CARDS_COUNT_STATISTICS_FAILURE'

export interface GetCreditCardsActiveBalanceLockEnabledCanIssueVirtualCardsCountStatisticsSuccessPayload {
  statistics: Array<{ value: number }>
  statisticNames: string[]
}

export interface GetCreditCardsActiveBalanceLockEnabledCanIssueVirtualCardsCountStatisticsInitAction {
  type: typeof GET_CREDIT_CARDS_ACTIVE_BALANCE_LOCK_ENABLED_CAN_ISSUE_VIRTUAL_CARDS_COUNT_STATISTICS_INIT
}
export interface GetCreditCardsActiveBalanceLockEnabledCanIssueVirtualCardsCountStatisticsSuccessAction {
  type: typeof GET_CREDIT_CARDS_ACTIVE_BALANCE_LOCK_ENABLED_CAN_ISSUE_VIRTUAL_CARDS_COUNT_STATISTICS_SUCCESS
  payload: GetCreditCardsActiveBalanceLockEnabledCanIssueVirtualCardsCountStatisticsSuccessPayload
}
export interface GetCreditCardsActiveBalanceLockEnabledCanIssueVirtualCardsCountStatisticsFailureAction {
  type: typeof GET_CREDIT_CARDS_ACTIVE_BALANCE_LOCK_ENABLED_CAN_ISSUE_VIRTUAL_CARDS_COUNT_STATISTICS_FAILURE
  payload: unknown
}

export type GetCreditCardsActiveBalanceLockEnabledCanIssueVirtualCardsCountStatisticsActions =

    | GetCreditCardsActiveBalanceLockEnabledCanIssueVirtualCardsCountStatisticsInitAction
    | GetCreditCardsActiveBalanceLockEnabledCanIssueVirtualCardsCountStatisticsSuccessAction
    | GetCreditCardsActiveBalanceLockEnabledCanIssueVirtualCardsCountStatisticsFailureAction

const createGetCreditCardsActiveBalanceLockEnabledCanIssueVirtualCardsCountStatisticsInitAction =
  (): GetCreditCardsActiveBalanceLockEnabledCanIssueVirtualCardsCountStatisticsInitAction => ({
    type: GET_CREDIT_CARDS_ACTIVE_BALANCE_LOCK_ENABLED_CAN_ISSUE_VIRTUAL_CARDS_COUNT_STATISTICS_INIT,
  })
const createGetCreditCardsActiveBalanceLockEnabledCanIssueVirtualCardsCountStatisticsSuccessAction =
  (
    payload: GetCreditCardsActiveBalanceLockEnabledCanIssueVirtualCardsCountStatisticsSuccessPayload,
  ): GetCreditCardsActiveBalanceLockEnabledCanIssueVirtualCardsCountStatisticsSuccessAction => ({
    type: GET_CREDIT_CARDS_ACTIVE_BALANCE_LOCK_ENABLED_CAN_ISSUE_VIRTUAL_CARDS_COUNT_STATISTICS_SUCCESS,
    payload,
  })
const createGetCreditCardsActiveBalanceLockEnabledCanIssueVirtualCardsCountStatisticsFailureAction =
  (
    response: unknown,
  ): GetCreditCardsActiveBalanceLockEnabledCanIssueVirtualCardsCountStatisticsFailureAction => ({
    type: GET_CREDIT_CARDS_ACTIVE_BALANCE_LOCK_ENABLED_CAN_ISSUE_VIRTUAL_CARDS_COUNT_STATISTICS_FAILURE,
    payload: response,
  })

export const getCreditCardsActiveBalanceLockEnabledCanIssueVirtualCardsCountStatistics =
  (): ThunkAction<
    Promise<
      | GetCreditCardsActiveBalanceLockEnabledCanIssueVirtualCardsCountStatisticsFailureAction
      | GetCreditCardsActiveBalanceLockEnabledCanIssueVirtualCardsCountStatisticsSuccessAction
    >,
    RootState,
    void,
    GetCreditCardsActiveBalanceLockEnabledCanIssueVirtualCardsCountStatisticsActions
  > => {
    return async (
      dispatch: ThunkDispatch<
        RootState,
        void,
        GetCreditCardsActiveBalanceLockEnabledCanIssueVirtualCardsCountStatisticsActions
      >,
      getState: () => RootState,
    ): Promise<
      | GetCreditCardsActiveBalanceLockEnabledCanIssueVirtualCardsCountStatisticsFailureAction
      | GetCreditCardsActiveBalanceLockEnabledCanIssueVirtualCardsCountStatisticsSuccessAction
    > => {
      dispatch(
        createGetCreditCardsActiveBalanceLockEnabledCanIssueVirtualCardsCountStatisticsInitAction(),
      )

      return axios
        .post<GetCreditCardsActiveBalanceLockEnabledCanIssueVirtualCardsCountStatisticsSuccessPayload>(
          `/statistics`,
          {
            statistics: [
              {
                agg: StatisticAggregateType.Count,
                type: StatisticModelType.CreditCard,
                query: {
                  balanceLockedEnabled: true,
                  status: CreditCardStatus.Active,
                  withPermission: CreditCardPermissions.IssueVirtualCards,
                },
              },
            ],
          },
          selectAuthedJsonApiRequestConfig(getState()),
        )
        .then(
          async (response) => {
            return dispatch(
              createGetCreditCardsActiveBalanceLockEnabledCanIssueVirtualCardsCountStatisticsSuccessAction(
                { ...response.data },
              ),
            )
          },
          (err) => {
            return dispatch(
              createGetCreditCardsActiveBalanceLockEnabledCanIssueVirtualCardsCountStatisticsFailureAction(
                err.response.data,
              ),
            )
          },
        )
    }
  }

export const GET_CREDIT_CARDS_ACTIVE_SOURCE_BALANCE_LOCK_ENABLED_CAN_ISSUE_VIRTUAL_CARDS_COUNT_STATISTICS_INIT =
  'data/statistics/GET_CREDIT_CARDS_ACTIVE_SOURCE_BALANCE_LOCK_ENABLED_CAN_ISSUE_VIRTUAL_CARDS_COUNT_STATISTICS_INIT'
export const GET_CREDIT_CARDS_ACTIVE_SOURCE_BALANCE_LOCK_ENABLED_CAN_ISSUE_VIRTUAL_CARDS_COUNT_STATISTICS_SUCCESS =
  'data/statistics/GET_CREDIT_CARDS_ACTIVE_SOURCE_BALANCE_LOCK_ENABLED_CAN_ISSUE_VIRTUAL_CARDS_COUNT_STATISTICS_SUCCESS'
export const GET_CREDIT_CARDS_ACTIVE_SOURCE_BALANCE_LOCK_ENABLED_CAN_ISSUE_VIRTUAL_CARDS_COUNT_STATISTICS_FAILURE =
  'data/statistics/GET_CREDIT_CARDS_ACTIVE_SOURCE_BALANCE_LOCK_ENABLED_CAN_ISSUE_VIRTUAL_CARDS_COUNT_STATISTICS_FAILURE'

export interface GetCreditCardsActiveSourceBalanceLockEnabledCanIssueVirtualCardsCountStatisticsSuccessPayload {
  statistics: Array<{ value: number }>
  statisticNames: string[]
}

export interface GetCreditCardsActiveSourceBalanceLockEnabledCanIssueVirtualCardsCountStatisticsInitAction {
  type: typeof GET_CREDIT_CARDS_ACTIVE_SOURCE_BALANCE_LOCK_ENABLED_CAN_ISSUE_VIRTUAL_CARDS_COUNT_STATISTICS_INIT
}
export interface GetCreditCardsActiveSourceBalanceLockEnabledCanIssueVirtualCardsCountStatisticsSuccessAction {
  type: typeof GET_CREDIT_CARDS_ACTIVE_SOURCE_BALANCE_LOCK_ENABLED_CAN_ISSUE_VIRTUAL_CARDS_COUNT_STATISTICS_SUCCESS
  payload: GetCreditCardsActiveSourceBalanceLockEnabledCanIssueVirtualCardsCountStatisticsSuccessPayload
}
export interface GetCreditCardsActiveSourceBalanceLockEnabledCanIssueVirtualCardsCountStatisticsFailureAction {
  type: typeof GET_CREDIT_CARDS_ACTIVE_SOURCE_BALANCE_LOCK_ENABLED_CAN_ISSUE_VIRTUAL_CARDS_COUNT_STATISTICS_FAILURE
  payload: unknown
}

export type GetCreditCardsActiveSourceBalanceLockEnabledCanIssueVirtualCardsCountStatisticsActions =

    | GetCreditCardsActiveSourceBalanceLockEnabledCanIssueVirtualCardsCountStatisticsInitAction
    | GetCreditCardsActiveSourceBalanceLockEnabledCanIssueVirtualCardsCountStatisticsSuccessAction
    | GetCreditCardsActiveSourceBalanceLockEnabledCanIssueVirtualCardsCountStatisticsFailureAction

const createGetCreditCardsActiveSourceBalanceLockEnabledCanIssueVirtualCardsCountStatisticsInitAction =
  (): GetCreditCardsActiveSourceBalanceLockEnabledCanIssueVirtualCardsCountStatisticsInitAction => ({
    type: GET_CREDIT_CARDS_ACTIVE_SOURCE_BALANCE_LOCK_ENABLED_CAN_ISSUE_VIRTUAL_CARDS_COUNT_STATISTICS_INIT,
  })
const createGetCreditCardsActiveSourceBalanceLockEnabledCanIssueVirtualCardsCountStatisticsSuccessAction =
  (
    payload: GetCreditCardsActiveSourceBalanceLockEnabledCanIssueVirtualCardsCountStatisticsSuccessPayload,
  ): GetCreditCardsActiveSourceBalanceLockEnabledCanIssueVirtualCardsCountStatisticsSuccessAction => ({
    type: GET_CREDIT_CARDS_ACTIVE_SOURCE_BALANCE_LOCK_ENABLED_CAN_ISSUE_VIRTUAL_CARDS_COUNT_STATISTICS_SUCCESS,
    payload,
  })
const createGetCreditCardsActiveSourceBalanceLockEnabledCanIssueVirtualCardsCountStatisticsFailureAction =
  (
    response: unknown,
  ): GetCreditCardsActiveSourceBalanceLockEnabledCanIssueVirtualCardsCountStatisticsFailureAction => ({
    type: GET_CREDIT_CARDS_ACTIVE_SOURCE_BALANCE_LOCK_ENABLED_CAN_ISSUE_VIRTUAL_CARDS_COUNT_STATISTICS_FAILURE,
    payload: response,
  })

export const getCreditCardsActiveSourceBalanceLockEnabledCanIssueVirtualCardsCountStatistics =
  (): ThunkAction<
    Promise<
      | GetCreditCardsActiveSourceBalanceLockEnabledCanIssueVirtualCardsCountStatisticsFailureAction
      | GetCreditCardsActiveSourceBalanceLockEnabledCanIssueVirtualCardsCountStatisticsSuccessAction
    >,
    RootState,
    void,
    GetCreditCardsActiveSourceBalanceLockEnabledCanIssueVirtualCardsCountStatisticsActions
  > => {
    return async (
      dispatch: ThunkDispatch<
        RootState,
        void,
        GetCreditCardsActiveSourceBalanceLockEnabledCanIssueVirtualCardsCountStatisticsActions
      >,
      getState: () => RootState,
    ): Promise<
      | GetCreditCardsActiveSourceBalanceLockEnabledCanIssueVirtualCardsCountStatisticsFailureAction
      | GetCreditCardsActiveSourceBalanceLockEnabledCanIssueVirtualCardsCountStatisticsSuccessAction
    > => {
      dispatch(
        createGetCreditCardsActiveSourceBalanceLockEnabledCanIssueVirtualCardsCountStatisticsInitAction(),
      )

      return axios
        .post<GetCreditCardsActiveSourceBalanceLockEnabledCanIssueVirtualCardsCountStatisticsSuccessPayload>(
          `/statistics`,
          {
            statistics: [
              {
                agg: StatisticAggregateType.Count,
                type: StatisticModelType.CreditCard,
                query: {
                  balanceLockedEnabled: true,
                  status: CreditCardStatus.Active,
                  withPermission: CreditCardPermissions.IssueVirtualCards,
                  type: CreditCardType.Source,
                },
              },
            ],
          },
          selectAuthedJsonApiRequestConfig(getState()),
        )
        .then(
          async (response) => {
            return dispatch(
              createGetCreditCardsActiveSourceBalanceLockEnabledCanIssueVirtualCardsCountStatisticsSuccessAction(
                { ...response.data },
              ),
            )
          },
          (err) => {
            return dispatch(
              createGetCreditCardsActiveSourceBalanceLockEnabledCanIssueVirtualCardsCountStatisticsFailureAction(
                err.response.data,
              ),
            )
          },
        )
    }
  }

export const GET_CREDIT_CARDS_ACTIVE_DIRECT_ENABLED_CAN_ISSUE_VIRTUAL_CARDS_COUNT_STATISTICS_INIT =
  'data/statistics/GET_CREDIT_CARDS_ACTIVE_DIRECT_ENABLED_CAN_ISSUE_VIRTUAL_CARDS_COUNT_STATISTICS_INIT'
export const GET_CREDIT_CARDS_ACTIVE_DIRECT_ENABLED_CAN_ISSUE_VIRTUAL_CARDS_COUNT_STATISTICS_SUCCESS =
  'data/statistics/GET_CREDIT_CARDS_ACTIVE_DIRECT_ENABLED_CAN_ISSUE_VIRTUAL_CARDS_COUNT_STATISTICS_SUCCESS'
export const GET_CREDIT_CARDS_ACTIVE_DIRECT_ENABLED_CAN_ISSUE_VIRTUAL_CARDS_COUNT_STATISTICS_FAILURE =
  'data/statistics/GET_CREDIT_CARDS_ACTIVE_DIRECT_ENABLED_CAN_ISSUE_VIRTUAL_CARDS_COUNT_STATISTICS_FAILURE'

export interface GetCreditCardsActiveDirectEnabledCanIssueVirtualCardsCountStatisticsSuccessPayload {
  statistics: Array<{ value: number }>
  statisticNames: string[]
}

export interface GetCreditCardsActiveDirectEnabledCanIssueVirtualCardsCountStatisticsInitAction {
  type: typeof GET_CREDIT_CARDS_ACTIVE_DIRECT_ENABLED_CAN_ISSUE_VIRTUAL_CARDS_COUNT_STATISTICS_INIT
}
export interface GetCreditCardsActiveDirectEnabledCanIssueVirtualCardsCountStatisticsSuccessAction {
  type: typeof GET_CREDIT_CARDS_ACTIVE_DIRECT_ENABLED_CAN_ISSUE_VIRTUAL_CARDS_COUNT_STATISTICS_SUCCESS
  payload: GetCreditCardsActiveDirectEnabledCanIssueVirtualCardsCountStatisticsSuccessPayload
}
export interface GetCreditCardsActiveDirectEnabledCanIssueVirtualCardsCountStatisticsFailureAction {
  type: typeof GET_CREDIT_CARDS_ACTIVE_DIRECT_ENABLED_CAN_ISSUE_VIRTUAL_CARDS_COUNT_STATISTICS_FAILURE
  payload: unknown
}

export type GetCreditCardsActiveDirectEnabledCanIssueVirtualCardsCountStatisticsActions =

    | GetCreditCardsActiveDirectEnabledCanIssueVirtualCardsCountStatisticsInitAction
    | GetCreditCardsActiveDirectEnabledCanIssueVirtualCardsCountStatisticsSuccessAction
    | GetCreditCardsActiveDirectEnabledCanIssueVirtualCardsCountStatisticsFailureAction

const createGetCreditCardsActiveDirectEnabledCanIssueVirtualCardsCountStatisticsInitAction =
  (): GetCreditCardsActiveDirectEnabledCanIssueVirtualCardsCountStatisticsInitAction => ({
    type: GET_CREDIT_CARDS_ACTIVE_DIRECT_ENABLED_CAN_ISSUE_VIRTUAL_CARDS_COUNT_STATISTICS_INIT,
  })
const createGetCreditCardsActiveDirectEnabledCanIssueVirtualCardsCountStatisticsSuccessAction =
  (
    payload: GetCreditCardsActiveDirectEnabledCanIssueVirtualCardsCountStatisticsSuccessPayload,
  ): GetCreditCardsActiveDirectEnabledCanIssueVirtualCardsCountStatisticsSuccessAction => ({
    type: GET_CREDIT_CARDS_ACTIVE_DIRECT_ENABLED_CAN_ISSUE_VIRTUAL_CARDS_COUNT_STATISTICS_SUCCESS,
    payload,
  })
const createGetCreditCardsActiveDirectEnabledCanIssueVirtualCardsCountStatisticsFailureAction =
  (
    response: unknown,
  ): GetCreditCardsActiveDirectEnabledCanIssueVirtualCardsCountStatisticsFailureAction => ({
    type: GET_CREDIT_CARDS_ACTIVE_DIRECT_ENABLED_CAN_ISSUE_VIRTUAL_CARDS_COUNT_STATISTICS_FAILURE,
    payload: response,
  })

export const getCreditCardsActiveDirectEnabledCanIssueVirtualCardsCountStatistics =
  (): ThunkAction<
    Promise<
      | GetCreditCardsActiveDirectEnabledCanIssueVirtualCardsCountStatisticsFailureAction
      | GetCreditCardsActiveDirectEnabledCanIssueVirtualCardsCountStatisticsSuccessAction
    >,
    RootState,
    void,
    GetCreditCardsActiveDirectEnabledCanIssueVirtualCardsCountStatisticsActions
  > => {
    return async (
      dispatch: ThunkDispatch<
        RootState,
        void,
        GetCreditCardsActiveDirectEnabledCanIssueVirtualCardsCountStatisticsActions
      >,
      getState: () => RootState,
    ): Promise<
      | GetCreditCardsActiveDirectEnabledCanIssueVirtualCardsCountStatisticsFailureAction
      | GetCreditCardsActiveDirectEnabledCanIssueVirtualCardsCountStatisticsSuccessAction
    > => {
      dispatch(
        createGetCreditCardsActiveDirectEnabledCanIssueVirtualCardsCountStatisticsInitAction(),
      )

      return axios
        .post<GetCreditCardsActiveDirectEnabledCanIssueVirtualCardsCountStatisticsSuccessPayload>(
          `/statistics`,
          {
            statistics: [
              {
                agg: StatisticAggregateType.Count,
                type: StatisticModelType.CreditCard,
                query: {
                  directEnabled: true,
                  status: CreditCardStatus.Active,
                  withPermission: CreditCardPermissions.IssueVirtualCards,
                },
              },
            ],
          },
          selectAuthedJsonApiRequestConfig(getState()),
        )
        .then(
          async (response) => {
            return dispatch(
              createGetCreditCardsActiveDirectEnabledCanIssueVirtualCardsCountStatisticsSuccessAction(
                { ...response.data },
              ),
            )
          },
          (err) => {
            return dispatch(
              createGetCreditCardsActiveDirectEnabledCanIssueVirtualCardsCountStatisticsFailureAction(
                err.response.data,
              ),
            )
          },
        )
    }
  }

export const GET_CREDIT_CARDS_ACTIVE_SOURCE_CAN_ISSUE_VIRTUAL_CARDS_COUNT_STATISTICS_INIT =
  'data/statistics/GET_CREDIT_CARDS_ACTIVE_SOURCE_CAN_ISSUE_VIRTUAL_CARDS_COUNT_STATISTICS_INIT'
export const GET_CREDIT_CARDS_ACTIVE_SOURCE_CAN_ISSUE_VIRTUAL_CARDS_COUNT_STATISTICS_SUCCESS =
  'data/statistics/GET_CREDIT_CARDS_ACTIVE_SOURCE_CAN_ISSUE_VIRTUAL_CARDS_COUNT_STATISTICS_SUCCESS'
export const GET_CREDIT_CARDS_ACTIVE_SOURCE_CAN_ISSUE_VIRTUAL_CARDS_COUNT_STATISTICS_FAILURE =
  'data/statistics/GET_CREDIT_CARDS_ACTIVE_SOURCE_CAN_ISSUE_VIRTUAL_CARDS_COUNT_STATISTICS_FAILURE'

export interface GetCreditCardsActiveSourceCanIssueVirtualCardsCountStatisticsSuccessPayload {
  statistics: Array<{ value: number }>
  statisticNames: string[]
}

export interface GetCreditCardsActiveSourceCanIssueVirtualCardsCountStatisticsInitAction {
  type: typeof GET_CREDIT_CARDS_ACTIVE_SOURCE_CAN_ISSUE_VIRTUAL_CARDS_COUNT_STATISTICS_INIT
}
export interface GetCreditCardsActiveSourceCanIssueVirtualCardsCountStatisticsSuccessAction {
  type: typeof GET_CREDIT_CARDS_ACTIVE_SOURCE_CAN_ISSUE_VIRTUAL_CARDS_COUNT_STATISTICS_SUCCESS
  payload: GetCreditCardsActiveSourceCanIssueVirtualCardsCountStatisticsSuccessPayload
}
export interface GetCreditCardsActiveSourceCanIssueVirtualCardsCountStatisticsFailureAction {
  type: typeof GET_CREDIT_CARDS_ACTIVE_SOURCE_CAN_ISSUE_VIRTUAL_CARDS_COUNT_STATISTICS_FAILURE
  payload: unknown
}

export type GetCreditCardsActiveSourceCanIssueVirtualCardsCountStatisticsActions =

    | GetCreditCardsActiveSourceCanIssueVirtualCardsCountStatisticsInitAction
    | GetCreditCardsActiveSourceCanIssueVirtualCardsCountStatisticsSuccessAction
    | GetCreditCardsActiveSourceCanIssueVirtualCardsCountStatisticsFailureAction

const createGetCreditCardsActiveSourceCanIssueVirtualCardsCountStatisticsInitAction =
  (): GetCreditCardsActiveSourceCanIssueVirtualCardsCountStatisticsInitAction => ({
    type: GET_CREDIT_CARDS_ACTIVE_SOURCE_CAN_ISSUE_VIRTUAL_CARDS_COUNT_STATISTICS_INIT,
  })
const createGetCreditCardsActiveSourceCanIssueVirtualCardsCountStatisticsSuccessAction =
  (
    payload: GetCreditCardsActiveSourceCanIssueVirtualCardsCountStatisticsSuccessPayload,
  ): GetCreditCardsActiveSourceCanIssueVirtualCardsCountStatisticsSuccessAction => ({
    type: GET_CREDIT_CARDS_ACTIVE_SOURCE_CAN_ISSUE_VIRTUAL_CARDS_COUNT_STATISTICS_SUCCESS,
    payload,
  })
const createGetCreditCardsActiveSourceCanIssueVirtualCardsCountStatisticsFailureAction =
  (
    response: unknown,
  ): GetCreditCardsActiveSourceCanIssueVirtualCardsCountStatisticsFailureAction => ({
    type: GET_CREDIT_CARDS_ACTIVE_SOURCE_CAN_ISSUE_VIRTUAL_CARDS_COUNT_STATISTICS_FAILURE,
    payload: response,
  })

export const getCreditCardsActiveSourceCanIssueVirtualCardsCountStatistics =
  (): ThunkAction<
    Promise<
      | GetCreditCardsActiveSourceCanIssueVirtualCardsCountStatisticsFailureAction
      | GetCreditCardsActiveSourceCanIssueVirtualCardsCountStatisticsSuccessAction
    >,
    RootState,
    void,
    GetCreditCardsActiveSourceCanIssueVirtualCardsCountStatisticsActions
  > => {
    return async (
      dispatch: ThunkDispatch<
        RootState,
        void,
        GetCreditCardsActiveSourceCanIssueVirtualCardsCountStatisticsActions
      >,
      getState: () => RootState,
    ): Promise<
      | GetCreditCardsActiveSourceCanIssueVirtualCardsCountStatisticsFailureAction
      | GetCreditCardsActiveSourceCanIssueVirtualCardsCountStatisticsSuccessAction
    > => {
      dispatch(
        createGetCreditCardsActiveSourceCanIssueVirtualCardsCountStatisticsInitAction(),
      )

      return axios
        .post<GetCreditCardsActiveSourceCanIssueVirtualCardsCountStatisticsSuccessPayload>(
          `/statistics`,
          {
            statistics: [
              {
                agg: StatisticAggregateType.Count,
                type: StatisticModelType.CreditCard,
                query: {
                  status: CreditCardStatus.Active,
                  withPermission: CreditCardPermissions.IssueVirtualCards,
                  type: CreditCardType.Source,
                },
              },
            ],
          },
          selectAuthedJsonApiRequestConfig(getState()),
        )
        .then(
          async (response) => {
            return dispatch(
              createGetCreditCardsActiveSourceCanIssueVirtualCardsCountStatisticsSuccessAction(
                { ...response.data },
              ),
            )
          },
          (err) => {
            return dispatch(
              createGetCreditCardsActiveSourceCanIssueVirtualCardsCountStatisticsFailureAction(
                err.response.data,
              ),
            )
          },
        )
    }
  }

export const GET_CREDIT_CARDS_ACTIVE_SOURCE_RECURRENCE_ENABLED_CAN_ISSUE_VIRTUAL_CARDS_COUNT_STATISTICS_INIT =
  'data/statistics/GET_CREDIT_CARDS_ACTIVE_SOURCE_RECURRENCE_ENABLED_CAN_ISSUE_VIRTUAL_CARDS_COUNT_STATISTICS_INIT'
export const GET_CREDIT_CARDS_ACTIVE_SOURCE_RECURRENCE_ENABLED_CAN_ISSUE_VIRTUAL_CARDS_COUNT_STATISTICS_SUCCESS =
  'data/statistics/GET_CREDIT_CARDS_ACTIVE_SOURCE_RECURRENCE_ENABLED_CAN_ISSUE_VIRTUAL_CARDS_COUNT_STATISTICS_SUCCESS'
export const GET_CREDIT_CARDS_ACTIVE_SOURCE_RECURRENCE_ENABLED_CAN_ISSUE_VIRTUAL_CARDS_COUNT_STATISTICS_FAILURE =
  'data/statistics/GET_CREDIT_CARDS_ACTIVE_SOURCE_RECURRENCE_ENABLED_CAN_ISSUE_VIRTUAL_CARDS_COUNT_STATISTICS_FAILURE'

export interface GetCreditCardsActiveSourceRecurrenceEnabledCanIssueVirtualCardsCountStatisticsSuccessPayload {
  statistics: Array<{ value: number }>
  statisticNames: string[]
}

export interface GetCreditCardsActiveSourceRecurrenceEnabledCanIssueVirtualCardsCountStatisticsInitAction {
  type: typeof GET_CREDIT_CARDS_ACTIVE_SOURCE_RECURRENCE_ENABLED_CAN_ISSUE_VIRTUAL_CARDS_COUNT_STATISTICS_INIT
}
export interface GetCreditCardsActiveSourceRecurrenceEnabledCanIssueVirtualCardsCountStatisticsSuccessAction {
  type: typeof GET_CREDIT_CARDS_ACTIVE_SOURCE_RECURRENCE_ENABLED_CAN_ISSUE_VIRTUAL_CARDS_COUNT_STATISTICS_SUCCESS
  payload: GetCreditCardsActiveSourceRecurrenceEnabledCanIssueVirtualCardsCountStatisticsSuccessPayload
}
export interface GetCreditCardsActiveSourceRecurrenceEnabledCanIssueVirtualCardsCountStatisticsFailureAction {
  type: typeof GET_CREDIT_CARDS_ACTIVE_SOURCE_RECURRENCE_ENABLED_CAN_ISSUE_VIRTUAL_CARDS_COUNT_STATISTICS_FAILURE
  payload: unknown
}

export type GetCreditCardsActiveSourceRecurrenceEnabledCanIssueVirtualCardsCountStatisticsActions =

    | GetCreditCardsActiveSourceRecurrenceEnabledCanIssueVirtualCardsCountStatisticsInitAction
    | GetCreditCardsActiveSourceRecurrenceEnabledCanIssueVirtualCardsCountStatisticsSuccessAction
    | GetCreditCardsActiveSourceRecurrenceEnabledCanIssueVirtualCardsCountStatisticsFailureAction

const createGetCreditCardsActiveSourceRecurrenceEnabledCanIssueVirtualCardsCountStatisticsInitAction =
  (): GetCreditCardsActiveSourceRecurrenceEnabledCanIssueVirtualCardsCountStatisticsInitAction => ({
    type: GET_CREDIT_CARDS_ACTIVE_SOURCE_RECURRENCE_ENABLED_CAN_ISSUE_VIRTUAL_CARDS_COUNT_STATISTICS_INIT,
  })
const createGetCreditCardsActiveSourceRecurrenceEnabledCanIssueVirtualCardsCountStatisticsSuccessAction =
  (
    payload: GetCreditCardsActiveSourceRecurrenceEnabledCanIssueVirtualCardsCountStatisticsSuccessPayload,
  ): GetCreditCardsActiveSourceRecurrenceEnabledCanIssueVirtualCardsCountStatisticsSuccessAction => ({
    type: GET_CREDIT_CARDS_ACTIVE_SOURCE_RECURRENCE_ENABLED_CAN_ISSUE_VIRTUAL_CARDS_COUNT_STATISTICS_SUCCESS,
    payload,
  })
const createGetCreditCardsActiveSourceRecurrenceEnabledCanIssueVirtualCardsCountStatisticsFailureAction =
  (
    response: unknown,
  ): GetCreditCardsActiveSourceRecurrenceEnabledCanIssueVirtualCardsCountStatisticsFailureAction => ({
    type: GET_CREDIT_CARDS_ACTIVE_SOURCE_RECURRENCE_ENABLED_CAN_ISSUE_VIRTUAL_CARDS_COUNT_STATISTICS_FAILURE,
    payload: response,
  })

export const getCreditCardsActiveSourceRecurrenceEnabledCanIssueVirtualCardsCountStatistics =
  (): ThunkAction<
    Promise<
      | GetCreditCardsActiveSourceRecurrenceEnabledCanIssueVirtualCardsCountStatisticsFailureAction
      | GetCreditCardsActiveSourceRecurrenceEnabledCanIssueVirtualCardsCountStatisticsSuccessAction
    >,
    RootState,
    void,
    GetCreditCardsActiveSourceRecurrenceEnabledCanIssueVirtualCardsCountStatisticsActions
  > => {
    return async (
      dispatch: ThunkDispatch<
        RootState,
        void,
        GetCreditCardsActiveSourceRecurrenceEnabledCanIssueVirtualCardsCountStatisticsActions
      >,
      getState: () => RootState,
    ): Promise<
      | GetCreditCardsActiveSourceRecurrenceEnabledCanIssueVirtualCardsCountStatisticsFailureAction
      | GetCreditCardsActiveSourceRecurrenceEnabledCanIssueVirtualCardsCountStatisticsSuccessAction
    > => {
      dispatch(
        createGetCreditCardsActiveSourceRecurrenceEnabledCanIssueVirtualCardsCountStatisticsInitAction(),
      )

      return axios
        .post<GetCreditCardsActiveSourceRecurrenceEnabledCanIssueVirtualCardsCountStatisticsSuccessPayload>(
          `/statistics`,
          {
            statistics: [
              {
                agg: StatisticAggregateType.Count,
                type: StatisticModelType.CreditCard,
                query: {
                  recurrenceEnabled: true,
                  status: CreditCardStatus.Active,
                  withPermission: CreditCardPermissions.IssueVirtualCards,
                  type: CreditCardType.Source,
                },
              },
            ],
          },
          selectAuthedJsonApiRequestConfig(getState()),
        )
        .then(
          async (response) => {
            return dispatch(
              createGetCreditCardsActiveSourceRecurrenceEnabledCanIssueVirtualCardsCountStatisticsSuccessAction(
                { ...response.data },
              ),
            )
          },
          (err) => {
            return dispatch(
              createGetCreditCardsActiveSourceRecurrenceEnabledCanIssueVirtualCardsCountStatisticsFailureAction(
                err.response.data,
              ),
            )
          },
        )
    }
  }

export const GET_CREDIT_CARDS_ACTIVE_STANDARD_CAN_ISSUE_VIRTUAL_CARDS_COUNT_STATISTICS_INIT =
  'data/statistics/GET_CREDIT_CARDS_ACTIVE_STANDARD_CAN_ISSUE_VIRTUAL_CARDS_COUNT_STATISTICS_INIT'
export const GET_CREDIT_CARDS_ACTIVE_STANDARD_CAN_ISSUE_VIRTUAL_CARDS_COUNT_STATISTICS_SUCCESS =
  'data/statistics/GET_CREDIT_CARDS_ACTIVE_STANDARD_CAN_ISSUE_VIRTUAL_CARDS_COUNT_STATISTICS_SUCCESS'
export const GET_CREDIT_CARDS_ACTIVE_STANDARD_CAN_ISSUE_VIRTUAL_CARDS_COUNT_STATISTICS_FAILURE =
  'data/statistics/GET_CREDIT_CARDS_ACTIVE_STANDARD_CAN_ISSUE_VIRTUAL_CARDS_COUNT_STATISTICS_FAILURE'

export interface GetCreditCardsActiveStandardCanIssueVirtualCardsCountStatisticsSuccessPayload {
  statistics: Array<{ value: number }>
  statisticNames: string[]
}

export interface GetCreditCardsActiveStandardCanIssueVirtualCardsCountStatisticsInitAction {
  type: typeof GET_CREDIT_CARDS_ACTIVE_STANDARD_CAN_ISSUE_VIRTUAL_CARDS_COUNT_STATISTICS_INIT
}
export interface GetCreditCardsActiveStandardCanIssueVirtualCardsCountStatisticsSuccessAction {
  type: typeof GET_CREDIT_CARDS_ACTIVE_STANDARD_CAN_ISSUE_VIRTUAL_CARDS_COUNT_STATISTICS_SUCCESS
  payload: GetCreditCardsActiveStandardCanIssueVirtualCardsCountStatisticsSuccessPayload
}
export interface GetCreditCardsActiveStandardCanIssueVirtualCardsCountStatisticsFailureAction {
  type: typeof GET_CREDIT_CARDS_ACTIVE_STANDARD_CAN_ISSUE_VIRTUAL_CARDS_COUNT_STATISTICS_FAILURE
  payload: unknown
}

export type GetCreditCardsActiveStandardCanIssueVirtualCardsCountStatisticsActions =

    | GetCreditCardsActiveStandardCanIssueVirtualCardsCountStatisticsInitAction
    | GetCreditCardsActiveStandardCanIssueVirtualCardsCountStatisticsSuccessAction
    | GetCreditCardsActiveStandardCanIssueVirtualCardsCountStatisticsFailureAction

const createGetCreditCardsActiveStandardCanIssueVirtualCardsCountStatisticsInitAction =
  (): GetCreditCardsActiveStandardCanIssueVirtualCardsCountStatisticsInitAction => ({
    type: GET_CREDIT_CARDS_ACTIVE_STANDARD_CAN_ISSUE_VIRTUAL_CARDS_COUNT_STATISTICS_INIT,
  })
const createGetCreditCardsActiveStandardCanIssueVirtualCardsCountStatisticsSuccessAction =
  (
    payload: GetCreditCardsActiveStandardCanIssueVirtualCardsCountStatisticsSuccessPayload,
  ): GetCreditCardsActiveStandardCanIssueVirtualCardsCountStatisticsSuccessAction => ({
    type: GET_CREDIT_CARDS_ACTIVE_STANDARD_CAN_ISSUE_VIRTUAL_CARDS_COUNT_STATISTICS_SUCCESS,
    payload,
  })
const createGetCreditCardsActiveStandardCanIssueVirtualCardsCountStatisticsFailureAction =
  (
    response: unknown,
  ): GetCreditCardsActiveStandardCanIssueVirtualCardsCountStatisticsFailureAction => ({
    type: GET_CREDIT_CARDS_ACTIVE_STANDARD_CAN_ISSUE_VIRTUAL_CARDS_COUNT_STATISTICS_FAILURE,
    payload: response,
  })

export const getCreditCardsActiveStandardCanIssueVirtualCardsCountStatistics =
  (): ThunkAction<
    Promise<
      | GetCreditCardsActiveStandardCanIssueVirtualCardsCountStatisticsFailureAction
      | GetCreditCardsActiveStandardCanIssueVirtualCardsCountStatisticsSuccessAction
    >,
    RootState,
    void,
    GetCreditCardsActiveStandardCanIssueVirtualCardsCountStatisticsActions
  > => {
    return async (
      dispatch: ThunkDispatch<
        RootState,
        void,
        GetCreditCardsActiveStandardCanIssueVirtualCardsCountStatisticsActions
      >,
      getState: () => RootState,
    ): Promise<
      | GetCreditCardsActiveStandardCanIssueVirtualCardsCountStatisticsFailureAction
      | GetCreditCardsActiveStandardCanIssueVirtualCardsCountStatisticsSuccessAction
    > => {
      dispatch(
        createGetCreditCardsActiveStandardCanIssueVirtualCardsCountStatisticsInitAction(),
      )

      return axios
        .post<GetCreditCardsActiveStandardCanIssueVirtualCardsCountStatisticsSuccessPayload>(
          `/statistics`,
          {
            statistics: [
              {
                agg: StatisticAggregateType.Count,
                type: StatisticModelType.CreditCard,
                query: {
                  status: CreditCardStatus.Active,
                  withPermission: CreditCardPermissions.IssueVirtualCards,
                  balanceLockedEnabled: false,
                },
              },
            ],
          },
          selectAuthedJsonApiRequestConfig(getState()),
        )
        .then(
          async (response) => {
            return dispatch(
              createGetCreditCardsActiveStandardCanIssueVirtualCardsCountStatisticsSuccessAction(
                { ...response.data },
              ),
            )
          },
          (err) => {
            return dispatch(
              createGetCreditCardsActiveStandardCanIssueVirtualCardsCountStatisticsFailureAction(
                err.response.data,
              ),
            )
          },
        )
    }
  }

export const GET_CREDIT_CARDS_ACTIVE_SOURCE_STANDARD_CAN_ISSUE_VIRTUAL_CARDS_COUNT_STATISTICS_INIT =
  'data/statistics/GET_CREDIT_CARDS_ACTIVE_SOURCE_STANDARD_CAN_ISSUE_VIRTUAL_CARDS_COUNT_STATISTICS_INIT'
export const GET_CREDIT_CARDS_ACTIVE_SOURCE_STANDARD_CAN_ISSUE_VIRTUAL_CARDS_COUNT_STATISTICS_SUCCESS =
  'data/statistics/GET_CREDIT_CARDS_ACTIVE_SOURCE_STANDARD_CAN_ISSUE_VIRTUAL_CARDS_COUNT_STATISTICS_SUCCESS'
export const GET_CREDIT_CARDS_ACTIVE_SOURCE_STANDARD_CAN_ISSUE_VIRTUAL_CARDS_COUNT_STATISTICS_FAILURE =
  'data/statistics/GET_CREDIT_CARDS_ACTIVE_SOURCE_STANDARD_CAN_ISSUE_VIRTUAL_CARDS_COUNT_STATISTICS_FAILURE'

export interface GetCreditCardsActiveSourceStandardCanIssueVirtualCardsCountStatisticsSuccessPayload {
  statistics: Array<{ value: number }>
  statisticNames: string[]
}

export interface GetCreditCardsActiveSourceStandardCanIssueVirtualCardsCountStatisticsInitAction {
  type: typeof GET_CREDIT_CARDS_ACTIVE_SOURCE_STANDARD_CAN_ISSUE_VIRTUAL_CARDS_COUNT_STATISTICS_INIT
}
export interface GetCreditCardsActiveSourceStandardCanIssueVirtualCardsCountStatisticsSuccessAction {
  type: typeof GET_CREDIT_CARDS_ACTIVE_SOURCE_STANDARD_CAN_ISSUE_VIRTUAL_CARDS_COUNT_STATISTICS_SUCCESS
  payload: GetCreditCardsActiveSourceStandardCanIssueVirtualCardsCountStatisticsSuccessPayload
}
export interface GetCreditCardsActiveSourceStandardCanIssueVirtualCardsCountStatisticsFailureAction {
  type: typeof GET_CREDIT_CARDS_ACTIVE_SOURCE_STANDARD_CAN_ISSUE_VIRTUAL_CARDS_COUNT_STATISTICS_FAILURE
  payload: unknown
}

export type GetCreditCardsActiveSourceStandardCanIssueVirtualCardsCountStatisticsActions =

    | GetCreditCardsActiveSourceStandardCanIssueVirtualCardsCountStatisticsInitAction
    | GetCreditCardsActiveSourceStandardCanIssueVirtualCardsCountStatisticsSuccessAction
    | GetCreditCardsActiveSourceStandardCanIssueVirtualCardsCountStatisticsFailureAction

const createGetCreditCardsActiveSourceStandardCanIssueVirtualCardsCountStatisticsInitAction =
  (): GetCreditCardsActiveSourceStandardCanIssueVirtualCardsCountStatisticsInitAction => ({
    type: GET_CREDIT_CARDS_ACTIVE_SOURCE_STANDARD_CAN_ISSUE_VIRTUAL_CARDS_COUNT_STATISTICS_INIT,
  })
const createGetCreditCardsActiveSourceStandardCanIssueVirtualCardsCountStatisticsSuccessAction =
  (
    payload: GetCreditCardsActiveSourceStandardCanIssueVirtualCardsCountStatisticsSuccessPayload,
  ): GetCreditCardsActiveSourceStandardCanIssueVirtualCardsCountStatisticsSuccessAction => ({
    type: GET_CREDIT_CARDS_ACTIVE_SOURCE_STANDARD_CAN_ISSUE_VIRTUAL_CARDS_COUNT_STATISTICS_SUCCESS,
    payload,
  })
const createGetCreditCardsActiveSourceStandardCanIssueVirtualCardsCountStatisticsFailureAction =
  (
    response: unknown,
  ): GetCreditCardsActiveSourceStandardCanIssueVirtualCardsCountStatisticsFailureAction => ({
    type: GET_CREDIT_CARDS_ACTIVE_SOURCE_STANDARD_CAN_ISSUE_VIRTUAL_CARDS_COUNT_STATISTICS_FAILURE,
    payload: response,
  })

export const getCreditCardsActiveSourceStandardCanIssueVirtualCardsCountStatistics =
  (): ThunkAction<
    Promise<
      | GetCreditCardsActiveSourceStandardCanIssueVirtualCardsCountStatisticsFailureAction
      | GetCreditCardsActiveSourceStandardCanIssueVirtualCardsCountStatisticsSuccessAction
    >,
    RootState,
    void,
    GetCreditCardsActiveSourceStandardCanIssueVirtualCardsCountStatisticsActions
  > => {
    return async (
      dispatch: ThunkDispatch<
        RootState,
        void,
        GetCreditCardsActiveSourceStandardCanIssueVirtualCardsCountStatisticsActions
      >,
      getState: () => RootState,
    ): Promise<
      | GetCreditCardsActiveSourceStandardCanIssueVirtualCardsCountStatisticsFailureAction
      | GetCreditCardsActiveSourceStandardCanIssueVirtualCardsCountStatisticsSuccessAction
    > => {
      dispatch(
        createGetCreditCardsActiveSourceStandardCanIssueVirtualCardsCountStatisticsInitAction(),
      )

      return axios
        .post<GetCreditCardsActiveSourceStandardCanIssueVirtualCardsCountStatisticsSuccessPayload>(
          `/statistics`,
          {
            statistics: [
              {
                agg: StatisticAggregateType.Count,
                type: StatisticModelType.CreditCard,
                query: {
                  status: CreditCardStatus.Active,
                  withPermission: CreditCardPermissions.IssueVirtualCards,
                  type: CreditCardType.Source,
                  balanceLockedEnabled: false,
                },
              },
            ],
          },
          selectAuthedJsonApiRequestConfig(getState()),
        )
        .then(
          async (response) => {
            return dispatch(
              createGetCreditCardsActiveSourceStandardCanIssueVirtualCardsCountStatisticsSuccessAction(
                { ...response.data },
              ),
            )
          },
          (err) => {
            return dispatch(
              createGetCreditCardsActiveSourceStandardCanIssueVirtualCardsCountStatisticsFailureAction(
                err.response.data,
              ),
            )
          },
        )
    }
  }

export const GET_CREDIT_CARDS_ACTIVE_SOURCE_DIRECT_ENABLED_CAN_ISSUE_VIRTUAL_CARDS_COUNT_STATISTICS_INIT =
  'data/statistics/GET_CREDIT_CARDS_ACTIVE_SOURCE_DIRECT_ENABLED_CAN_ISSUE_VIRTUAL_CARDS_COUNT_STATISTICS_INIT'
export const GET_CREDIT_CARDS_ACTIVE_SOURCE_DIRECT_ENABLED_CAN_ISSUE_VIRTUAL_CARDS_COUNT_STATISTICS_SUCCESS =
  'data/statistics/GET_CREDIT_CARDS_ACTIVE_SOURCE_DIRECT_ENABLED_CAN_ISSUE_VIRTUAL_CARDS_COUNT_STATISTICS_SUCCESS'
export const GET_CREDIT_CARDS_ACTIVE_SOURCE_DIRECT_ENABLED_CAN_ISSUE_VIRTUAL_CARDS_COUNT_STATISTICS_FAILURE =
  'data/statistics/GET_CREDIT_CARDS_ACTIVE_SOURCE_DIRECT_ENABLED_CAN_ISSUE_VIRTUAL_CARDS_COUNT_STATISTICS_FAILURE'

export interface GetCreditCardsActiveSourceDirectEnabledCanIssueVirtualCardsCountStatisticsSuccessPayload {
  statistics: Array<{ value: number }>
  statisticNames: string[]
}

export interface GetCreditCardsActiveSourceDirectEnabledCanIssueVirtualCardsCountStatisticsInitAction {
  type: typeof GET_CREDIT_CARDS_ACTIVE_SOURCE_DIRECT_ENABLED_CAN_ISSUE_VIRTUAL_CARDS_COUNT_STATISTICS_INIT
}
export interface GetCreditCardsActiveSourceDirectEnabledCanIssueVirtualCardsCountStatisticsSuccessAction {
  type: typeof GET_CREDIT_CARDS_ACTIVE_SOURCE_DIRECT_ENABLED_CAN_ISSUE_VIRTUAL_CARDS_COUNT_STATISTICS_SUCCESS
  payload: GetCreditCardsActiveSourceDirectEnabledCanIssueVirtualCardsCountStatisticsSuccessPayload
}
export interface GetCreditCardsActiveSourceDirectEnabledCanIssueVirtualCardsCountStatisticsFailureAction {
  type: typeof GET_CREDIT_CARDS_ACTIVE_SOURCE_DIRECT_ENABLED_CAN_ISSUE_VIRTUAL_CARDS_COUNT_STATISTICS_FAILURE
  payload: unknown
}

export type GetCreditCardsActiveSourceDirectEnabledCanIssueVirtualCardsCountStatisticsActions =

    | GetCreditCardsActiveSourceDirectEnabledCanIssueVirtualCardsCountStatisticsInitAction
    | GetCreditCardsActiveSourceDirectEnabledCanIssueVirtualCardsCountStatisticsSuccessAction
    | GetCreditCardsActiveSourceDirectEnabledCanIssueVirtualCardsCountStatisticsFailureAction

const createGetCreditCardsActiveSourceDirectEnabledCanIssueVirtualCardsCountStatisticsInitAction =
  (): GetCreditCardsActiveSourceDirectEnabledCanIssueVirtualCardsCountStatisticsInitAction => ({
    type: GET_CREDIT_CARDS_ACTIVE_SOURCE_DIRECT_ENABLED_CAN_ISSUE_VIRTUAL_CARDS_COUNT_STATISTICS_INIT,
  })
const createGetCreditCardsActiveSourceDirectEnabledCanIssueVirtualCardsCountStatisticsSuccessAction =
  (
    payload: GetCreditCardsActiveSourceDirectEnabledCanIssueVirtualCardsCountStatisticsSuccessPayload,
  ): GetCreditCardsActiveSourceDirectEnabledCanIssueVirtualCardsCountStatisticsSuccessAction => ({
    type: GET_CREDIT_CARDS_ACTIVE_SOURCE_DIRECT_ENABLED_CAN_ISSUE_VIRTUAL_CARDS_COUNT_STATISTICS_SUCCESS,
    payload,
  })
const createGetCreditCardsActiveSourceDirectEnabledCanIssueVirtualCardsCountStatisticsFailureAction =
  (
    response: unknown,
  ): GetCreditCardsActiveSourceDirectEnabledCanIssueVirtualCardsCountStatisticsFailureAction => ({
    type: GET_CREDIT_CARDS_ACTIVE_SOURCE_DIRECT_ENABLED_CAN_ISSUE_VIRTUAL_CARDS_COUNT_STATISTICS_FAILURE,
    payload: response,
  })

export const getCreditCardsActiveSourceDirectEnabledCanIssueVirtualCardsCountStatistics =
  (): ThunkAction<
    Promise<
      | GetCreditCardsActiveSourceDirectEnabledCanIssueVirtualCardsCountStatisticsFailureAction
      | GetCreditCardsActiveSourceDirectEnabledCanIssueVirtualCardsCountStatisticsSuccessAction
    >,
    RootState,
    void,
    GetCreditCardsActiveSourceDirectEnabledCanIssueVirtualCardsCountStatisticsActions
  > => {
    return async (
      dispatch: ThunkDispatch<
        RootState,
        void,
        GetCreditCardsActiveSourceDirectEnabledCanIssueVirtualCardsCountStatisticsActions
      >,
      getState: () => RootState,
    ): Promise<
      | GetCreditCardsActiveSourceDirectEnabledCanIssueVirtualCardsCountStatisticsFailureAction
      | GetCreditCardsActiveSourceDirectEnabledCanIssueVirtualCardsCountStatisticsSuccessAction
    > => {
      dispatch(
        createGetCreditCardsActiveSourceDirectEnabledCanIssueVirtualCardsCountStatisticsInitAction(),
      )

      return axios
        .post<GetCreditCardsActiveSourceDirectEnabledCanIssueVirtualCardsCountStatisticsSuccessPayload>(
          `/statistics`,
          {
            statistics: [
              {
                agg: StatisticAggregateType.Count,
                type: StatisticModelType.CreditCard,
                query: {
                  directEnabled: true,
                  status: CreditCardStatus.Active,
                  withPermission: CreditCardPermissions.IssueVirtualCards,
                  type: CreditCardType.Source,
                },
              },
            ],
          },
          selectAuthedJsonApiRequestConfig(getState()),
        )
        .then(
          async (response) => {
            return dispatch(
              createGetCreditCardsActiveSourceDirectEnabledCanIssueVirtualCardsCountStatisticsSuccessAction(
                { ...response.data },
              ),
            )
          },
          (err) => {
            return dispatch(
              createGetCreditCardsActiveSourceDirectEnabledCanIssueVirtualCardsCountStatisticsFailureAction(
                err.response.data,
              ),
            )
          },
        )
    }
  }

export const GET_CREDIT_CARDS_ACTIVE_SOURCE_BUDGETS_ENABLED_CAN_ISSUE_BUDGETS_COUNT_STATISTICS_INIT =
  'data/statistics/GET_CREDIT_CARDS_ACTIVE_SOURCE_BUDGETS_ENABLED_CAN_ISSUE_BUDGETS_COUNT_STATISTICS_INIT'
export const GET_CREDIT_CARDS_ACTIVE_SOURCE_BUDGETS_ENABLED_CAN_ISSUE_BUDGETS_COUNT_STATISTICS_SUCCESS =
  'data/statistics/GET_CREDIT_CARDS_ACTIVE_SOURCE_BUDGETS_ENABLED_CAN_ISSUE_BUDGETS_COUNT_STATISTICS_SUCCESS'
export const GET_CREDIT_CARDS_ACTIVE_SOURCE_BUDGETS_ENABLED_CAN_ISSUE_BUDGETS_COUNT_STATISTICS_FAILURE =
  'data/statistics/GET_CREDIT_CARDS_ACTIVE_SOURCE_BUDGETS_ENABLED_CAN_ISSUE_BUDGETS_COUNT_STATISTICS_FAILURE'

export interface GetCreditCardsActiveSourceBudgetsEnabledCanIssueBudgetsCountStatisticsSuccessPayload {
  statistics: Array<{ value: number }>
  statisticNames: string[]
}

export interface GetCreditCardsActiveSourceBudgetsEnabledCanIssueBudgetsCountStatisticsInitAction {
  type: typeof GET_CREDIT_CARDS_ACTIVE_SOURCE_BUDGETS_ENABLED_CAN_ISSUE_BUDGETS_COUNT_STATISTICS_INIT
}
export interface GetCreditCardsActiveSourceBudgetsEnabledCanIssueBudgetsCountStatisticsSuccessAction {
  type: typeof GET_CREDIT_CARDS_ACTIVE_SOURCE_BUDGETS_ENABLED_CAN_ISSUE_BUDGETS_COUNT_STATISTICS_SUCCESS
  payload: GetCreditCardsActiveSourceBudgetsEnabledCanIssueBudgetsCountStatisticsSuccessPayload
}
export interface GetCreditCardsActiveSourceBudgetsEnabledCanIssueBudgetsCountStatisticsFailureAction {
  type: typeof GET_CREDIT_CARDS_ACTIVE_SOURCE_BUDGETS_ENABLED_CAN_ISSUE_BUDGETS_COUNT_STATISTICS_FAILURE
  payload: unknown
}

export type GetCreditCardsActiveSourceBudgetsEnabledCanIssueBudgetsCountStatisticsActions =

    | GetCreditCardsActiveSourceBudgetsEnabledCanIssueBudgetsCountStatisticsInitAction
    | GetCreditCardsActiveSourceBudgetsEnabledCanIssueBudgetsCountStatisticsSuccessAction
    | GetCreditCardsActiveSourceBudgetsEnabledCanIssueBudgetsCountStatisticsFailureAction

const createGetCreditCardsActiveSourceBudgetsEnabledCanIssueBudgetsCountStatisticsInitAction =
  (): GetCreditCardsActiveSourceBudgetsEnabledCanIssueBudgetsCountStatisticsInitAction => ({
    type: GET_CREDIT_CARDS_ACTIVE_SOURCE_BUDGETS_ENABLED_CAN_ISSUE_BUDGETS_COUNT_STATISTICS_INIT,
  })
const createGetCreditCardsActiveSourceBudgetsEnabledCanIssueBudgetsCountStatisticsSuccessAction =
  (
    payload: GetCreditCardsActiveSourceBudgetsEnabledCanIssueBudgetsCountStatisticsSuccessPayload,
  ): GetCreditCardsActiveSourceBudgetsEnabledCanIssueBudgetsCountStatisticsSuccessAction => ({
    type: GET_CREDIT_CARDS_ACTIVE_SOURCE_BUDGETS_ENABLED_CAN_ISSUE_BUDGETS_COUNT_STATISTICS_SUCCESS,
    payload,
  })
const createGetCreditCardsActiveSourceBudgetsEnabledCanIssueBudgetsCountStatisticsFailureAction =
  (
    response: unknown,
  ): GetCreditCardsActiveSourceBudgetsEnabledCanIssueBudgetsCountStatisticsFailureAction => ({
    type: GET_CREDIT_CARDS_ACTIVE_SOURCE_BUDGETS_ENABLED_CAN_ISSUE_BUDGETS_COUNT_STATISTICS_FAILURE,
    payload: response,
  })

export const getCreditCardsActiveSourceBudgetsEnabledCanIssueBudgetsCountStatistics =
  (): ThunkAction<
    Promise<
      | GetCreditCardsActiveSourceBudgetsEnabledCanIssueBudgetsCountStatisticsFailureAction
      | GetCreditCardsActiveSourceBudgetsEnabledCanIssueBudgetsCountStatisticsSuccessAction
    >,
    RootState,
    void,
    GetCreditCardsActiveSourceBudgetsEnabledCanIssueBudgetsCountStatisticsActions
  > => {
    return async (
      dispatch: ThunkDispatch<
        RootState,
        void,
        GetCreditCardsActiveSourceBudgetsEnabledCanIssueBudgetsCountStatisticsActions
      >,
      getState: () => RootState,
    ): Promise<
      | GetCreditCardsActiveSourceBudgetsEnabledCanIssueBudgetsCountStatisticsFailureAction
      | GetCreditCardsActiveSourceBudgetsEnabledCanIssueBudgetsCountStatisticsSuccessAction
    > => {
      dispatch(
        createGetCreditCardsActiveSourceBudgetsEnabledCanIssueBudgetsCountStatisticsInitAction(),
      )

      return axios
        .post<GetCreditCardsActiveSourceBudgetsEnabledCanIssueBudgetsCountStatisticsSuccessPayload>(
          `/statistics`,
          {
            statistics: [
              {
                agg: StatisticAggregateType.Count,
                type: StatisticModelType.CreditCard,
                query: {
                  type: CreditCardType.Source,
                  status: CreditCardStatus.Active,
                  budgetsEnabled: true,
                  withPermission:
                    CreditCardPermissions.IssueDelegateCreditCards,
                },
              },
            ],
          },
          selectAuthedJsonApiRequestConfig(getState()),
        )
        .then(
          async (response) => {
            return dispatch(
              createGetCreditCardsActiveSourceBudgetsEnabledCanIssueBudgetsCountStatisticsSuccessAction(
                { ...response.data },
              ),
            )
          },
          (err) => {
            return dispatch(
              createGetCreditCardsActiveSourceBudgetsEnabledCanIssueBudgetsCountStatisticsFailureAction(
                err.response.data,
              ),
            )
          },
        )
    }
  }

export const GET_VIRTUAL_CARDS_ACTIVE_SOURCE_USER_OR_VIEWER_OR_DELEGATE_USER_ME_COUNT_STATISTICS_INIT =
  'data/statistics/GET_VIRTUAL_CARDS_ACTIVE_SOURCE_USER_OR_VIEWER_OR_DELEGATE_USER_ME_COUNT_STATISTICS_INIT'
export const GET_VIRTUAL_CARDS_ACTIVE_SOURCE_USER_OR_VIEWER_OR_DELEGATE_USER_ME_COUNT_STATISTICS_SUCCESS =
  'data/statistics/GET_VIRTUAL_CARDS_ACTIVE_SOURCE_USER_OR_VIEWER_OR_DELEGATE_USER_ME_COUNT_STATISTICS_SUCCESS'
export const GET_VIRTUAL_CARDS_ACTIVE_SOURCE_USER_OR_VIEWER_OR_DELEGATE_USER_ME_COUNT_STATISTICS_FAILURE =
  'data/statistics/GET_VIRTUAL_CARDS_ACTIVE_SOURCE_USER_OR_VIEWER_OR_DELEGATE_USER_ME_COUNT_STATISTICS_FAILURE'

export interface GetVirtualCardsActiveSourceUserOrViewerOrDelegateUserMeCountStatisticsSuccessPayload {
  statistics: Array<{ value: number }>
  statisticNames: string[]
}

export interface GetVirtualCardsActiveSourceUserOrViewerOrDelegateUserMeCountStatisticsInitAction {
  type: typeof GET_VIRTUAL_CARDS_ACTIVE_SOURCE_USER_OR_VIEWER_OR_DELEGATE_USER_ME_COUNT_STATISTICS_INIT
}
export interface GetVirtualCardsActiveSourceUserOrViewerOrDelegateUserMeCountStatisticsSuccessAction {
  type: typeof GET_VIRTUAL_CARDS_ACTIVE_SOURCE_USER_OR_VIEWER_OR_DELEGATE_USER_ME_COUNT_STATISTICS_SUCCESS
  payload: GetVirtualCardsActiveSourceUserOrViewerOrDelegateUserMeCountStatisticsSuccessPayload
}
export interface GetVirtualCardsActiveSourceUserOrViewerOrDelegateUserMeCountStatisticsFailureAction {
  type: typeof GET_VIRTUAL_CARDS_ACTIVE_SOURCE_USER_OR_VIEWER_OR_DELEGATE_USER_ME_COUNT_STATISTICS_FAILURE
  payload: unknown
}

export type GetVirtualCardsActiveSourceUserOrViewerOrDelegateUserMeCountStatisticsActions =

    | GetVirtualCardsActiveSourceUserOrViewerOrDelegateUserMeCountStatisticsInitAction
    | GetVirtualCardsActiveSourceUserOrViewerOrDelegateUserMeCountStatisticsSuccessAction
    | GetVirtualCardsActiveSourceUserOrViewerOrDelegateUserMeCountStatisticsFailureAction

const createGetVirtualCardsActiveSourceUserOrViewerOrDelegateUserMeCountStatisticsInitAction =
  (): GetVirtualCardsActiveSourceUserOrViewerOrDelegateUserMeCountStatisticsInitAction => ({
    type: GET_VIRTUAL_CARDS_ACTIVE_SOURCE_USER_OR_VIEWER_OR_DELEGATE_USER_ME_COUNT_STATISTICS_INIT,
  })
const createGetVirtualCardsActiveSourceUserOrViewerOrDelegateUserMeCountStatisticsSuccessAction =
  (
    payload: GetVirtualCardsActiveSourceUserOrViewerOrDelegateUserMeCountStatisticsSuccessPayload,
  ): GetVirtualCardsActiveSourceUserOrViewerOrDelegateUserMeCountStatisticsSuccessAction => ({
    type: GET_VIRTUAL_CARDS_ACTIVE_SOURCE_USER_OR_VIEWER_OR_DELEGATE_USER_ME_COUNT_STATISTICS_SUCCESS,
    payload,
  })
const createGetVirtualCardsActiveSourceUserOrViewerOrDelegateUserMeCountStatisticsFailureAction =
  (
    response: unknown,
  ): GetVirtualCardsActiveSourceUserOrViewerOrDelegateUserMeCountStatisticsFailureAction => ({
    type: GET_VIRTUAL_CARDS_ACTIVE_SOURCE_USER_OR_VIEWER_OR_DELEGATE_USER_ME_COUNT_STATISTICS_FAILURE,
    payload: response,
  })

export const getVirtualCardsActiveSourceUserOrViewerOrDelegateUserMeCountStatistics =
  (): ThunkAction<
    Promise<
      | GetVirtualCardsActiveSourceUserOrViewerOrDelegateUserMeCountStatisticsFailureAction
      | GetVirtualCardsActiveSourceUserOrViewerOrDelegateUserMeCountStatisticsSuccessAction
    >,
    RootState,
    void,
    GetVirtualCardsActiveSourceUserOrViewerOrDelegateUserMeCountStatisticsActions
  > => {
    return async (
      dispatch: ThunkDispatch<
        RootState,
        void,
        GetVirtualCardsActiveSourceUserOrViewerOrDelegateUserMeCountStatisticsActions
      >,
      getState: () => RootState,
    ): Promise<
      | GetVirtualCardsActiveSourceUserOrViewerOrDelegateUserMeCountStatisticsFailureAction
      | GetVirtualCardsActiveSourceUserOrViewerOrDelegateUserMeCountStatisticsSuccessAction
    > => {
      dispatch(
        createGetVirtualCardsActiveSourceUserOrViewerOrDelegateUserMeCountStatisticsInitAction(),
      )

      return axios
        .post<GetVirtualCardsActiveSourceUserOrViewerOrDelegateUserMeCountStatisticsSuccessPayload>(
          `/statistics`,
          {
            statistics: [
              {
                agg: StatisticAggregateType.Count,
                type: StatisticModelType.VirtualCard,
                query: {
                  status: VirtualCardStatus.Active,
                  cardholderOrViewer: 'me',
                },
              },
            ],
          },
          selectAuthedJsonApiRequestConfig(getState()),
        )
        .then(
          async (response) => {
            return dispatch(
              createGetVirtualCardsActiveSourceUserOrViewerOrDelegateUserMeCountStatisticsSuccessAction(
                { ...response.data },
              ),
            )
          },
          (err) => {
            return dispatch(
              createGetVirtualCardsActiveSourceUserOrViewerOrDelegateUserMeCountStatisticsFailureAction(
                err.response.data,
              ),
            )
          },
        )
    }
  }

export const GET_VIRTUAL_CARDS_CARDHOLDER_OR_VIEWER_ME_ACTIVE_AND_INACTIVE_COUNT_STATISTICS_INIT =
  'data/statistics/GET_VIRTUAL_CARDS_CARDHOLDER_OR_VIEWER_ME_ACTIVE_AND_INACTIVE_COUNT_STATISTICS_INIT'
export const GET_VIRTUAL_CARDS_CARDHOLDER_OR_VIEWER_ME_ACTIVE_AND_INACTIVE_COUNT_STATISTICS_SUCCESS =
  'data/statistics/GET_VIRTUAL_CARDS_CARDHOLDER_OR_VIEWER_ME_ACTIVE_AND_INACTIVE_COUNT_STATISTICS_SUCCESS'
export const GET_VIRTUAL_CARDS_CARDHOLDER_OR_VIEWER_ME_ACTIVE_AND_INACTIVE_COUNT_STATISTICS_FAILURE =
  'data/statistics/GET_VIRTUAL_CARDS_CARDHOLDER_OR_VIEWER_ME_ACTIVE_AND_INACTIVE_COUNT_STATISTICS_FAILURE'

export interface GetVirtualCardsCardholderOrViewerMeActiveAndInactiveCountStatisticsSuccessPayload {
  statistics: Array<{ value: number }>
  statisticNames: string[]
}

export interface GetVirtualCardsCardholderOrViewerMeActiveAndInactiveCountStatisticsInitAction {
  type: typeof GET_VIRTUAL_CARDS_CARDHOLDER_OR_VIEWER_ME_ACTIVE_AND_INACTIVE_COUNT_STATISTICS_INIT
}
export interface GetVirtualCardsCardholderOrViewerMeActiveAndInactiveCountStatisticsSuccessAction {
  type: typeof GET_VIRTUAL_CARDS_CARDHOLDER_OR_VIEWER_ME_ACTIVE_AND_INACTIVE_COUNT_STATISTICS_SUCCESS
  payload: GetVirtualCardsCardholderOrViewerMeActiveAndInactiveCountStatisticsSuccessPayload
}
export interface GetVirtualCardsCardholderOrViewerMeActiveAndInactiveCountStatisticsFailureAction {
  type: typeof GET_VIRTUAL_CARDS_CARDHOLDER_OR_VIEWER_ME_ACTIVE_AND_INACTIVE_COUNT_STATISTICS_FAILURE
  payload: unknown
}

export type GetVirtualCardsCardholderOrViewerMeActiveAndInactiveCountStatisticsActions =

    | GetVirtualCardsCardholderOrViewerMeActiveAndInactiveCountStatisticsInitAction
    | GetVirtualCardsCardholderOrViewerMeActiveAndInactiveCountStatisticsSuccessAction
    | GetVirtualCardsCardholderOrViewerMeActiveAndInactiveCountStatisticsFailureAction

const createGetVirtualCardsCardholderOrViewerMeActiveAndInactiveCountStatisticsInitAction =
  (): GetVirtualCardsCardholderOrViewerMeActiveAndInactiveCountStatisticsInitAction => ({
    type: GET_VIRTUAL_CARDS_CARDHOLDER_OR_VIEWER_ME_ACTIVE_AND_INACTIVE_COUNT_STATISTICS_INIT,
  })
const createGetVirtualCardsCardholderOrViewerMeActiveAndInactiveCountStatisticsSuccessAction =
  (
    payload: GetVirtualCardsCardholderOrViewerMeActiveAndInactiveCountStatisticsSuccessPayload,
  ): GetVirtualCardsCardholderOrViewerMeActiveAndInactiveCountStatisticsSuccessAction => ({
    type: GET_VIRTUAL_CARDS_CARDHOLDER_OR_VIEWER_ME_ACTIVE_AND_INACTIVE_COUNT_STATISTICS_SUCCESS,
    payload,
  })
const createGetVirtualCardsCardholderOrViewerMeActiveAndInactiveCountStatisticsFailureAction =
  (
    response: unknown,
  ): GetVirtualCardsCardholderOrViewerMeActiveAndInactiveCountStatisticsFailureAction => ({
    type: GET_VIRTUAL_CARDS_CARDHOLDER_OR_VIEWER_ME_ACTIVE_AND_INACTIVE_COUNT_STATISTICS_FAILURE,
    payload: response,
  })

export const getVirtualCardsCardholderOrViewerMeActiveAndInactiveCountStatistics =
  (): ThunkAction<
    Promise<
      | GetVirtualCardsCardholderOrViewerMeActiveAndInactiveCountStatisticsFailureAction
      | GetVirtualCardsCardholderOrViewerMeActiveAndInactiveCountStatisticsSuccessAction
    >,
    RootState,
    void,
    GetVirtualCardsCardholderOrViewerMeActiveAndInactiveCountStatisticsActions
  > => {
    return async (
      dispatch: ThunkDispatch<
        RootState,
        void,
        GetVirtualCardsCardholderOrViewerMeActiveAndInactiveCountStatisticsActions
      >,
      getState: () => RootState,
    ): Promise<
      | GetVirtualCardsCardholderOrViewerMeActiveAndInactiveCountStatisticsFailureAction
      | GetVirtualCardsCardholderOrViewerMeActiveAndInactiveCountStatisticsSuccessAction
    > => {
      dispatch(
        createGetVirtualCardsCardholderOrViewerMeActiveAndInactiveCountStatisticsInitAction(),
      )

      return axios
        .post<GetVirtualCardsCardholderOrViewerMeActiveAndInactiveCountStatisticsSuccessPayload>(
          `/statistics`,
          {
            statistics: [
              {
                agg: StatisticAggregateType.Count,
                type: StatisticModelType.VirtualCard,
                query: {
                  issued: true,
                  cardholderOrViewer: 'me',
                  statuses: [
                    VirtualCardStatus.Active,
                    VirtualCardStatus.Cancelled,
                    VirtualCardStatus.Consumed,
                    VirtualCardStatus.Closed,
                    VirtualCardStatus.Expired,
                  ],
                },
              },
            ],
          },
          selectAuthedJsonApiRequestConfig(getState()),
        )
        .then(
          async (response) => {
            return dispatch(
              createGetVirtualCardsCardholderOrViewerMeActiveAndInactiveCountStatisticsSuccessAction(
                { ...response.data },
              ),
            )
          },
          (err) => {
            return dispatch(
              createGetVirtualCardsCardholderOrViewerMeActiveAndInactiveCountStatisticsFailureAction(
                err.response.data,
              ),
            )
          },
        )
    }
  }

export const GET_BILL_PAY_CARDS_ACTIVE_SOURCE_USER_OR_VIEWER_OR_DELEGATE_USER_ME_COUNT_STATISTICS_INIT =
  'data/statistics/GET_BILL_PAY_CARDS_ACTIVE_SOURCE_USER_OR_VIEWER_OR_DELEGATE_USER_ME_COUNT_STATISTICS_INIT'
export const GET_BILL_PAY_CARDS_ACTIVE_SOURCE_USER_OR_VIEWER_OR_DELEGATE_USER_ME_COUNT_STATISTICS_SUCCESS =
  'data/statistics/GET_BILL_PAY_CARDS_ACTIVE_SOURCE_USER_OR_VIEWER_OR_DELEGATE_USER_ME_COUNT_STATISTICS_SUCCESS'
export const GET_BILL_PAY_CARDS_ACTIVE_SOURCE_USER_OR_VIEWER_OR_DELEGATE_USER_ME_COUNT_STATISTICS_FAILURE =
  'data/statistics/GET_BILL_PAY_CARDS_ACTIVE_SOURCE_USER_OR_VIEWER_OR_DELEGATE_USER_ME_COUNT_STATISTICS_FAILURE'

export interface GetBillPayCardsActiveSourceUserOrViewerOrDelegateUserMeCountStatisticsSuccessPayload {
  statistics: Array<{ value: number }>
  statisticNames: string[]
}

export interface GetBillPayCardsActiveSourceUserOrViewerOrDelegateUserMeCountStatisticsInitAction {
  type: typeof GET_BILL_PAY_CARDS_ACTIVE_SOURCE_USER_OR_VIEWER_OR_DELEGATE_USER_ME_COUNT_STATISTICS_INIT
}
export interface GetBillPayCardsActiveSourceUserOrViewerOrDelegateUserMeCountStatisticsSuccessAction {
  type: typeof GET_BILL_PAY_CARDS_ACTIVE_SOURCE_USER_OR_VIEWER_OR_DELEGATE_USER_ME_COUNT_STATISTICS_SUCCESS
  payload: GetBillPayCardsActiveSourceUserOrViewerOrDelegateUserMeCountStatisticsSuccessPayload
}
export interface GetBillPayCardsActiveSourceUserOrViewerOrDelegateUserMeCountStatisticsFailureAction {
  type: typeof GET_BILL_PAY_CARDS_ACTIVE_SOURCE_USER_OR_VIEWER_OR_DELEGATE_USER_ME_COUNT_STATISTICS_FAILURE
  payload: unknown
}

export type GetBillPayCardsActiveSourceUserOrViewerOrDelegateUserMeCountStatisticsActions =

    | GetBillPayCardsActiveSourceUserOrViewerOrDelegateUserMeCountStatisticsInitAction
    | GetBillPayCardsActiveSourceUserOrViewerOrDelegateUserMeCountStatisticsSuccessAction
    | GetBillPayCardsActiveSourceUserOrViewerOrDelegateUserMeCountStatisticsFailureAction

const createGetBillPayCardsActiveSourceUserOrViewerOrDelegateUserMeCountStatisticsInitAction =
  (): GetBillPayCardsActiveSourceUserOrViewerOrDelegateUserMeCountStatisticsInitAction => ({
    type: GET_BILL_PAY_CARDS_ACTIVE_SOURCE_USER_OR_VIEWER_OR_DELEGATE_USER_ME_COUNT_STATISTICS_INIT,
  })
const createGetBillPayCardsActiveSourceUserOrViewerOrDelegateUserMeCountStatisticsSuccessAction =
  (
    payload: GetBillPayCardsActiveSourceUserOrViewerOrDelegateUserMeCountStatisticsSuccessPayload,
  ): GetBillPayCardsActiveSourceUserOrViewerOrDelegateUserMeCountStatisticsSuccessAction => ({
    type: GET_BILL_PAY_CARDS_ACTIVE_SOURCE_USER_OR_VIEWER_OR_DELEGATE_USER_ME_COUNT_STATISTICS_SUCCESS,
    payload,
  })
const createGetBillPayCardsActiveSourceUserOrViewerOrDelegateUserMeCountStatisticsFailureAction =
  (
    response: unknown,
  ): GetBillPayCardsActiveSourceUserOrViewerOrDelegateUserMeCountStatisticsFailureAction => ({
    type: GET_BILL_PAY_CARDS_ACTIVE_SOURCE_USER_OR_VIEWER_OR_DELEGATE_USER_ME_COUNT_STATISTICS_FAILURE,
    payload: response,
  })

export const getBillPayCardsActiveSourceUserOrViewerOrDelegateUserMeCountStatistics =
  (): ThunkAction<
    Promise<
      | GetBillPayCardsActiveSourceUserOrViewerOrDelegateUserMeCountStatisticsFailureAction
      | GetBillPayCardsActiveSourceUserOrViewerOrDelegateUserMeCountStatisticsSuccessAction
    >,
    RootState,
    void,
    GetBillPayCardsActiveSourceUserOrViewerOrDelegateUserMeCountStatisticsActions
  > => {
    return async (
      dispatch: ThunkDispatch<
        RootState,
        void,
        GetBillPayCardsActiveSourceUserOrViewerOrDelegateUserMeCountStatisticsActions
      >,
      getState: () => RootState,
    ): Promise<
      | GetBillPayCardsActiveSourceUserOrViewerOrDelegateUserMeCountStatisticsFailureAction
      | GetBillPayCardsActiveSourceUserOrViewerOrDelegateUserMeCountStatisticsSuccessAction
    > => {
      dispatch(
        createGetBillPayCardsActiveSourceUserOrViewerOrDelegateUserMeCountStatisticsInitAction(),
      )

      return axios
        .post<GetBillPayCardsActiveSourceUserOrViewerOrDelegateUserMeCountStatisticsSuccessPayload>(
          `/statistics`,
          {
            statistics: [
              {
                agg: StatisticAggregateType.Count,
                type: StatisticModelType.VirtualCard,
                query: {
                  status: VirtualCardStatus.Active,
                  cardholderOrViewer: 'me',
                  cardTypes: [VirtualCardTypes.billPay],
                },
              },
            ],
          },
          selectAuthedJsonApiRequestConfig(getState()),
        )
        .then(
          async (response) => {
            return dispatch(
              createGetBillPayCardsActiveSourceUserOrViewerOrDelegateUserMeCountStatisticsSuccessAction(
                { ...response.data },
              ),
            )
          },
          (err) => {
            return dispatch(
              createGetBillPayCardsActiveSourceUserOrViewerOrDelegateUserMeCountStatisticsFailureAction(
                err.response.data,
              ),
            )
          },
        )
    }
  }

export const GET_BILL_PAY_CARDS_CARDHOLDER_OR_VIEWER_ME_ACTIVE_AND_INACTIVE_COUNT_STATISTICS_INIT =
  'data/statistics/GET_BILL_PAY_CARDS_CARDHOLDER_OR_VIEWER_ME_ACTIVE_AND_INACTIVE_COUNT_STATISTICS_INIT'
export const GET_BILL_PAY_CARDS_CARDHOLDER_OR_VIEWER_ME_ACTIVE_AND_INACTIVE_COUNT_STATISTICS_SUCCESS =
  'data/statistics/GET_BILL_PAY_CARDS_CARDHOLDER_OR_VIEWER_ME_ACTIVE_AND_INACTIVE_COUNT_STATISTICS_SUCCESS'
export const GET_BILL_PAY_CARDS_CARDHOLDER_OR_VIEWER_ME_ACTIVE_AND_INACTIVE_COUNT_STATISTICS_FAILURE =
  'data/statistics/GET_BILL_PAY_CARDS_CARDHOLDER_OR_VIEWER_ME_ACTIVE_AND_INACTIVE_COUNT_STATISTICS_FAILURE'

export interface GetBillPayCardsCardholderOrViewerMeActiveAndInactiveCountStatisticsSuccessPayload {
  statistics: Array<{ value: number }>
  statisticNames: string[]
}

export interface GetBillPayCardsCardholderOrViewerMeActiveAndInactiveCountStatisticsInitAction {
  type: typeof GET_BILL_PAY_CARDS_CARDHOLDER_OR_VIEWER_ME_ACTIVE_AND_INACTIVE_COUNT_STATISTICS_INIT
}
export interface GetBillPayCardsCardholderOrViewerMeActiveAndInactiveCountStatisticsSuccessAction {
  type: typeof GET_BILL_PAY_CARDS_CARDHOLDER_OR_VIEWER_ME_ACTIVE_AND_INACTIVE_COUNT_STATISTICS_SUCCESS
  payload: GetBillPayCardsCardholderOrViewerMeActiveAndInactiveCountStatisticsSuccessPayload
}
export interface GetBillPayCardsCardholderOrViewerMeActiveAndInactiveCountStatisticsFailureAction {
  type: typeof GET_BILL_PAY_CARDS_CARDHOLDER_OR_VIEWER_ME_ACTIVE_AND_INACTIVE_COUNT_STATISTICS_FAILURE
  payload: unknown
}

export type GetBillPayCardsCardholderOrViewerMeActiveAndInactiveCountStatisticsActions =

    | GetBillPayCardsCardholderOrViewerMeActiveAndInactiveCountStatisticsInitAction
    | GetBillPayCardsCardholderOrViewerMeActiveAndInactiveCountStatisticsSuccessAction
    | GetBillPayCardsCardholderOrViewerMeActiveAndInactiveCountStatisticsFailureAction

const createGetBillPayCardsCardholderOrViewerMeActiveAndInactiveCountStatisticsInitAction =
  (): GetBillPayCardsCardholderOrViewerMeActiveAndInactiveCountStatisticsInitAction => ({
    type: GET_BILL_PAY_CARDS_CARDHOLDER_OR_VIEWER_ME_ACTIVE_AND_INACTIVE_COUNT_STATISTICS_INIT,
  })
const createGetBillPayCardsCardholderOrViewerMeActiveAndInactiveCountStatisticsSuccessAction =
  (
    payload: GetBillPayCardsCardholderOrViewerMeActiveAndInactiveCountStatisticsSuccessPayload,
  ): GetBillPayCardsCardholderOrViewerMeActiveAndInactiveCountStatisticsSuccessAction => ({
    type: GET_BILL_PAY_CARDS_CARDHOLDER_OR_VIEWER_ME_ACTIVE_AND_INACTIVE_COUNT_STATISTICS_SUCCESS,
    payload,
  })
const createGetBillPayCardsCardholderOrViewerMeActiveAndInactiveCountStatisticsFailureAction =
  (
    response: unknown,
  ): GetBillPayCardsCardholderOrViewerMeActiveAndInactiveCountStatisticsFailureAction => ({
    type: GET_BILL_PAY_CARDS_CARDHOLDER_OR_VIEWER_ME_ACTIVE_AND_INACTIVE_COUNT_STATISTICS_FAILURE,
    payload: response,
  })

export const getBillPayCardsCardholderOrViewerMeActiveAndInactiveCountStatistics =
  (): ThunkAction<
    Promise<
      | GetBillPayCardsCardholderOrViewerMeActiveAndInactiveCountStatisticsFailureAction
      | GetBillPayCardsCardholderOrViewerMeActiveAndInactiveCountStatisticsSuccessAction
    >,
    RootState,
    void,
    GetBillPayCardsCardholderOrViewerMeActiveAndInactiveCountStatisticsActions
  > => {
    return async (
      dispatch: ThunkDispatch<
        RootState,
        void,
        GetBillPayCardsCardholderOrViewerMeActiveAndInactiveCountStatisticsActions
      >,
      getState: () => RootState,
    ): Promise<
      | GetBillPayCardsCardholderOrViewerMeActiveAndInactiveCountStatisticsFailureAction
      | GetBillPayCardsCardholderOrViewerMeActiveAndInactiveCountStatisticsSuccessAction
    > => {
      dispatch(
        createGetBillPayCardsCardholderOrViewerMeActiveAndInactiveCountStatisticsInitAction(),
      )

      return axios
        .post<GetBillPayCardsCardholderOrViewerMeActiveAndInactiveCountStatisticsSuccessPayload>(
          `/statistics`,
          {
            statistics: [
              {
                agg: StatisticAggregateType.Count,
                type: StatisticModelType.VirtualCard,
                query: {
                  issued: true,
                  cardholderOrViewer: 'me',
                  statuses: [
                    VirtualCardStatus.Active,
                    VirtualCardStatus.Cancelled,
                    VirtualCardStatus.Consumed,
                    VirtualCardStatus.Closed,
                    VirtualCardStatus.Expired,
                  ],
                  cardTypes: [VirtualCardTypes.billPay],
                },
              },
            ],
          },
          selectAuthedJsonApiRequestConfig(getState()),
        )
        .then(
          async (response) => {
            return dispatch(
              createGetBillPayCardsCardholderOrViewerMeActiveAndInactiveCountStatisticsSuccessAction(
                { ...response.data },
              ),
            )
          },
          (err) => {
            return dispatch(
              createGetBillPayCardsCardholderOrViewerMeActiveAndInactiveCountStatisticsFailureAction(
                err.response.data,
              ),
            )
          },
        )
    }
  }

export const GET_BILL_PAY_CARDS_PENDING_REQUESTS_COUNT_STATISTICS_INIT =
  'data/statistics/GET_BILL_PAY_CARDS_PENDING_REQUESTS_COUNT_STATISTICS_INIT'
export const GET_BILL_PAY_CARDS_PENDING_REQUESTS_COUNT_STATISTICS_SUCCESS =
  'data/statistics/GET_BILL_PAY_CARDS_PENDING_REQUESTS_COUNT_STATISTICS_SUCCESS'
export const GET_BILL_PAY_CARDS_PENDING_REQUESTS_COUNT_STATISTICS_FAILURE =
  'data/statistics/GET_BILL_PAY_CARDS_PENDING_REQUESTS_COUNT_STATISTICS_FAILURE'

export interface GetBillPayCardsPendingRequestsCountStatisticsSuccessPayload {
  statistics: Array<{ value: number }>
  statisticNames: string[]
}

export interface GetBillPayCardsPendingRequestsCountStatisticsInitAction {
  type: typeof GET_BILL_PAY_CARDS_PENDING_REQUESTS_COUNT_STATISTICS_INIT
}
export interface GetBillPayCardsPendingRequestsCountStatisticsSuccessAction {
  type: typeof GET_BILL_PAY_CARDS_PENDING_REQUESTS_COUNT_STATISTICS_SUCCESS
  payload: GetBillPayCardsPendingRequestsCountStatisticsSuccessPayload
}
export interface GetBillPayCardsPendingRequestsCountStatisticsFailureAction {
  type: typeof GET_BILL_PAY_CARDS_PENDING_REQUESTS_COUNT_STATISTICS_FAILURE
  payload: unknown
}

export type GetBillPayCardsPendingRequestsCountStatisticsActions =
  | GetBillPayCardsPendingRequestsCountStatisticsInitAction
  | GetBillPayCardsPendingRequestsCountStatisticsSuccessAction
  | GetBillPayCardsPendingRequestsCountStatisticsFailureAction

const createGetBillPayCardsPendingRequestsCountStatisticsInitAction =
  (): GetBillPayCardsPendingRequestsCountStatisticsInitAction => ({
    type: GET_BILL_PAY_CARDS_PENDING_REQUESTS_COUNT_STATISTICS_INIT,
  })
const createGetBillPayCardsPendingRequestsCountStatisticsSuccessAction = (
  payload: GetBillPayCardsPendingRequestsCountStatisticsSuccessPayload,
): GetBillPayCardsPendingRequestsCountStatisticsSuccessAction => ({
  type: GET_BILL_PAY_CARDS_PENDING_REQUESTS_COUNT_STATISTICS_SUCCESS,
  payload,
})
const createGetBillPayCardsPendingRequestsCountStatisticsFailureAction = (
  response: unknown,
): GetBillPayCardsPendingRequestsCountStatisticsFailureAction => ({
  type: GET_BILL_PAY_CARDS_PENDING_REQUESTS_COUNT_STATISTICS_FAILURE,
  payload: response,
})

export const getBillPayCardsPendingRequestsCountStatistics = (): ThunkAction<
  Promise<
    | GetBillPayCardsPendingRequestsCountStatisticsFailureAction
    | GetBillPayCardsPendingRequestsCountStatisticsSuccessAction
  >,
  RootState,
  void,
  GetBillPayCardsPendingRequestsCountStatisticsActions
> => {
  return async (
    dispatch: ThunkDispatch<
      RootState,
      void,
      GetBillPayCardsPendingRequestsCountStatisticsActions
    >,
    getState: () => RootState,
  ): Promise<
    | GetBillPayCardsPendingRequestsCountStatisticsFailureAction
    | GetBillPayCardsPendingRequestsCountStatisticsSuccessAction
  > => {
    dispatch(createGetBillPayCardsPendingRequestsCountStatisticsInitAction())

    return axios
      .post<GetBillPayCardsPendingRequestsCountStatisticsSuccessPayload>(
        `/statistics`,
        {
          statistics: [
            {
              agg: StatisticAggregateType.Count,
              type: StatisticModelType.VirtualCard,
              query: {
                cardholderPendingRequestOrApproverRuleApproval: 'me',
                withPermission: VirtualCardPermissions.UpdateVirtualCard,
                cardTypes: [VirtualCardTypes.billPay],
              },
            },
          ],
        },
        selectAuthedJsonApiRequestConfig(getState()),
      )
      .then(
        async (response) => {
          return dispatch(
            createGetBillPayCardsPendingRequestsCountStatisticsSuccessAction({
              ...response.data,
            }),
          )
        },
        (err) => {
          return dispatch(
            createGetBillPayCardsPendingRequestsCountStatisticsFailureAction(
              err.response.data,
            ),
          )
        },
      )
  }
}

export const GET_CREDIT_CARDS_CAN_ISSUE_VIRTUAL_CARDS_COUNT_STATISTICS_INIT =
  'data/statistics/GET_CREDIT_CARDS_CAN_ISSUE_VIRTUAL_CARDS_COUNT_STATISTICS_INIT'
export const GET_CREDIT_CARDS_CAN_ISSUE_VIRTUAL_CARDS_COUNT_STATISTICS_SUCCESS =
  'data/statistics/GET_CREDIT_CARDS_CAN_ISSUE_VIRTUAL_CARDS_COUNT_STATISTICS_SUCCESS'
export const GET_CREDIT_CARDS_CAN_ISSUE_VIRTUAL_CARDS_COUNT_STATISTICS_FAILURE =
  'data/statistics/GET_CREDIT_CARDS_CAN_ISSUE_VIRTUAL_CARDS_COUNT_STATISTICS_FAILURE'

export interface GetCreditCardsCanIssueVirtualCardsCountStatisticsSuccessPayload {
  statistics: Array<{ value: number }>
  statisticNames: string[]
}

export interface GetCreditCardsCanIssueVirtualCardsCountStatisticsInitAction {
  type: typeof GET_CREDIT_CARDS_CAN_ISSUE_VIRTUAL_CARDS_COUNT_STATISTICS_INIT
}
export interface GetCreditCardsCanIssueVirtualCardsCountStatisticsSuccessAction {
  type: typeof GET_CREDIT_CARDS_CAN_ISSUE_VIRTUAL_CARDS_COUNT_STATISTICS_SUCCESS
  payload: GetCreditCardsCanIssueVirtualCardsCountStatisticsSuccessPayload
}
export interface GetCreditCardsCanIssueVirtualCardsCountStatisticsFailureAction {
  type: typeof GET_CREDIT_CARDS_CAN_ISSUE_VIRTUAL_CARDS_COUNT_STATISTICS_FAILURE
  payload: unknown
}

export type GetCreditCardsCanIssueVirtualCardsCountStatisticsActions =
  | GetCreditCardsCanIssueVirtualCardsCountStatisticsInitAction
  | GetCreditCardsCanIssueVirtualCardsCountStatisticsSuccessAction
  | GetCreditCardsCanIssueVirtualCardsCountStatisticsFailureAction

const createGetCreditCardsCanIssueVirtualCardsCountStatisticsInitAction =
  (): GetCreditCardsCanIssueVirtualCardsCountStatisticsInitAction => ({
    type: GET_CREDIT_CARDS_CAN_ISSUE_VIRTUAL_CARDS_COUNT_STATISTICS_INIT,
  })
const createGetCreditCardsCanIssueVirtualCardsCountStatisticsSuccessAction = (
  payload: GetCreditCardsCanIssueVirtualCardsCountStatisticsSuccessPayload,
): GetCreditCardsCanIssueVirtualCardsCountStatisticsSuccessAction => ({
  type: GET_CREDIT_CARDS_CAN_ISSUE_VIRTUAL_CARDS_COUNT_STATISTICS_SUCCESS,
  payload,
})
const createGetCreditCardsCanIssueVirtualCardsCountStatisticsFailureAction = (
  response: unknown,
): GetCreditCardsCanIssueVirtualCardsCountStatisticsFailureAction => ({
  type: GET_CREDIT_CARDS_CAN_ISSUE_VIRTUAL_CARDS_COUNT_STATISTICS_FAILURE,
  payload: response,
})

export const getCreditCardsCanIssueVirtualCardsCountStatistics =
  (): ThunkAction<
    Promise<
      | GetCreditCardsCanIssueVirtualCardsCountStatisticsFailureAction
      | GetCreditCardsCanIssueVirtualCardsCountStatisticsSuccessAction
    >,
    RootState,
    void,
    GetCreditCardsCanIssueVirtualCardsCountStatisticsActions
  > => {
    return async (
      dispatch: ThunkDispatch<
        RootState,
        void,
        GetCreditCardsCanIssueVirtualCardsCountStatisticsActions
      >,
      getState: () => RootState,
    ): Promise<
      | GetCreditCardsCanIssueVirtualCardsCountStatisticsFailureAction
      | GetCreditCardsCanIssueVirtualCardsCountStatisticsSuccessAction
    > => {
      dispatch(
        createGetCreditCardsCanIssueVirtualCardsCountStatisticsInitAction(),
      )

      return axios
        .post<GetCreditCardsCanIssueVirtualCardsCountStatisticsSuccessPayload>(
          `/statistics`,
          {
            statistics: [
              {
                agg: StatisticAggregateType.Count,
                type: StatisticModelType.CreditCard,
                query: {
                  withPermission: CreditCardPermissions.IssueVirtualCards,
                },
              },
            ],
          },
          selectAuthedJsonApiRequestConfig(getState()),
        )
        .then(
          async (response) => {
            return dispatch(
              createGetCreditCardsCanIssueVirtualCardsCountStatisticsSuccessAction(
                { ...response.data },
              ),
            )
          },
          (err) => {
            return dispatch(
              createGetCreditCardsCanIssueVirtualCardsCountStatisticsFailureAction(
                err.response.data,
              ),
            )
          },
        )
    }
  }

export const GET_CREDIT_CARDS_SOURCE_BUDGETS_ENABLED_CAN_ISSUE_BUDGETS_COUNT_STATISTICS_INIT =
  'data/statistics/GET_CREDIT_CARDS_SOURCE_BUDGETS_ENABLED_CAN_ISSUE_BUDGETS_COUNT_STATISTICS_INIT'
export const GET_CREDIT_CARDS_SOURCE_BUDGETS_ENABLED_CAN_ISSUE_BUDGETS_COUNT_STATISTICS_SUCCESS =
  'data/statistics/GET_CREDIT_CARDS_SOURCE_BUDGETS_ENABLED_CAN_ISSUE_BUDGETS_COUNT_STATISTICS_SUCCESS'
export const GET_CREDIT_CARDS_SOURCE_BUDGETS_ENABLED_CAN_ISSUE_BUDGETS_COUNT_STATISTICS_FAILURE =
  'data/statistics/GET_CREDIT_CARDS_SOURCE_BUDGETS_ENABLED_CAN_ISSUE_BUDGETS_COUNT_STATISTICS_FAILURE'

export interface GetCreditCardsSourceBudgetsEnabledCanIssueBudgetsCountStatisticsSuccessPayload {
  statistics: Array<{ value: number }>
  statisticNames: string[]
}

export interface GetCreditCardsSourceBudgetsEnabledCanIssueBudgetsCountStatisticsInitAction {
  type: typeof GET_CREDIT_CARDS_SOURCE_BUDGETS_ENABLED_CAN_ISSUE_BUDGETS_COUNT_STATISTICS_INIT
}
export interface GetCreditCardsSourceBudgetsEnabledCanIssueBudgetsCountStatisticsSuccessAction {
  type: typeof GET_CREDIT_CARDS_SOURCE_BUDGETS_ENABLED_CAN_ISSUE_BUDGETS_COUNT_STATISTICS_SUCCESS
  payload: GetCreditCardsSourceBudgetsEnabledCanIssueBudgetsCountStatisticsSuccessPayload
}
export interface GetCreditCardsSourceBudgetsEnabledCanIssueBudgetsCountStatisticsFailureAction {
  type: typeof GET_CREDIT_CARDS_SOURCE_BUDGETS_ENABLED_CAN_ISSUE_BUDGETS_COUNT_STATISTICS_FAILURE
  payload: unknown
}

export type GetCreditCardsSourceBudgetsEnabledCanIssueBudgetsCountStatisticsActions =

    | GetCreditCardsSourceBudgetsEnabledCanIssueBudgetsCountStatisticsInitAction
    | GetCreditCardsSourceBudgetsEnabledCanIssueBudgetsCountStatisticsSuccessAction
    | GetCreditCardsSourceBudgetsEnabledCanIssueBudgetsCountStatisticsFailureAction

const createGetCreditCardsSourceBudgetsEnabledCanIssueBudgetsCountStatisticsInitAction =
  (): GetCreditCardsSourceBudgetsEnabledCanIssueBudgetsCountStatisticsInitAction => ({
    type: GET_CREDIT_CARDS_SOURCE_BUDGETS_ENABLED_CAN_ISSUE_BUDGETS_COUNT_STATISTICS_INIT,
  })
const createGetCreditCardsSourceBudgetsEnabledCanIssueBudgetsCountStatisticsSuccessAction =
  (
    payload: GetCreditCardsSourceBudgetsEnabledCanIssueBudgetsCountStatisticsSuccessPayload,
  ): GetCreditCardsSourceBudgetsEnabledCanIssueBudgetsCountStatisticsSuccessAction => ({
    type: GET_CREDIT_CARDS_SOURCE_BUDGETS_ENABLED_CAN_ISSUE_BUDGETS_COUNT_STATISTICS_SUCCESS,
    payload,
  })
const createGetCreditCardsSourceBudgetsEnabledCanIssueBudgetsCountStatisticsFailureAction =
  (
    response: unknown,
  ): GetCreditCardsSourceBudgetsEnabledCanIssueBudgetsCountStatisticsFailureAction => ({
    type: GET_CREDIT_CARDS_SOURCE_BUDGETS_ENABLED_CAN_ISSUE_BUDGETS_COUNT_STATISTICS_FAILURE,
    payload: response,
  })

export const getCreditCardsSourceBudgetsEnabledCanIssueBudgetsCountStatistics =
  (): ThunkAction<
    Promise<
      | GetCreditCardsSourceBudgetsEnabledCanIssueBudgetsCountStatisticsFailureAction
      | GetCreditCardsSourceBudgetsEnabledCanIssueBudgetsCountStatisticsSuccessAction
    >,
    RootState,
    void,
    GetCreditCardsSourceBudgetsEnabledCanIssueBudgetsCountStatisticsActions
  > => {
    return async (
      dispatch: ThunkDispatch<
        RootState,
        void,
        GetCreditCardsSourceBudgetsEnabledCanIssueBudgetsCountStatisticsActions
      >,
      getState: () => RootState,
    ): Promise<
      | GetCreditCardsSourceBudgetsEnabledCanIssueBudgetsCountStatisticsFailureAction
      | GetCreditCardsSourceBudgetsEnabledCanIssueBudgetsCountStatisticsSuccessAction
    > => {
      dispatch(
        createGetCreditCardsSourceBudgetsEnabledCanIssueBudgetsCountStatisticsInitAction(),
      )

      return axios
        .post<GetCreditCardsSourceBudgetsEnabledCanIssueBudgetsCountStatisticsSuccessPayload>(
          `/statistics`,
          {
            statistics: [
              {
                agg: StatisticAggregateType.Count,
                type: StatisticModelType.CreditCard,
                query: {
                  type: CreditCardType.Source,
                  budgetsEnabled: true,
                  withPermission:
                    CreditCardPermissions.IssueDelegateCreditCards,
                },
              },
            ],
          },
          selectAuthedJsonApiRequestConfig(getState()),
        )
        .then(
          async (response) => {
            return dispatch(
              createGetCreditCardsSourceBudgetsEnabledCanIssueBudgetsCountStatisticsSuccessAction(
                { ...response.data },
              ),
            )
          },
          (err) => {
            return dispatch(
              createGetCreditCardsSourceBudgetsEnabledCanIssueBudgetsCountStatisticsFailureAction(
                err.response.data,
              ),
            )
          },
        )
    }
  }

export const GET_CREDIT_CARDS_SOURCE_BUDGETS_ENABLED_COUNT_STATISTICS_INIT =
  'data/statistics/GET_CREDIT_CARDS_SOURCE_BUDGETS_ENABLED_COUNT_STATISTICS_INIT'
export const GET_CREDIT_CARDS_SOURCE_BUDGETS_ENABLED_COUNT_STATISTICS_SUCCESS =
  'data/statistics/GET_CREDIT_CARDS_SOURCE_BUDGETS_ENABLED_COUNT_STATISTICS_SUCCESS'
export const GET_CREDIT_CARDS_SOURCE_BUDGETS_ENABLED_COUNT_STATISTICS_FAILURE =
  'data/statistics/GET_CREDIT_CARDS_SOURCE_BUDGETS_ENABLED_COUNT_STATISTICS_FAILURE'

export interface GetCreditCardsSourceBudgetsEnabledCountStatisticsSuccessPayload {
  statistics: Array<{ value: number }>
  statisticNames: string[]
}

export interface GetCreditCardsSourceBudgetsEnabledCountStatisticsInitAction {
  type: typeof GET_CREDIT_CARDS_SOURCE_BUDGETS_ENABLED_COUNT_STATISTICS_INIT
}
export interface GetCreditCardsSourceBudgetsEnabledCountStatisticsSuccessAction {
  type: typeof GET_CREDIT_CARDS_SOURCE_BUDGETS_ENABLED_COUNT_STATISTICS_SUCCESS
  payload: GetCreditCardsSourceBudgetsEnabledCountStatisticsSuccessPayload
}
export interface GetCreditCardsSourceBudgetsEnabledCountStatisticsFailureAction {
  type: typeof GET_CREDIT_CARDS_SOURCE_BUDGETS_ENABLED_COUNT_STATISTICS_FAILURE
  payload: unknown
}

export type GetCreditCardsSourceBudgetsEnabledCountStatisticsActions =
  | GetCreditCardsSourceBudgetsEnabledCountStatisticsInitAction
  | GetCreditCardsSourceBudgetsEnabledCountStatisticsSuccessAction
  | GetCreditCardsSourceBudgetsEnabledCountStatisticsFailureAction

const createGetCreditCardsSourceBudgetsEnabledCountStatisticsInitAction =
  (): GetCreditCardsSourceBudgetsEnabledCountStatisticsInitAction => ({
    type: GET_CREDIT_CARDS_SOURCE_BUDGETS_ENABLED_COUNT_STATISTICS_INIT,
  })
const createGetCreditCardsSourceBudgetsEnabledCountStatisticsSuccessAction = (
  payload: GetCreditCardsSourceBudgetsEnabledCountStatisticsSuccessPayload,
): GetCreditCardsSourceBudgetsEnabledCountStatisticsSuccessAction => ({
  type: GET_CREDIT_CARDS_SOURCE_BUDGETS_ENABLED_COUNT_STATISTICS_SUCCESS,
  payload,
})
const createGetCreditCardsSourceBudgetsEnabledCountStatisticsFailureAction = (
  response: unknown,
): GetCreditCardsSourceBudgetsEnabledCountStatisticsFailureAction => ({
  type: GET_CREDIT_CARDS_SOURCE_BUDGETS_ENABLED_COUNT_STATISTICS_FAILURE,
  payload: response,
})

export const getCreditCardsSourceBudgetsEnabledCountStatistics =
  (): ThunkAction<
    Promise<
      | GetCreditCardsSourceBudgetsEnabledCountStatisticsFailureAction
      | GetCreditCardsSourceBudgetsEnabledCountStatisticsSuccessAction
    >,
    RootState,
    void,
    GetCreditCardsSourceBudgetsEnabledCountStatisticsActions
  > => {
    return async (
      dispatch: ThunkDispatch<
        RootState,
        void,
        GetCreditCardsSourceBudgetsEnabledCountStatisticsActions
      >,
      getState: () => RootState,
    ): Promise<
      | GetCreditCardsSourceBudgetsEnabledCountStatisticsFailureAction
      | GetCreditCardsSourceBudgetsEnabledCountStatisticsSuccessAction
    > => {
      dispatch(
        createGetCreditCardsSourceBudgetsEnabledCountStatisticsInitAction(),
      )

      return axios
        .post<GetCreditCardsSourceBudgetsEnabledCountStatisticsSuccessPayload>(
          `/statistics`,
          {
            statistics: [
              {
                agg: StatisticAggregateType.Count,
                type: StatisticModelType.CreditCard,
                query: {
                  type: CreditCardType.Source,
                  budgetsEnabled: true,
                },
              },
            ],
          },
          selectAuthedJsonApiRequestConfig(getState()),
        )
        .then(
          async (response) => {
            return dispatch(
              createGetCreditCardsSourceBudgetsEnabledCountStatisticsSuccessAction(
                { ...response.data },
              ),
            )
          },
          (err) => {
            return dispatch(
              createGetCreditCardsSourceBudgetsEnabledCountStatisticsFailureAction(
                err.response.data,
              ),
            )
          },
        )
    }
  }

export const GET_CREDIT_CARDS_USER_OR_VIEWER_ME_COUNT_STATISTICS_INIT =
  'data/statistics/GET_CREDIT_CARDS_USER_OR_VIEWER_ME_COUNT_STATISTICS_INIT'
export const GET_CREDIT_CARDS_USER_OR_VIEWER_ME_COUNT_STATISTICS_SUCCESS =
  'data/statistics/GET_CREDIT_CARDS_USER_OR_VIEWER_ME_COUNT_STATISTICS_SUCCESS'
export const GET_CREDIT_CARDS_USER_OR_VIEWER_ME_COUNT_STATISTICS_FAILURE =
  'data/statistics/GET_CREDIT_CARDS_USER_OR_VIEWER_ME_COUNT_STATISTICS_FAILURE'

export interface GetCreditCardsUserOrViewerMeCountStatisticsSuccessPayload {
  statistics: Array<{ value: number }>
  statisticNames: string[]
}

export interface GetCreditCardsUserOrViewerMeCountStatisticsInitAction {
  type: typeof GET_CREDIT_CARDS_USER_OR_VIEWER_ME_COUNT_STATISTICS_INIT
}
export interface GetCreditCardsUserOrViewerMeCountStatisticsSuccessAction {
  type: typeof GET_CREDIT_CARDS_USER_OR_VIEWER_ME_COUNT_STATISTICS_SUCCESS
  payload: GetCreditCardsUserOrViewerMeCountStatisticsSuccessPayload
}
export interface GetCreditCardsUserOrViewerMeCountStatisticsFailureAction {
  type: typeof GET_CREDIT_CARDS_USER_OR_VIEWER_ME_COUNT_STATISTICS_FAILURE
  payload: unknown
}

export type GetCreditCardsUserOrViewerMeCountStatisticsActions =
  | GetCreditCardsUserOrViewerMeCountStatisticsInitAction
  | GetCreditCardsUserOrViewerMeCountStatisticsSuccessAction
  | GetCreditCardsUserOrViewerMeCountStatisticsFailureAction

const createGetCreditCardsUserOrViewerMeCountStatisticsInitAction =
  (): GetCreditCardsUserOrViewerMeCountStatisticsInitAction => ({
    type: GET_CREDIT_CARDS_USER_OR_VIEWER_ME_COUNT_STATISTICS_INIT,
  })
const createGetCreditCardsUserOrViewerMeCountStatisticsSuccessAction = (
  payload: GetCreditCardsUserOrViewerMeCountStatisticsSuccessPayload,
): GetCreditCardsUserOrViewerMeCountStatisticsSuccessAction => ({
  type: GET_CREDIT_CARDS_USER_OR_VIEWER_ME_COUNT_STATISTICS_SUCCESS,
  payload,
})
const createGetCreditCardsUserOrViewerMeCountStatisticsFailureAction = (
  response: unknown,
): GetCreditCardsUserOrViewerMeCountStatisticsFailureAction => ({
  type: GET_CREDIT_CARDS_USER_OR_VIEWER_ME_COUNT_STATISTICS_FAILURE,
  payload: response,
})

export const getCreditCardsUserOrViewerMeCountStatistics = (): ThunkAction<
  Promise<
    | GetCreditCardsUserOrViewerMeCountStatisticsFailureAction
    | GetCreditCardsUserOrViewerMeCountStatisticsSuccessAction
  >,
  RootState,
  void,
  GetCreditCardsUserOrViewerMeCountStatisticsActions
> => {
  return async (
    dispatch: ThunkDispatch<
      RootState,
      void,
      GetCreditCardsUserOrViewerMeCountStatisticsActions
    >,
    getState: () => RootState,
  ): Promise<
    | GetCreditCardsUserOrViewerMeCountStatisticsFailureAction
    | GetCreditCardsUserOrViewerMeCountStatisticsSuccessAction
  > => {
    dispatch(createGetCreditCardsUserOrViewerMeCountStatisticsInitAction())

    return axios
      .post<GetCreditCardsUserOrViewerMeCountStatisticsSuccessPayload>(
        `/statistics`,
        {
          statistics: [
            {
              agg: StatisticAggregateType.Count,
              type: StatisticModelType.CreditCard,
              query: {
                // TODO: remove this once backend has a fix for dealing with empty query objects
                neverGonnaGive: 'youUp',
                neverGonnaLet: 'youDown',
              },
            },
          ],
        },
        selectAuthedJsonApiRequestConfig(getState()),
      )
      .then(
        async (response) => {
          return dispatch(
            createGetCreditCardsUserOrViewerMeCountStatisticsSuccessAction({
              ...response.data,
            }),
          )
        },
        (err) => {
          return dispatch(
            createGetCreditCardsUserOrViewerMeCountStatisticsFailureAction(
              err.response.data,
            ),
          )
        },
      )
  }
}

export const GET_VIRTUAL_CARDS_PENDING_REQUEST_CARDHOLDER_ME_COUNT_STATISTICS_INIT =
  'data/statistics/GET_VIRTUAL_CARDS_PENDING_REQUEST_CARDHOLDER_ME_COUNT_STATISTICS_INIT'
export const GET_VIRTUAL_CARDS_PENDING_REQUEST_CARDHOLDER_ME_COUNT_STATISTICS_SUCCESS =
  'data/statistics/GET_VIRTUAL_CARDS_PENDING_REQUEST_CARDHOLDER_ME_COUNT_STATISTICS_SUCCESS'
export const GET_VIRTUAL_CARDS_PENDING_REQUEST_CARDHOLDER_ME_COUNT_STATISTICS_FAILURE =
  'data/statistics/GET_VIRTUAL_CARDS_PENDING_REQUEST_CARDHOLDER_ME_COUNT_STATISTICS_FAILURE'

export interface GetVirtualCardsPendingRequestCardholderMeCountStatisticsSuccessPayload {
  statistics: Array<{ value: number }>
  statisticNames: string[]
}

export interface GetVirtualCardsPendingRequestCardholderMeCountStatisticsInitAction {
  type: typeof GET_VIRTUAL_CARDS_PENDING_REQUEST_CARDHOLDER_ME_COUNT_STATISTICS_INIT
}
export interface GetVirtualCardsPendingRequestCardholderMeCountStatisticsSuccessAction {
  type: typeof GET_VIRTUAL_CARDS_PENDING_REQUEST_CARDHOLDER_ME_COUNT_STATISTICS_SUCCESS
  payload: GetVirtualCardsPendingRequestCardholderMeCountStatisticsSuccessPayload
}
export interface GetVirtualCardsPendingRequestCardholderMeCountStatisticsFailureAction {
  type: typeof GET_VIRTUAL_CARDS_PENDING_REQUEST_CARDHOLDER_ME_COUNT_STATISTICS_FAILURE
  payload: unknown
}

export type GetVirtualCardsPendingRequestCardholderMeCountStatisticsActions =
  | GetVirtualCardsPendingRequestCardholderMeCountStatisticsInitAction
  | GetVirtualCardsPendingRequestCardholderMeCountStatisticsSuccessAction
  | GetVirtualCardsPendingRequestCardholderMeCountStatisticsFailureAction

const createGetVirtualCardsPendingRequestCardholderMeCountStatisticsInitAction =
  (): GetVirtualCardsPendingRequestCardholderMeCountStatisticsInitAction => ({
    type: GET_VIRTUAL_CARDS_PENDING_REQUEST_CARDHOLDER_ME_COUNT_STATISTICS_INIT,
  })
const createGetVirtualCardsPendingRequestCardholderMeCountStatisticsSuccessAction =
  (
    payload: GetVirtualCardsPendingRequestCardholderMeCountStatisticsSuccessPayload,
  ): GetVirtualCardsPendingRequestCardholderMeCountStatisticsSuccessAction => ({
    type: GET_VIRTUAL_CARDS_PENDING_REQUEST_CARDHOLDER_ME_COUNT_STATISTICS_SUCCESS,
    payload,
  })
const createGetVirtualCardsPendingRequestCardholderMeCountStatisticsFailureAction =
  (
    response: unknown,
  ): GetVirtualCardsPendingRequestCardholderMeCountStatisticsFailureAction => ({
    type: GET_VIRTUAL_CARDS_PENDING_REQUEST_CARDHOLDER_ME_COUNT_STATISTICS_FAILURE,
    payload: response,
  })

export const getVirtualCardsPendingRequestCardholderMeCountStatistics =
  (): ThunkAction<
    Promise<
      | GetVirtualCardsPendingRequestCardholderMeCountStatisticsFailureAction
      | GetVirtualCardsPendingRequestCardholderMeCountStatisticsSuccessAction
    >,
    RootState,
    void,
    GetVirtualCardsPendingRequestCardholderMeCountStatisticsActions
  > => {
    return async (
      dispatch: ThunkDispatch<
        RootState,
        void,
        GetVirtualCardsPendingRequestCardholderMeCountStatisticsActions
      >,
      getState: () => RootState,
    ): Promise<
      | GetVirtualCardsPendingRequestCardholderMeCountStatisticsFailureAction
      | GetVirtualCardsPendingRequestCardholderMeCountStatisticsSuccessAction
    > => {
      dispatch(
        createGetVirtualCardsPendingRequestCardholderMeCountStatisticsInitAction(),
      )

      return axios
        .post<GetVirtualCardsPendingRequestCardholderMeCountStatisticsSuccessPayload>(
          `/statistics`,
          {
            statistics: [
              {
                agg: StatisticAggregateType.Count,
                type: StatisticModelType.VirtualCard,
                query: {
                  pendingRequest: true,
                  cardholder: 'me',
                },
              },
            ],
          },
          selectAuthedJsonApiRequestConfig(getState()),
        )
        .then(
          async (response) => {
            return dispatch(
              createGetVirtualCardsPendingRequestCardholderMeCountStatisticsSuccessAction(
                { ...response.data },
              ),
            )
          },
          (err) => {
            return dispatch(
              createGetVirtualCardsPendingRequestCardholderMeCountStatisticsFailureAction(
                err.response.data,
              ),
            )
          },
        )
    }
  }

export const GET_CREDIT_CARDS_PENDING_SOURCE_USER_OR_VIEWER_ME_COUNT_STATISTICS_INIT =
  'data/statistics/GET_CREDIT_CARDS_PENDING_SOURCE_USER_OR_VIEWER_ME_COUNT_STATISTICS_INIT'
export const GET_CREDIT_CARDS_PENDING_SOURCE_USER_OR_VIEWER_ME_COUNT_STATISTICS_SUCCESS =
  'data/statistics/GET_CREDIT_CARDS_PENDING_SOURCE_USER_OR_VIEWER_ME_COUNT_STATISTICS_SUCCESS'
export const GET_CREDIT_CARDS_PENDING_SOURCE_USER_OR_VIEWER_ME_COUNT_STATISTICS_FAILURE =
  'data/statistics/GET_CREDIT_CARDS_PENDING_SOURCE_USER_OR_VIEWER_ME_COUNT_STATISTICS_FAILURE'

export interface GetCreditCardsPendingSourceUserOrViewerMeCountStatisticsSuccessPayload {
  statistics: Array<{ value: number }>
  statisticNames: string[]
}

export interface GetCreditCardsPendingSourceUserOrViewerMeCountStatisticsInitAction {
  type: typeof GET_CREDIT_CARDS_PENDING_SOURCE_USER_OR_VIEWER_ME_COUNT_STATISTICS_INIT
}
export interface GetCreditCardsPendingSourceUserOrViewerMeCountStatisticsSuccessAction {
  type: typeof GET_CREDIT_CARDS_PENDING_SOURCE_USER_OR_VIEWER_ME_COUNT_STATISTICS_SUCCESS
  payload: GetCreditCardsPendingSourceUserOrViewerMeCountStatisticsSuccessPayload
}
export interface GetCreditCardsPendingSourceUserOrViewerMeCountStatisticsFailureAction {
  type: typeof GET_CREDIT_CARDS_PENDING_SOURCE_USER_OR_VIEWER_ME_COUNT_STATISTICS_FAILURE
  payload: unknown
}

export type GetCreditCardsPendingSourceUserOrViewerMeCountStatisticsActions =
  | GetCreditCardsPendingSourceUserOrViewerMeCountStatisticsInitAction
  | GetCreditCardsPendingSourceUserOrViewerMeCountStatisticsSuccessAction
  | GetCreditCardsPendingSourceUserOrViewerMeCountStatisticsFailureAction

const createGetCreditCardsPendingSourceUserOrViewerMeCountStatisticsInitAction =
  (): GetCreditCardsPendingSourceUserOrViewerMeCountStatisticsInitAction => ({
    type: GET_CREDIT_CARDS_PENDING_SOURCE_USER_OR_VIEWER_ME_COUNT_STATISTICS_INIT,
  })
const createGetCreditCardsPendingSourceUserOrViewerMeCountStatisticsSuccessAction =
  (
    payload: GetCreditCardsPendingSourceUserOrViewerMeCountStatisticsSuccessPayload,
  ): GetCreditCardsPendingSourceUserOrViewerMeCountStatisticsSuccessAction => ({
    type: GET_CREDIT_CARDS_PENDING_SOURCE_USER_OR_VIEWER_ME_COUNT_STATISTICS_SUCCESS,
    payload,
  })
const createGetCreditCardsPendingSourceUserOrViewerMeCountStatisticsFailureAction =
  (
    response: unknown,
  ): GetCreditCardsPendingSourceUserOrViewerMeCountStatisticsFailureAction => ({
    type: GET_CREDIT_CARDS_PENDING_SOURCE_USER_OR_VIEWER_ME_COUNT_STATISTICS_FAILURE,
    payload: response,
  })

export const getCreditCardsPendingSourceUserOrViewerMeCountStatistics =
  (): ThunkAction<
    Promise<
      | GetCreditCardsPendingSourceUserOrViewerMeCountStatisticsFailureAction
      | GetCreditCardsPendingSourceUserOrViewerMeCountStatisticsSuccessAction
    >,
    RootState,
    void,
    GetCreditCardsPendingSourceUserOrViewerMeCountStatisticsActions
  > => {
    return async (
      dispatch: ThunkDispatch<
        RootState,
        void,
        GetCreditCardsPendingSourceUserOrViewerMeCountStatisticsActions
      >,
      getState: () => RootState,
    ): Promise<
      | GetCreditCardsPendingSourceUserOrViewerMeCountStatisticsFailureAction
      | GetCreditCardsPendingSourceUserOrViewerMeCountStatisticsSuccessAction
    > => {
      dispatch(
        createGetCreditCardsPendingSourceUserOrViewerMeCountStatisticsInitAction(),
      )

      return axios
        .post<GetCreditCardsPendingSourceUserOrViewerMeCountStatisticsSuccessPayload>(
          `/statistics`,
          {
            statistics: [
              {
                agg: StatisticAggregateType.Count,
                type: StatisticModelType.CreditCard,
                query: {
                  displayStatus: CreditCardDisplayStatus.Pending,
                  type: CreditCardType.Source,
                },
              },
            ],
          },
          selectAuthedJsonApiRequestConfig(getState()),
        )
        .then(
          async (response) => {
            return dispatch(
              createGetCreditCardsPendingSourceUserOrViewerMeCountStatisticsSuccessAction(
                { ...response.data },
              ),
            )
          },
          (err) => {
            return dispatch(
              createGetCreditCardsPendingSourceUserOrViewerMeCountStatisticsFailureAction(
                err.response.data,
              ),
            )
          },
        )
    }
  }

export const GET_CREDIT_CARDS_PENDING_SOURCE_USER_ME_COUNT_STATISTICS_INIT =
  'data/statistics/GET_CREDIT_CARDS_PENDING_SOURCE_USER_ME_COUNT_STATISTICS_INIT'
export const GET_CREDIT_CARDS_PENDING_SOURCE_USER_ME_COUNT_STATISTICS_SUCCESS =
  'data/statistics/GET_CREDIT_CARDS_PENDING_SOURCE_USER_ME_COUNT_STATISTICS_SUCCESS'
export const GET_CREDIT_CARDS_PENDING_SOURCE_USER_ME_COUNT_STATISTICS_FAILURE =
  'data/statistics/GET_CREDIT_CARDS_PENDING_SOURCE_USER_ME_COUNT_STATISTICS_FAILURE'

export interface GetCreditCardsPendingSourceUserMeCountStatisticsSuccessPayload {
  statistics: Array<{ value: number }>
  statisticNames: string[]
}

export interface GetCreditCardsPendingSourceUserMeCountStatisticsInitAction {
  type: typeof GET_CREDIT_CARDS_PENDING_SOURCE_USER_ME_COUNT_STATISTICS_INIT
}
export interface GetCreditCardsPendingSourceUserMeCountStatisticsSuccessAction {
  type: typeof GET_CREDIT_CARDS_PENDING_SOURCE_USER_ME_COUNT_STATISTICS_SUCCESS
  payload: GetCreditCardsPendingSourceUserMeCountStatisticsSuccessPayload
}
export interface GetCreditCardsPendingSourceUserMeCountStatisticsFailureAction {
  type: typeof GET_CREDIT_CARDS_PENDING_SOURCE_USER_ME_COUNT_STATISTICS_FAILURE
  payload: unknown
}

export type GetCreditCardsPendingSourceUserMeCountStatisticsActions =
  | GetCreditCardsPendingSourceUserMeCountStatisticsInitAction
  | GetCreditCardsPendingSourceUserMeCountStatisticsSuccessAction
  | GetCreditCardsPendingSourceUserMeCountStatisticsFailureAction

const createGetCreditCardsPendingSourceUserMeCountStatisticsInitAction =
  (): GetCreditCardsPendingSourceUserMeCountStatisticsInitAction => ({
    type: GET_CREDIT_CARDS_PENDING_SOURCE_USER_ME_COUNT_STATISTICS_INIT,
  })
const createGetCreditCardsPendingSourceUserMeCountStatisticsSuccessAction = (
  payload: GetCreditCardsPendingSourceUserMeCountStatisticsSuccessPayload,
): GetCreditCardsPendingSourceUserMeCountStatisticsSuccessAction => ({
  type: GET_CREDIT_CARDS_PENDING_SOURCE_USER_ME_COUNT_STATISTICS_SUCCESS,
  payload,
})
const createGetCreditCardsPendingSourceUserMeCountStatisticsFailureAction = (
  response: unknown,
): GetCreditCardsPendingSourceUserMeCountStatisticsFailureAction => ({
  type: GET_CREDIT_CARDS_PENDING_SOURCE_USER_ME_COUNT_STATISTICS_FAILURE,
  payload: response,
})

export const getCreditCardsPendingSourceUserMeCountStatistics = (): ThunkAction<
  Promise<
    | GetCreditCardsPendingSourceUserMeCountStatisticsFailureAction
    | GetCreditCardsPendingSourceUserMeCountStatisticsSuccessAction
  >,
  RootState,
  void,
  GetCreditCardsPendingSourceUserMeCountStatisticsActions
> => {
  return async (
    dispatch: ThunkDispatch<
      RootState,
      void,
      GetCreditCardsPendingSourceUserMeCountStatisticsActions
    >,
    getState: () => RootState,
  ): Promise<
    | GetCreditCardsPendingSourceUserMeCountStatisticsFailureAction
    | GetCreditCardsPendingSourceUserMeCountStatisticsSuccessAction
  > => {
    dispatch(createGetCreditCardsPendingSourceUserMeCountStatisticsInitAction())

    return axios
      .post<GetCreditCardsPendingSourceUserMeCountStatisticsSuccessPayload>(
        `/statistics`,
        {
          statistics: [
            {
              agg: StatisticAggregateType.Count,
              type: StatisticModelType.CreditCard,
              query: {
                displayStatus: CreditCardDisplayStatus.Pending,
                type: CreditCardType.Source,
                user: 'me',
              },
            },
          ],
        },
        selectAuthedJsonApiRequestConfig(getState()),
      )
      .then(
        async (response) => {
          return dispatch(
            createGetCreditCardsPendingSourceUserMeCountStatisticsSuccessAction(
              { ...response.data },
            ),
          )
        },
        (err) => {
          return dispatch(
            createGetCreditCardsPendingSourceUserMeCountStatisticsFailureAction(
              err.response.data,
            ),
          )
        },
      )
  }
}

export const GET_VIRTUAL_CARDS_CARDHOLDER_PENDING_REQUEST_OR_APPROVER_RULE_APPROVAL_COUNT_STATISTICS_INIT =
  'data/statistics/GET_VIRTUAL_CARDS_CARDHOLDER_PENDING_REQUEST_OR_APPROVER_RULE_APPROVAL_COUNT_STATISTICS_INIT'
export const GET_VIRTUAL_CARDS_CARDHOLDER_PENDING_REQUEST_OR_APPROVER_RULE_APPROVAL_COUNT_STATISTICS_SUCCESS =
  'data/statistics/GET_VIRTUAL_CARDS_CARDHOLDER_PENDING_REQUEST_OR_APPROVER_RULE_APPROVAL_COUNT_STATISTICS_SUCCESS'
export const GET_VIRTUAL_CARDS_CARDHOLDER_PENDING_REQUEST_OR_APPROVER_RULE_APPROVAL_COUNT_STATISTICS_FAILURE =
  'data/statistics/GET_VIRTUAL_CARDS_CARDHOLDER_PENDING_REQUEST_OR_APPROVER_RULE_APPROVAL_COUNT_STATISTICS_FAILURE'

export interface GetVirtualCardsCardholderPendingRequestOrApproverRuleApprovalCountStatisticsSuccessPayload {
  statistics: Array<{ value: number }>
  statisticNames: string[]
}

export interface GetVirtualCardsCardholderPendingRequestOrApproverRuleApprovalMeCountStatisticsInitAction {
  type: typeof GET_VIRTUAL_CARDS_CARDHOLDER_PENDING_REQUEST_OR_APPROVER_RULE_APPROVAL_COUNT_STATISTICS_INIT
}
export interface GetVirtualCardsCardholderPendingRequestOrApproverRuleApprovalMeCountStatisticsSuccessAction {
  type: typeof GET_VIRTUAL_CARDS_CARDHOLDER_PENDING_REQUEST_OR_APPROVER_RULE_APPROVAL_COUNT_STATISTICS_SUCCESS
  payload: GetVirtualCardsCardholderPendingRequestOrApproverRuleApprovalCountStatisticsSuccessPayload
}
export interface GetVirtualCardsCardholderPendingRequestOrApproverRuleApprovalMeCountStatisticsFailureAction {
  type: typeof GET_VIRTUAL_CARDS_CARDHOLDER_PENDING_REQUEST_OR_APPROVER_RULE_APPROVAL_COUNT_STATISTICS_FAILURE
  payload: unknown
}

export type GetVirtualCardsCardholderPendingRequestOrApproverRuleApprovalMeCountStatisticsActions =

    | GetVirtualCardsCardholderPendingRequestOrApproverRuleApprovalMeCountStatisticsInitAction
    | GetVirtualCardsCardholderPendingRequestOrApproverRuleApprovalMeCountStatisticsSuccessAction
    | GetVirtualCardsCardholderPendingRequestOrApproverRuleApprovalMeCountStatisticsFailureAction

const createGetVirtualCardsCardholderPendingRequestOrApproverRuleApprovalMeCountStatisticsInitAction =
  (): GetVirtualCardsCardholderPendingRequestOrApproverRuleApprovalMeCountStatisticsInitAction => ({
    type: GET_VIRTUAL_CARDS_CARDHOLDER_PENDING_REQUEST_OR_APPROVER_RULE_APPROVAL_COUNT_STATISTICS_INIT,
  })
const createGetVirtualCardsCardholderPendingRequestOrApproverRuleApprovalMeCountStatisticsSuccessAction =
  (
    payload: GetVirtualCardsCardholderPendingRequestOrApproverRuleApprovalCountStatisticsSuccessPayload,
  ): GetVirtualCardsCardholderPendingRequestOrApproverRuleApprovalMeCountStatisticsSuccessAction => ({
    type: GET_VIRTUAL_CARDS_CARDHOLDER_PENDING_REQUEST_OR_APPROVER_RULE_APPROVAL_COUNT_STATISTICS_SUCCESS,
    payload,
  })
const createGetVirtualCardsCardholderPendingRequestOrApproverRuleApprovalMeCountStatisticsFailureAction =
  (
    response: unknown,
  ): GetVirtualCardsCardholderPendingRequestOrApproverRuleApprovalMeCountStatisticsFailureAction => ({
    type: GET_VIRTUAL_CARDS_CARDHOLDER_PENDING_REQUEST_OR_APPROVER_RULE_APPROVAL_COUNT_STATISTICS_FAILURE,
    payload: response,
  })

export const getVirtualCardsCardholderPendingRequestOrApproverRuleApprovalMeCountStatistics =
  (): ThunkAction<
    Promise<
      | GetVirtualCardsCardholderPendingRequestOrApproverRuleApprovalMeCountStatisticsFailureAction
      | GetVirtualCardsCardholderPendingRequestOrApproverRuleApprovalMeCountStatisticsSuccessAction
    >,
    RootState,
    void,
    GetVirtualCardsCardholderPendingRequestOrApproverRuleApprovalMeCountStatisticsActions
  > => {
    return async (
      dispatch: ThunkDispatch<
        RootState,
        void,
        GetVirtualCardsCardholderPendingRequestOrApproverRuleApprovalMeCountStatisticsActions
      >,
      getState: () => RootState,
    ): Promise<
      | GetVirtualCardsCardholderPendingRequestOrApproverRuleApprovalMeCountStatisticsFailureAction
      | GetVirtualCardsCardholderPendingRequestOrApproverRuleApprovalMeCountStatisticsSuccessAction
    > => {
      dispatch(
        createGetVirtualCardsCardholderPendingRequestOrApproverRuleApprovalMeCountStatisticsInitAction(),
      )

      return axios
        .post<GetVirtualCardsCardholderPendingRequestOrApproverRuleApprovalCountStatisticsSuccessPayload>(
          `/statistics`,
          {
            statistics: [
              {
                agg: StatisticAggregateType.Count,
                type: StatisticModelType.VirtualCard,
                query: {
                  cardholderPendingRequestOrApproverRuleApproval: 'me',
                  withPermission: VirtualCardPermissions.UpdateVirtualCard,
                },
              },
            ],
          },
          selectAuthedJsonApiRequestConfig(getState()),
        )
        .then(
          async (response) => {
            return dispatch(
              createGetVirtualCardsCardholderPendingRequestOrApproverRuleApprovalMeCountStatisticsSuccessAction(
                { ...response.data },
              ),
            )
          },
          (err) => {
            return dispatch(
              createGetVirtualCardsCardholderPendingRequestOrApproverRuleApprovalMeCountStatisticsFailureAction(
                err.response.data,
              ),
            )
          },
        )
    }
  }

export const GET_VIRTUAL_CARDS_RECIPIENT_ME_COUNT_STATISTICS_INIT =
  'data/statistics/GET_VIRTUAL_CARDS_RECIPIENT_ME_COUNT_STATISTICS_INIT'
export const GET_VIRTUAL_CARDS_RECIPIENT_ME_COUNT_STATISTICS_SUCCESS =
  'data/statistics/GET_VIRTUAL_CARDS_RECIPIENT_ME_COUNT_STATISTICS_SUCCESS'
export const GET_VIRTUAL_CARDS_RECIPIENT_ME_COUNT_STATISTICS_FAILURE =
  'data/statistics/GET_VIRTUAL_CARDS_RECIPIENT_ME_COUNT_STATISTICS_FAILURE'

export interface GetVirtualCardsRecipientMeCountStatisticsSuccessPayload {
  statistics: Array<{ value: number }>
  statisticNames: string[]
}

export interface GetVirtualCardsRecipientMeCountStatisticsInitAction {
  type: typeof GET_VIRTUAL_CARDS_RECIPIENT_ME_COUNT_STATISTICS_INIT
}
export interface GetVirtualCardsRecipientMeCountStatisticsSuccessAction {
  type: typeof GET_VIRTUAL_CARDS_RECIPIENT_ME_COUNT_STATISTICS_SUCCESS
  payload: GetVirtualCardsRecipientMeCountStatisticsSuccessPayload
}
export interface GetVirtualCardsRecipientMeCountStatisticsFailureAction {
  type: typeof GET_VIRTUAL_CARDS_RECIPIENT_ME_COUNT_STATISTICS_FAILURE
  payload: unknown
}

export type GetVirtualCardsRecipientMeCountStatisticsActions =
  | GetVirtualCardsRecipientMeCountStatisticsInitAction
  | GetVirtualCardsRecipientMeCountStatisticsSuccessAction
  | GetVirtualCardsRecipientMeCountStatisticsFailureAction

const createGetVirtualCardsRecipientMeCountStatisticsInitAction =
  (): GetVirtualCardsRecipientMeCountStatisticsInitAction => ({
    type: GET_VIRTUAL_CARDS_RECIPIENT_ME_COUNT_STATISTICS_INIT,
  })
const createGetVirtualCardsRecipientMeCountStatisticsSuccessAction = (
  payload: GetVirtualCardsRecipientMeCountStatisticsSuccessPayload,
): GetVirtualCardsRecipientMeCountStatisticsSuccessAction => ({
  type: GET_VIRTUAL_CARDS_RECIPIENT_ME_COUNT_STATISTICS_SUCCESS,
  payload,
})
const createGetVirtualCardsRecipientMeCountStatisticsFailureAction = (
  response: unknown,
): GetVirtualCardsRecipientMeCountStatisticsFailureAction => ({
  type: GET_VIRTUAL_CARDS_RECIPIENT_ME_COUNT_STATISTICS_FAILURE,
  payload: response,
})

export const getVirtualCardsRecipientMeCountStatistics = (): ThunkAction<
  Promise<
    | GetVirtualCardsRecipientMeCountStatisticsFailureAction
    | GetVirtualCardsRecipientMeCountStatisticsSuccessAction
  >,
  RootState,
  void,
  GetVirtualCardsRecipientMeCountStatisticsActions
> => {
  return async (
    dispatch: ThunkDispatch<
      RootState,
      void,
      GetVirtualCardsRecipientMeCountStatisticsActions
    >,
    getState: () => RootState,
  ): Promise<
    | GetVirtualCardsRecipientMeCountStatisticsFailureAction
    | GetVirtualCardsRecipientMeCountStatisticsSuccessAction
  > => {
    dispatch(createGetVirtualCardsRecipientMeCountStatisticsInitAction())

    return axios
      .post<GetVirtualCardsRecipientMeCountStatisticsSuccessPayload>(
        `/statistics`,
        {
          statistics: [
            {
              agg: StatisticAggregateType.Count,
              type: StatisticModelType.VirtualCard,
              query: {
                recipient: 'me',
              },
            },
          ],
        },
        selectAuthedJsonApiRequestConfig(getState()),
      )
      .then(
        async (response) => {
          return dispatch(
            createGetVirtualCardsRecipientMeCountStatisticsSuccessAction({
              ...response.data,
            }),
          )
        },
        (err) => {
          return dispatch(
            createGetVirtualCardsRecipientMeCountStatisticsFailureAction(
              err.response.data,
            ),
          )
        },
      )
  }
}

export const GET_GUEST_VIRTUAL_CARDS_RECIPIENT_ME_COUNT_STATISTICS_INIT =
  'data/statistics/GET_GUEST_VIRTUAL_CARDS_RECIPIENT_ME_COUNT_STATISTICS_INIT'
export const GET_GUEST_VIRTUAL_CARDS_RECIPIENT_ME_COUNT_STATISTICS_SUCCESS =
  'data/statistics/GET_GUEST_VIRTUAL_CARDS_RECIPIENT_ME_COUNT_STATISTICS_SUCCESS'
export const GET_GUEST_VIRTUAL_CARDS_RECIPIENT_ME_COUNT_STATISTICS_FAILURE =
  'data/statistics/GET_GUEST_VIRTUAL_CARDS_RECIPIENT_ME_COUNT_STATISTICS_FAILURE'

export interface GetGuestVirtualCardsRecipientMeCountStatisticsSuccessPayload {
  statistics: Array<{ value: number }>
  statisticNames: string[]
}

export interface GetGuestVirtualCardsRecipientMeCountStatisticsInitAction {
  type: typeof GET_GUEST_VIRTUAL_CARDS_RECIPIENT_ME_COUNT_STATISTICS_INIT
}
export interface GetGuestVirtualCardsRecipientMeCountStatisticsSuccessAction {
  type: typeof GET_GUEST_VIRTUAL_CARDS_RECIPIENT_ME_COUNT_STATISTICS_SUCCESS
  payload: GetVirtualCardsRecipientMeCountStatisticsSuccessPayload
}
export interface GetGuestVirtualCardsRecipientMeCountStatisticsFailureAction {
  type: typeof GET_GUEST_VIRTUAL_CARDS_RECIPIENT_ME_COUNT_STATISTICS_FAILURE
  payload: unknown
}

export type GetGuestVirtualCardsRecipientMeCountStatisticsActions =
  | GetGuestVirtualCardsRecipientMeCountStatisticsInitAction
  | GetGuestVirtualCardsRecipientMeCountStatisticsSuccessAction
  | GetGuestVirtualCardsRecipientMeCountStatisticsFailureAction

const createGetGuestVirtualCardsRecipientMeCountStatisticsInitAction =
  (): GetGuestVirtualCardsRecipientMeCountStatisticsInitAction => ({
    type: GET_GUEST_VIRTUAL_CARDS_RECIPIENT_ME_COUNT_STATISTICS_INIT,
  })
const createGetGuestVirtualCardsRecipientMeCountStatisticsSuccessAction = (
  payload: GetVirtualCardsRecipientMeCountStatisticsSuccessPayload,
): GetGuestVirtualCardsRecipientMeCountStatisticsSuccessAction => ({
  type: GET_GUEST_VIRTUAL_CARDS_RECIPIENT_ME_COUNT_STATISTICS_SUCCESS,
  payload,
})
const createGetGuestVirtualCardsRecipientMeCountStatisticsFailureAction = (
  response: unknown,
): GetGuestVirtualCardsRecipientMeCountStatisticsFailureAction => ({
  type: GET_GUEST_VIRTUAL_CARDS_RECIPIENT_ME_COUNT_STATISTICS_FAILURE,
  payload: response,
})

export const getGuestVirtualCardsRecipientMeCountStatistics = (): ThunkAction<
  Promise<
    | GetGuestVirtualCardsRecipientMeCountStatisticsFailureAction
    | GetGuestVirtualCardsRecipientMeCountStatisticsSuccessAction
  >,
  RootState,
  void,
  GetGuestVirtualCardsRecipientMeCountStatisticsActions
> => {
  return async (
    dispatch: ThunkDispatch<
      RootState,
      void,
      GetGuestVirtualCardsRecipientMeCountStatisticsActions
    >,
    getState: () => RootState,
  ): Promise<
    | GetGuestVirtualCardsRecipientMeCountStatisticsFailureAction
    | GetGuestVirtualCardsRecipientMeCountStatisticsSuccessAction
  > => {
    dispatch(createGetGuestVirtualCardsRecipientMeCountStatisticsInitAction())

    return axios
      .post<GetGuestVirtualCardsRecipientMeCountStatisticsSuccessPayload>(
        `/statistics`,
        {
          statistics: [
            {
              agg: StatisticAggregateType.Count,
              type: StatisticModelType.VirtualCard,
              query: {
                recipient: 'me',
                isDirect: true,
              },
            },
          ],
        },
        selectAuthedJsonApiRequestConfig(getState()),
      )
      .then(
        async (response) => {
          return dispatch(
            createGetGuestVirtualCardsRecipientMeCountStatisticsSuccessAction({
              ...response.data,
            }),
          )
        },
        (err) => {
          return dispatch(
            createGetGuestVirtualCardsRecipientMeCountStatisticsFailureAction(
              err.response.data,
            ),
          )
        },
      )
  }
}

export const GET_VIRTUAL_CARDS_INACTIVE_RECIPIENT_ME_ISSUED_COUNT_STATISTICS_INIT =
  'data/statistics/GET_VIRTUAL_CARDS_INACTIVE_RECIPIENT_ME_ISSUED_COUNT_STATISTICS_INIT'
export const GET_VIRTUAL_CARDS_INACTIVE_RECIPIENT_ME_ISSUED_COUNT_STATISTICS_SUCCESS =
  'data/statistics/GET_VIRTUAL_CARDS_INACTIVE_RECIPIENT_ME_ISSUED_COUNT_STATISTICS_SUCCESS'
export const GET_VIRTUAL_CARDS_INACTIVE_RECIPIENT_ME_ISSUED_COUNT_STATISTICS_FAILURE =
  'data/statistics/GET_VIRTUAL_CARDS_INACTIVE_RECIPIENT_ME_ISSUED_COUNT_STATISTICS_FAILURE'

export interface GetVirtualCardsInactiveRecipientMeIssuedCountStatisticsSuccessPayload {
  statistics: Array<{ value: number }>
  statisticNames: string[]
}

export interface GetVirtualCardsInactiveRecipientMeIssuedCountStatisticsInitAction {
  type: typeof GET_VIRTUAL_CARDS_INACTIVE_RECIPIENT_ME_ISSUED_COUNT_STATISTICS_INIT
}
export interface GetVirtualCardsInactiveRecipientMeIssuedCountStatisticsSuccessAction {
  type: typeof GET_VIRTUAL_CARDS_INACTIVE_RECIPIENT_ME_ISSUED_COUNT_STATISTICS_SUCCESS
  payload: GetVirtualCardsInactiveRecipientMeIssuedCountStatisticsSuccessPayload
}
export interface GetVirtualCardsInactiveRecipientMeIssuedCountStatisticsFailureAction {
  type: typeof GET_VIRTUAL_CARDS_INACTIVE_RECIPIENT_ME_ISSUED_COUNT_STATISTICS_FAILURE
  payload: unknown
}

export type GetVirtualCardsInactiveRecipientMeIssuedCountStatisticsActions =
  | GetVirtualCardsInactiveRecipientMeIssuedCountStatisticsInitAction
  | GetVirtualCardsInactiveRecipientMeIssuedCountStatisticsSuccessAction
  | GetVirtualCardsInactiveRecipientMeIssuedCountStatisticsFailureAction

const createGetVirtualCardsInactiveRecipientMeIssuedCountStatisticsInitAction =
  (): GetVirtualCardsInactiveRecipientMeIssuedCountStatisticsInitAction => ({
    type: GET_VIRTUAL_CARDS_INACTIVE_RECIPIENT_ME_ISSUED_COUNT_STATISTICS_INIT,
  })
const createGetVirtualCardsInactiveRecipientMeIssuedCountStatisticsSuccessAction =
  (
    payload: GetVirtualCardsInactiveRecipientMeIssuedCountStatisticsSuccessPayload,
  ): GetVirtualCardsInactiveRecipientMeIssuedCountStatisticsSuccessAction => ({
    type: GET_VIRTUAL_CARDS_INACTIVE_RECIPIENT_ME_ISSUED_COUNT_STATISTICS_SUCCESS,
    payload,
  })
const createGetVirtualCardsInactiveRecipientMeIssuedCountStatisticsFailureAction =
  (
    response: unknown,
  ): GetVirtualCardsInactiveRecipientMeIssuedCountStatisticsFailureAction => ({
    type: GET_VIRTUAL_CARDS_INACTIVE_RECIPIENT_ME_ISSUED_COUNT_STATISTICS_FAILURE,
    payload: response,
  })

export const getVirtualCardsInactiveRecipientMeIssuedCountStatistics =
  (): ThunkAction<
    Promise<
      | GetVirtualCardsInactiveRecipientMeIssuedCountStatisticsFailureAction
      | GetVirtualCardsInactiveRecipientMeIssuedCountStatisticsSuccessAction
    >,
    RootState,
    void,
    GetVirtualCardsInactiveRecipientMeIssuedCountStatisticsActions
  > => {
    return async (
      dispatch: ThunkDispatch<
        RootState,
        void,
        GetVirtualCardsInactiveRecipientMeIssuedCountStatisticsActions
      >,
      getState: () => RootState,
    ): Promise<
      | GetVirtualCardsInactiveRecipientMeIssuedCountStatisticsFailureAction
      | GetVirtualCardsInactiveRecipientMeIssuedCountStatisticsSuccessAction
    > => {
      dispatch(
        createGetVirtualCardsInactiveRecipientMeIssuedCountStatisticsInitAction(),
      )

      return axios
        .post<GetVirtualCardsInactiveRecipientMeIssuedCountStatisticsSuccessPayload>(
          `/statistics`,
          {
            statistics: [
              {
                agg: StatisticAggregateType.Count,
                type: StatisticModelType.VirtualCard,
                query: {
                  statuses: [
                    VirtualCardStatus.Cancelled,
                    VirtualCardStatus.Consumed,
                    VirtualCardStatus.Closed,
                    VirtualCardStatus.Expired,
                  ],
                  recipient: 'me',
                  issued: true,
                },
              },
            ],
          },
          selectAuthedJsonApiRequestConfig(getState()),
        )
        .then(
          async (response) => {
            return dispatch(
              createGetVirtualCardsInactiveRecipientMeIssuedCountStatisticsSuccessAction(
                { ...response.data },
              ),
            )
          },
          (err) => {
            return dispatch(
              createGetVirtualCardsInactiveRecipientMeIssuedCountStatisticsFailureAction(
                err.response.data,
              ),
            )
          },
        )
    }
  }

export const GET_VIRTUAL_CARDS_ACTIVE_OR_PENDING_RECIPIENT_ME_COUNT_STATISTICS_INIT =
  'data/statistics/GET_VIRTUAL_CARDS_ACTIVE_OR_PENDING_RECIPIENT_ME_COUNT_STATISTICS_INIT'
export const GET_VIRTUAL_CARDS_ACTIVE_OR_PENDING_RECIPIENT_ME_COUNT_STATISTICS_SUCCESS =
  'data/statistics/GET_VIRTUAL_CARDS_ACTIVE_OR_PENDING_RECIPIENT_ME_COUNT_STATISTICS_SUCCESS'
export const GET_VIRTUAL_CARDS_ACTIVE_OR_PENDING_RECIPIENT_ME_COUNT_STATISTICS_FAILURE =
  'data/statistics/GET_VIRTUAL_CARDS_ACTIVE_OR_PENDING_RECIPIENT_ME_COUNT_STATISTICS_FAILURE'

export interface GetVirtualCardsActiveOrPendingRecipientMeCountStatisticsSuccessPayload {
  statistics: Array<{ value: number }>
  statisticNames: string[]
}

export interface GetVirtualCardsActiveOrPendingRecipientMeCountStatisticsInitAction {
  type: typeof GET_VIRTUAL_CARDS_ACTIVE_OR_PENDING_RECIPIENT_ME_COUNT_STATISTICS_INIT
}
export interface GetVirtualCardsActiveOrPendingRecipientMeCountStatisticsSuccessAction {
  type: typeof GET_VIRTUAL_CARDS_ACTIVE_OR_PENDING_RECIPIENT_ME_COUNT_STATISTICS_SUCCESS
  payload: GetVirtualCardsActiveOrPendingRecipientMeCountStatisticsSuccessPayload
}
export interface GetVirtualCardsActiveOrPendingRecipientMeCountStatisticsFailureAction {
  type: typeof GET_VIRTUAL_CARDS_ACTIVE_OR_PENDING_RECIPIENT_ME_COUNT_STATISTICS_FAILURE
  payload: unknown
}

export type GetVirtualCardsActiveOrPendingRecipientMeCountStatisticsActions =
  | GetVirtualCardsActiveOrPendingRecipientMeCountStatisticsInitAction
  | GetVirtualCardsActiveOrPendingRecipientMeCountStatisticsSuccessAction
  | GetVirtualCardsActiveOrPendingRecipientMeCountStatisticsFailureAction

const createGetVirtualCardsActiveOrPendingRecipientMeCountStatisticsInitAction =
  (): GetVirtualCardsActiveOrPendingRecipientMeCountStatisticsInitAction => ({
    type: GET_VIRTUAL_CARDS_ACTIVE_OR_PENDING_RECIPIENT_ME_COUNT_STATISTICS_INIT,
  })
const createGetVirtualCardsActiveOrPendingRecipientMeCountStatisticsSuccessAction =
  (
    payload: GetVirtualCardsActiveOrPendingRecipientMeCountStatisticsSuccessPayload,
  ): GetVirtualCardsActiveOrPendingRecipientMeCountStatisticsSuccessAction => ({
    type: GET_VIRTUAL_CARDS_ACTIVE_OR_PENDING_RECIPIENT_ME_COUNT_STATISTICS_SUCCESS,
    payload,
  })
const createGetVirtualCardsActiveOrPendingRecipientMeCountStatisticsFailureAction =
  (
    response: unknown,
  ): GetVirtualCardsActiveOrPendingRecipientMeCountStatisticsFailureAction => ({
    type: GET_VIRTUAL_CARDS_ACTIVE_OR_PENDING_RECIPIENT_ME_COUNT_STATISTICS_FAILURE,
    payload: response,
  })

export const getVirtualCardsActiveOrPendingRecipientMeCountStatistics =
  (): ThunkAction<
    Promise<
      | GetVirtualCardsActiveOrPendingRecipientMeCountStatisticsFailureAction
      | GetVirtualCardsActiveOrPendingRecipientMeCountStatisticsSuccessAction
    >,
    RootState,
    void,
    GetVirtualCardsActiveOrPendingRecipientMeCountStatisticsActions
  > => {
    return async (
      dispatch: ThunkDispatch<
        RootState,
        void,
        GetVirtualCardsActiveOrPendingRecipientMeCountStatisticsActions
      >,
      getState: () => RootState,
    ): Promise<
      | GetVirtualCardsActiveOrPendingRecipientMeCountStatisticsFailureAction
      | GetVirtualCardsActiveOrPendingRecipientMeCountStatisticsSuccessAction
    > => {
      dispatch(
        createGetVirtualCardsActiveOrPendingRecipientMeCountStatisticsInitAction(),
      )

      return axios
        .post<GetVirtualCardsActiveOrPendingRecipientMeCountStatisticsSuccessPayload>(
          `/statistics`,
          {
            statistics: [
              {
                agg: StatisticAggregateType.Count,
                type: StatisticModelType.VirtualCard,
                query: {
                  statuses: [
                    VirtualCardStatus.Active,
                    VirtualCardStatus.Pending,
                  ],
                  recipient: 'me',
                  withPermission: VirtualCardPermissions.RequestUpdate,
                },
              },
            ],
          },
          selectAuthedJsonApiRequestConfig(getState()),
        )
        .then(
          async (response) => {
            return dispatch(
              createGetVirtualCardsActiveOrPendingRecipientMeCountStatisticsSuccessAction(
                { ...response.data },
              ),
            )
          },
          (err) => {
            return dispatch(
              createGetVirtualCardsActiveOrPendingRecipientMeCountStatisticsFailureAction(
                err.response.data,
              ),
            )
          },
        )
    }
  }

export const GET_VIRTUAL_CARDS_ACTIVE_RECIPIENT_ME_COUNT_STATISTICS_INIT =
  'data/statistics/GET_VIRTUAL_CARDS_ACTIVE_RECIPIENT_ME_COUNT_STATISTICS_INIT'
export const GET_VIRTUAL_CARDS_ACTIVE_RECIPIENT_ME_COUNT_STATISTICS_SUCCESS =
  'data/statistics/GET_VIRTUAL_CARDS_ACTIVE_RECIPIENT_ME_COUNT_STATISTICS_SUCCESS'
export const GET_VIRTUAL_CARDS_ACTIVE_RECIPIENT_ME_COUNT_STATISTICS_FAILURE =
  'data/statistics/GET_VIRTUAL_CARDS_ACTIVE_RECIPIENT_ME_COUNT_STATISTICS_FAILURE'

export interface GetVirtualCardsActiveRecipientMeCountStatisticsSuccessPayload {
  statistics: Array<{ value: number }>
  statisticNames: string[]
}

export interface GetVirtualCardsActiveRecipientMeCountStatisticsInitAction {
  type: typeof GET_VIRTUAL_CARDS_ACTIVE_RECIPIENT_ME_COUNT_STATISTICS_INIT
}
export interface GetVirtualCardsActiveRecipientMeCountStatisticsSuccessAction {
  type: typeof GET_VIRTUAL_CARDS_ACTIVE_RECIPIENT_ME_COUNT_STATISTICS_SUCCESS
  payload: GetVirtualCardsActiveRecipientMeCountStatisticsSuccessPayload
}
export interface GetVirtualCardsActiveRecipientMeCountStatisticsFailureAction {
  type: typeof GET_VIRTUAL_CARDS_ACTIVE_RECIPIENT_ME_COUNT_STATISTICS_FAILURE
  payload: unknown
}

export type GetVirtualCardsActiveRecipientMeCountStatisticsActions =
  | GetVirtualCardsActiveRecipientMeCountStatisticsInitAction
  | GetVirtualCardsActiveRecipientMeCountStatisticsSuccessAction
  | GetVirtualCardsActiveRecipientMeCountStatisticsFailureAction

const createGetVirtualCardsActiveRecipientMeCountStatisticsInitAction =
  (): GetVirtualCardsActiveRecipientMeCountStatisticsInitAction => ({
    type: GET_VIRTUAL_CARDS_ACTIVE_RECIPIENT_ME_COUNT_STATISTICS_INIT,
  })
const createGetVirtualCardsActiveRecipientMeCountStatisticsSuccessAction = (
  payload: GetVirtualCardsActiveRecipientMeCountStatisticsSuccessPayload,
): GetVirtualCardsActiveRecipientMeCountStatisticsSuccessAction => ({
  type: GET_VIRTUAL_CARDS_ACTIVE_RECIPIENT_ME_COUNT_STATISTICS_SUCCESS,
  payload,
})
const createGetVirtualCardsActiveRecipientMeCountStatisticsFailureAction = (
  response: unknown,
): GetVirtualCardsActiveRecipientMeCountStatisticsFailureAction => ({
  type: GET_VIRTUAL_CARDS_ACTIVE_RECIPIENT_ME_COUNT_STATISTICS_FAILURE,
  payload: response,
})

export const getVirtualCardsActiveRecipientMeCountStatistics = (): ThunkAction<
  Promise<
    | GetVirtualCardsActiveRecipientMeCountStatisticsFailureAction
    | GetVirtualCardsActiveRecipientMeCountStatisticsSuccessAction
  >,
  RootState,
  void,
  GetVirtualCardsActiveRecipientMeCountStatisticsActions
> => {
  return async (
    dispatch: ThunkDispatch<
      RootState,
      void,
      GetVirtualCardsActiveRecipientMeCountStatisticsActions
    >,
    getState: () => RootState,
  ): Promise<
    | GetVirtualCardsActiveRecipientMeCountStatisticsFailureAction
    | GetVirtualCardsActiveRecipientMeCountStatisticsSuccessAction
  > => {
    dispatch(createGetVirtualCardsActiveRecipientMeCountStatisticsInitAction())

    return axios
      .post<GetVirtualCardsActiveRecipientMeCountStatisticsSuccessPayload>(
        `/statistics`,
        {
          statistics: [
            {
              agg: StatisticAggregateType.Count,
              type: StatisticModelType.VirtualCard,
              query: {
                statuses: [VirtualCardStatus.Active],
                recipient: 'me',
                withpermission: VirtualCardPermissions.RequestUpdate,
              },
            },
          ],
        },
        selectAuthedJsonApiRequestConfig(getState()),
      )
      .then(
        async (response) => {
          return dispatch(
            createGetVirtualCardsActiveRecipientMeCountStatisticsSuccessAction({
              ...response.data,
            }),
          )
        },
        (err) => {
          return dispatch(
            createGetVirtualCardsActiveRecipientMeCountStatisticsFailureAction(
              err.response.data,
            ),
          )
        },
      )
  }
}

export const GET_CREDIT_CARDS_ACTIVE_DELEGATE_COUNT_STATISTICS_INIT =
  'data/statistics/GET_CREDIT_CARDS_ACTIVE_DELEGATE_COUNT_STATISTICS_INIT'
export const GET_CREDIT_CARDS_ACTIVE_DELEGATE_COUNT_STATISTICS_SUCCESS =
  'data/statistics/GET_CREDIT_CARDS_ACTIVE_DELEGATE_COUNT_STATISTICS_SUCCESS'
export const GET_CREDIT_CARDS_ACTIVE_DELEGATE_COUNT_STATISTICS_FAILURE =
  'data/statistics/GET_CREDIT_CARDS_ACTIVE_DELEGATE_COUNT_STATISTICS_FAILURE'

export interface GetCreditCardsActiveDelegateCountStatisticsSuccessPayload {
  statistics: Array<{ value: number }>
  statisticNames: string[]
}

export interface GetCreditCardsActiveDelegateCountStatisticsInitAction {
  type: typeof GET_CREDIT_CARDS_ACTIVE_DELEGATE_COUNT_STATISTICS_INIT
}
export interface GetCreditCardsActiveDelegateCountStatisticsSuccessAction {
  type: typeof GET_CREDIT_CARDS_ACTIVE_DELEGATE_COUNT_STATISTICS_SUCCESS
  payload: GetCreditCardsActiveDelegateCountStatisticsSuccessPayload
}
export interface GetCreditCardsActiveDelegateCountStatisticsFailureAction {
  type: typeof GET_CREDIT_CARDS_ACTIVE_DELEGATE_COUNT_STATISTICS_FAILURE
  payload: unknown
}

export type GetCreditCardsActiveDelegateCountStatisticsActions =
  | GetCreditCardsActiveDelegateCountStatisticsInitAction
  | GetCreditCardsActiveDelegateCountStatisticsSuccessAction
  | GetCreditCardsActiveDelegateCountStatisticsFailureAction

const createGetCreditCardsActiveDelegateCountStatisticsInitAction =
  (): GetCreditCardsActiveDelegateCountStatisticsInitAction => ({
    type: GET_CREDIT_CARDS_ACTIVE_DELEGATE_COUNT_STATISTICS_INIT,
  })
const createGetCreditCardsActiveDelegateCountStatisticsSuccessAction = (
  payload: GetCreditCardsActiveDelegateCountStatisticsSuccessPayload,
): GetCreditCardsActiveDelegateCountStatisticsSuccessAction => ({
  type: GET_CREDIT_CARDS_ACTIVE_DELEGATE_COUNT_STATISTICS_SUCCESS,
  payload,
})
const createGetCreditCardsActiveDelegateCountStatisticsFailureAction = (
  response: unknown,
): GetCreditCardsActiveDelegateCountStatisticsFailureAction => ({
  type: GET_CREDIT_CARDS_ACTIVE_DELEGATE_COUNT_STATISTICS_FAILURE,
  payload: response,
})

export const getCreditCardsActiveDelegateCountStatistics = (
  cacheTimestamp?: string,
): ThunkAction<
  Promise<
    | GetCreditCardsActiveDelegateCountStatisticsFailureAction
    | GetCreditCardsActiveDelegateCountStatisticsSuccessAction
  >,
  RootState,
  void,
  GetCreditCardsActiveDelegateCountStatisticsActions
> => {
  return async (
    dispatch: ThunkDispatch<
      RootState,
      void,
      GetCreditCardsActiveDelegateCountStatisticsActions
    >,
    getState: () => RootState,
  ): Promise<
    | GetCreditCardsActiveDelegateCountStatisticsFailureAction
    | GetCreditCardsActiveDelegateCountStatisticsSuccessAction
  > => {
    dispatch(createGetCreditCardsActiveDelegateCountStatisticsInitAction())

    return axios
      .post<GetCreditCardsActiveDelegateCountStatisticsSuccessPayload>(
        `/statistics`,
        {
          statistics: [
            {
              agg: StatisticAggregateType.Count,
              type: StatisticModelType.CreditCard,
              query: {
                status: CreditCardStatus.Active,
                type: CreditCardType.Delegate,
                cacheTimestamp: cacheTimestamp,
              },
            },
          ],
        },
        selectAuthedJsonApiRequestConfig(getState()),
      )
      .then(
        async (response) => {
          return dispatch(
            createGetCreditCardsActiveDelegateCountStatisticsSuccessAction({
              ...response.data,
            }),
          )
        },
        (err) => {
          return dispatch(
            createGetCreditCardsActiveDelegateCountStatisticsFailureAction(
              err.response.data,
            ),
          )
        },
      )
  }
}

export const GET_CREDIT_CARDS_ACTIVE_SOURCE_COUNT_STATISTICS_INIT =
  'data/statistics/GET_CREDIT_CARDS_ACTIVE_SOURCE_COUNT_STATISTICS_INIT'
export const GET_CREDIT_CARDS_ACTIVE_SOURCE_COUNT_STATISTICS_SUCCESS =
  'data/statistics/GET_CREDIT_CARDS_ACTIVE_SOURCE_COUNT_STATISTICS_SUCCESS'
export const GET_CREDIT_CARDS_ACTIVE_SOURCE_COUNT_STATISTICS_FAILURE =
  'data/statistics/GET_CREDIT_CARDS_ACTIVE_SOURCE_COUNT_STATISTICS_FAILURE'

export interface GetCreditCardsActiveSourceCountStatisticsSuccessPayload {
  statistics: Array<{ value: number }>
  statisticNames: string[]
}

export interface GetCreditCardsActiveSourceCountStatisticsInitAction {
  type: typeof GET_CREDIT_CARDS_ACTIVE_SOURCE_COUNT_STATISTICS_INIT
}
export interface GetCreditCardsActiveSourceCountStatisticsSuccessAction {
  type: typeof GET_CREDIT_CARDS_ACTIVE_SOURCE_COUNT_STATISTICS_SUCCESS
  payload: GetCreditCardsActiveSourceCountStatisticsSuccessPayload
}
export interface GetCreditCardsActiveSourceCountStatisticsFailureAction {
  type: typeof GET_CREDIT_CARDS_ACTIVE_SOURCE_COUNT_STATISTICS_FAILURE
  payload: unknown
}

export type GetCreditCardsActiveSourceCountStatisticsActions =
  | GetCreditCardsActiveSourceCountStatisticsInitAction
  | GetCreditCardsActiveSourceCountStatisticsSuccessAction
  | GetCreditCardsActiveSourceCountStatisticsFailureAction

const createGetCreditCardsActiveSourceCountStatisticsInitAction =
  (): GetCreditCardsActiveSourceCountStatisticsInitAction => ({
    type: GET_CREDIT_CARDS_ACTIVE_SOURCE_COUNT_STATISTICS_INIT,
  })
const createGetCreditCardsActiveSourceCountStatisticsSuccessAction = (
  payload: GetCreditCardsActiveSourceCountStatisticsSuccessPayload,
): GetCreditCardsActiveSourceCountStatisticsSuccessAction => ({
  type: GET_CREDIT_CARDS_ACTIVE_SOURCE_COUNT_STATISTICS_SUCCESS,
  payload,
})
const createGetCreditCardsActiveSourceCountStatisticsFailureAction = (
  response: unknown,
): GetCreditCardsActiveSourceCountStatisticsFailureAction => ({
  type: GET_CREDIT_CARDS_ACTIVE_SOURCE_COUNT_STATISTICS_FAILURE,
  payload: response,
})

export const getCreditCardsActiveSourceCountStatistics = (
  cacheTimestamp?: string,
): ThunkAction<
  Promise<
    | GetCreditCardsActiveSourceCountStatisticsFailureAction
    | GetCreditCardsActiveSourceCountStatisticsSuccessAction
  >,
  RootState,
  void,
  GetCreditCardsActiveSourceCountStatisticsActions
> => {
  return async (
    dispatch: ThunkDispatch<
      RootState,
      void,
      GetCreditCardsActiveSourceCountStatisticsActions
    >,
    getState: () => RootState,
  ): Promise<
    | GetCreditCardsActiveSourceCountStatisticsFailureAction
    | GetCreditCardsActiveSourceCountStatisticsSuccessAction
  > => {
    dispatch(createGetCreditCardsActiveSourceCountStatisticsInitAction())

    return axios
      .post<GetCreditCardsActiveSourceCountStatisticsSuccessPayload>(
        `/statistics`,
        {
          statistics: [
            {
              agg: StatisticAggregateType.Count,
              type: StatisticModelType.CreditCard,
              query: {
                displayStatus: CreditCardDisplayStatus.Active,
                type: CreditCardType.Source,
                cacheTimestamp: cacheTimestamp,
              },
            },
          ],
        },
        selectAuthedJsonApiRequestConfig(getState()),
      )
      .then(
        async (response) => {
          return dispatch(
            createGetCreditCardsActiveSourceCountStatisticsSuccessAction({
              ...response.data,
            }),
          )
        },
        (err) => {
          return dispatch(
            createGetCreditCardsActiveSourceCountStatisticsFailureAction(
              err.response.data,
            ),
          )
        },
      )
  }
}

export const GET_CREDIT_CARDS_TIMED_OUT_SOURCE_COUNT_STATISTICS_INIT =
  'data/statistics/GET_CREDIT_CARDS_TIMED_OUT_SOURCE_COUNT_STATISTICS_INIT'
export const GET_CREDIT_CARDS_TIMED_OUT_SOURCE_COUNT_STATISTICS_SUCCESS =
  'data/statistics/GET_CREDIT_CARDS_TIMED_OUT_SOURCE_COUNT_STATISTICS_SUCCESS'
export const GET_CREDIT_CARDS_TIMED_OUT_SOURCE_COUNT_STATISTICS_FAILURE =
  'data/statistics/GET_CREDIT_CARDS_TIMED_OUT_SOURCE_COUNT_STATISTICS_FAILURE'

export interface GetCreditCardsTimedOutSourceCountStatisticsSuccessPayload {
  statistics: Array<{ value: number }>
  statisticNames: string[]
}

export interface GetCreditCardsTimedOutSourceCountStatisticsInitAction {
  type: typeof GET_CREDIT_CARDS_TIMED_OUT_SOURCE_COUNT_STATISTICS_INIT
}
export interface GetCreditCardsTimedOutSourceCountStatisticsSuccessAction {
  type: typeof GET_CREDIT_CARDS_TIMED_OUT_SOURCE_COUNT_STATISTICS_SUCCESS
  payload: GetCreditCardsTimedOutSourceCountStatisticsSuccessPayload
}
export interface GetCreditCardsTimedOutSourceCountStatisticsFailureAction {
  type: typeof GET_CREDIT_CARDS_TIMED_OUT_SOURCE_COUNT_STATISTICS_FAILURE
  payload: unknown
}

export type GetCreditCardsTimedOutSourceCountStatisticsActions =
  | GetCreditCardsTimedOutSourceCountStatisticsInitAction
  | GetCreditCardsTimedOutSourceCountStatisticsSuccessAction
  | GetCreditCardsTimedOutSourceCountStatisticsFailureAction

const createGetCreditCardsTimedOutSourceCountStatisticsInitAction =
  (): GetCreditCardsTimedOutSourceCountStatisticsInitAction => ({
    type: GET_CREDIT_CARDS_TIMED_OUT_SOURCE_COUNT_STATISTICS_INIT,
  })
const createGetCreditCardsTimedOutSourceCountStatisticsSuccessAction = (
  payload: GetCreditCardsTimedOutSourceCountStatisticsSuccessPayload,
): GetCreditCardsTimedOutSourceCountStatisticsSuccessAction => ({
  type: GET_CREDIT_CARDS_TIMED_OUT_SOURCE_COUNT_STATISTICS_SUCCESS,
  payload,
})
const createGetCreditCardsTimedOutSourceCountStatisticsFailureAction = (
  response: unknown,
): GetCreditCardsTimedOutSourceCountStatisticsFailureAction => ({
  type: GET_CREDIT_CARDS_TIMED_OUT_SOURCE_COUNT_STATISTICS_FAILURE,
  payload: response,
})

export const getCreditCardsTimedOutSourceCountStatistics = (): ThunkAction<
  Promise<
    | GetCreditCardsTimedOutSourceCountStatisticsFailureAction
    | GetCreditCardsTimedOutSourceCountStatisticsSuccessAction
  >,
  RootState,
  void,
  GetCreditCardsTimedOutSourceCountStatisticsActions
> => {
  return async (
    dispatch: ThunkDispatch<
      RootState,
      void,
      GetCreditCardsTimedOutSourceCountStatisticsActions
    >,
    getState: () => RootState,
  ): Promise<
    | GetCreditCardsTimedOutSourceCountStatisticsFailureAction
    | GetCreditCardsTimedOutSourceCountStatisticsSuccessAction
  > => {
    dispatch(createGetCreditCardsTimedOutSourceCountStatisticsInitAction())

    return axios
      .post<GetCreditCardsTimedOutSourceCountStatisticsSuccessPayload>(
        `/statistics`,
        {
          statistics: [
            {
              agg: StatisticAggregateType.Count,
              type: StatisticModelType.CreditCard,
              query: {
                status: CreditCardStatus.Inactive,
                type: CreditCardType.Source,
              },
            },
          ],
        },
        selectAuthedJsonApiRequestConfig(getState()),
      )
      .then(
        async (response) => {
          return dispatch(
            createGetCreditCardsTimedOutSourceCountStatisticsSuccessAction({
              ...response.data,
            }),
          )
        },
        (err) => {
          return dispatch(
            createGetCreditCardsTimedOutSourceCountStatisticsFailureAction(
              err.response.data,
            ),
          )
        },
      )
  }
}

export const GET_CREDIT_CARDS_PENDING_SOURCE_NEED_CARDHOLDER_VERIFICATION_USER_OR_VIEWER_ME_COUNT_STATISTICS_INIT =
  'data/statistics/GET_CREDIT_CARDS_PENDING_SOURCE_NEED_CARDHOLDER_VERIFICATION_USER_OR_VIEWER_ME_COUNT_STATISTICS_INIT'
export const GET_CREDIT_CARDS_PENDING_SOURCE_NEED_CARDHOLDER_VERIFICATION_USER_OR_VIEWER_ME_COUNT_STATISTICS_SUCCESS =
  'data/statistics/GET_CREDIT_CARDS_PENDING_SOURCE_NEED_CARDHOLDER_VERIFICATION_USER_OR_VIEWER_ME_COUNT_STATISTICS_SUCCESS'
export const GET_CREDIT_CARDS_PENDING_SOURCE_NEED_CARDHOLDER_VERIFICATION_USER_OR_VIEWER_ME_COUNT_STATISTICS_FAILURE =
  'data/statistics/GET_CREDIT_CARDS_PENDING_SOURCE_NEED_CARDHOLDER_VERIFICATION_USER_OR_VIEWER_ME_COUNT_STATISTICS_FAILURE'

export interface GetCreditCardsPendingSourceNeedCardholderVerificationUserOrViewerMeCountStatisticsSuccessPayload {
  statistics: Array<{ value: number }>
  statisticNames: string[]
}

export interface GetCreditCardsPendingSourceNeedCardholderVerificationUserOrViewerMeCountStatisticsInitAction {
  type: typeof GET_CREDIT_CARDS_PENDING_SOURCE_NEED_CARDHOLDER_VERIFICATION_USER_OR_VIEWER_ME_COUNT_STATISTICS_INIT
}
export interface GetCreditCardsPendingSourceNeedCardholderVerificationUserOrViewerMeCountStatisticsSuccessAction {
  type: typeof GET_CREDIT_CARDS_PENDING_SOURCE_NEED_CARDHOLDER_VERIFICATION_USER_OR_VIEWER_ME_COUNT_STATISTICS_SUCCESS
  payload: GetCreditCardsPendingSourceNeedCardholderVerificationUserOrViewerMeCountStatisticsSuccessPayload
}
export interface GetCreditCardsPendingSourceNeedCardholderVerificationUserOrViewerMeCountStatisticsFailureAction {
  type: typeof GET_CREDIT_CARDS_PENDING_SOURCE_NEED_CARDHOLDER_VERIFICATION_USER_OR_VIEWER_ME_COUNT_STATISTICS_FAILURE
  payload: unknown
}

export type GetCreditCardsPendingSourceNeedCardholderVerificationUserOrViewerMeCountStatisticsActions =

    | GetCreditCardsPendingSourceNeedCardholderVerificationUserOrViewerMeCountStatisticsInitAction
    | GetCreditCardsPendingSourceNeedCardholderVerificationUserOrViewerMeCountStatisticsSuccessAction
    | GetCreditCardsPendingSourceNeedCardholderVerificationUserOrViewerMeCountStatisticsFailureAction

const createGetCreditCardsPendingSourceNeedCardholderVerificationUserOrViewerMeCountStatisticsInitAction =
  (): GetCreditCardsPendingSourceNeedCardholderVerificationUserOrViewerMeCountStatisticsInitAction => ({
    type: GET_CREDIT_CARDS_PENDING_SOURCE_NEED_CARDHOLDER_VERIFICATION_USER_OR_VIEWER_ME_COUNT_STATISTICS_INIT,
  })
const createGetCreditCardsPendingSourceNeedCardholderVerificationUserOrViewerMeCountStatisticsSuccessAction =
  (
    payload: GetCreditCardsPendingSourceNeedCardholderVerificationUserOrViewerMeCountStatisticsSuccessPayload,
  ): GetCreditCardsPendingSourceNeedCardholderVerificationUserOrViewerMeCountStatisticsSuccessAction => ({
    type: GET_CREDIT_CARDS_PENDING_SOURCE_NEED_CARDHOLDER_VERIFICATION_USER_OR_VIEWER_ME_COUNT_STATISTICS_SUCCESS,
    payload,
  })
const createGetCreditCardsPendingSourceNeedCardholderVerificationUserOrViewerMeCountStatisticsFailureAction =
  (
    response: unknown,
  ): GetCreditCardsPendingSourceNeedCardholderVerificationUserOrViewerMeCountStatisticsFailureAction => ({
    type: GET_CREDIT_CARDS_PENDING_SOURCE_NEED_CARDHOLDER_VERIFICATION_USER_OR_VIEWER_ME_COUNT_STATISTICS_FAILURE,
    payload: response,
  })

export const getCreditCardsPendingSourceNeedCardholderVerificationUserOrViewerMeCountStatistics =
  (): ThunkAction<
    Promise<
      | GetCreditCardsPendingSourceNeedCardholderVerificationUserOrViewerMeCountStatisticsFailureAction
      | GetCreditCardsPendingSourceNeedCardholderVerificationUserOrViewerMeCountStatisticsSuccessAction
    >,
    RootState,
    void,
    GetCreditCardsPendingSourceNeedCardholderVerificationUserOrViewerMeCountStatisticsActions
  > => {
    return async (
      dispatch: ThunkDispatch<
        RootState,
        void,
        GetCreditCardsPendingSourceNeedCardholderVerificationUserOrViewerMeCountStatisticsActions
      >,
      getState: () => RootState,
    ): Promise<
      | GetCreditCardsPendingSourceNeedCardholderVerificationUserOrViewerMeCountStatisticsFailureAction
      | GetCreditCardsPendingSourceNeedCardholderVerificationUserOrViewerMeCountStatisticsSuccessAction
    > => {
      dispatch(
        createGetCreditCardsPendingSourceNeedCardholderVerificationUserOrViewerMeCountStatisticsInitAction(),
      )

      return axios
        .post<GetCreditCardsPendingSourceNeedCardholderVerificationUserOrViewerMeCountStatisticsSuccessPayload>(
          `/statistics`,
          {
            statistics: [
              {
                agg: StatisticAggregateType.Count,
                type: StatisticModelType.CreditCard,
                query: {
                  type: CreditCardType.Source,
                  status: CreditCardStatus.Pending,
                  needsCardholderVerification: true,
                },
              },
            ],
          },
          selectAuthedJsonApiRequestConfig(getState()),
        )
        .then(
          async (response) => {
            return dispatch(
              createGetCreditCardsPendingSourceNeedCardholderVerificationUserOrViewerMeCountStatisticsSuccessAction(
                { ...response.data },
              ),
            )
          },
          (err) => {
            return dispatch(
              createGetCreditCardsPendingSourceNeedCardholderVerificationUserOrViewerMeCountStatisticsFailureAction(
                err.response.data,
              ),
            )
          },
        )
    }
  }

export const GET_CREDIT_CARDS_DELEGATE_COUNT_STATISTICS_INIT =
  'data/statistics/GET_CREDIT_CARDS_DELEGATE_COUNT_STATISTICS_INIT'
export const GET_CREDIT_CARDS_DELEGATE_COUNT_STATISTICS_SUCCESS =
  'data/statistics/GET_CREDIT_CARDS_DELEGATE_COUNT_STATISTICS_SUCCESS'
export const GET_CREDIT_CARDS_DELEGATE_COUNT_STATISTICS_FAILURE =
  'data/statistics/GET_CREDIT_CARDS_DELEGATE_COUNT_STATISTICS_FAILURE'

export interface GetCreditCardsDelegateCountStatisticsSuccessPayload {
  statistics: Array<{ value: number }>
  statisticNames: string[]
}

export interface GetCreditCardsDelegateCountStatisticsInitAction {
  type: typeof GET_CREDIT_CARDS_DELEGATE_COUNT_STATISTICS_INIT
}
export interface GetCreditCardsDelegateCountStatisticsSuccessAction {
  type: typeof GET_CREDIT_CARDS_DELEGATE_COUNT_STATISTICS_SUCCESS
  payload: GetCreditCardsDelegateCountStatisticsSuccessPayload
}
export interface GetCreditCardsDelegateCountStatisticsFailureAction {
  type: typeof GET_CREDIT_CARDS_DELEGATE_COUNT_STATISTICS_FAILURE
  payload: unknown
}

export type GetCreditCardsDelegateCountStatisticsActions =
  | GetCreditCardsDelegateCountStatisticsInitAction
  | GetCreditCardsDelegateCountStatisticsSuccessAction
  | GetCreditCardsDelegateCountStatisticsFailureAction

const createGetCreditCardsDelegateCountStatisticsInitAction =
  (): GetCreditCardsDelegateCountStatisticsInitAction => ({
    type: GET_CREDIT_CARDS_DELEGATE_COUNT_STATISTICS_INIT,
  })
const createGetCreditCardsDelegateCountStatisticsSuccessAction = (
  payload: GetCreditCardsDelegateCountStatisticsSuccessPayload,
): GetCreditCardsDelegateCountStatisticsSuccessAction => ({
  type: GET_CREDIT_CARDS_DELEGATE_COUNT_STATISTICS_SUCCESS,
  payload,
})
const createGetCreditCardsDelegateCountStatisticsFailureAction = (
  response: unknown,
): GetCreditCardsDelegateCountStatisticsFailureAction => ({
  type: GET_CREDIT_CARDS_DELEGATE_COUNT_STATISTICS_FAILURE,
  payload: response,
})

export const getCreditCardsDelegateCountStatistics = (
  cacheTimestamp?: string,
): ThunkAction<
  Promise<
    | GetCreditCardsDelegateCountStatisticsFailureAction
    | GetCreditCardsDelegateCountStatisticsSuccessAction
  >,
  RootState,
  void,
  GetCreditCardsDelegateCountStatisticsActions
> => {
  return async (
    dispatch: ThunkDispatch<
      RootState,
      void,
      GetCreditCardsDelegateCountStatisticsActions
    >,
    getState: () => RootState,
  ): Promise<
    | GetCreditCardsDelegateCountStatisticsFailureAction
    | GetCreditCardsDelegateCountStatisticsSuccessAction
  > => {
    dispatch(createGetCreditCardsDelegateCountStatisticsInitAction())

    return axios
      .post<GetCreditCardsDelegateCountStatisticsSuccessPayload>(
        `/statistics`,
        {
          statistics: [
            {
              agg: StatisticAggregateType.Count,
              type: StatisticModelType.CreditCard,
              query: {
                type: CreditCardType.Delegate,
                cacheTimestamp: cacheTimestamp,
              },
            },
          ],
        },
        selectAuthedJsonApiRequestConfig(getState()),
      )
      .then(
        async (response) => {
          return dispatch(
            createGetCreditCardsDelegateCountStatisticsSuccessAction({
              ...response.data,
            }),
          )
        },
        (err) => {
          return dispatch(
            createGetCreditCardsDelegateCountStatisticsFailureAction(
              err.response.data,
            ),
          )
        },
      )
  }
}

export const GET_CREDIT_CARDS_DELEGATE_OWNER_ME_COUNT_STATISTICS_INIT =
  'data/statistics/GET_CREDIT_CARDS_DELEGATE_OWNER_ME_COUNT_STATISTICS_INIT'
export const GET_CREDIT_CARDS_DELEGATE_OWNER_ME_COUNT_STATISTICS_SUCCESS =
  'data/statistics/GET_CREDIT_CARDS_DELEGATE_OWNER_ME_COUNT_STATISTICS_SUCCESS'
export const GET_CREDIT_CARDS_DELEGATE_OWNER_ME_COUNT_STATISTICS_FAILURE =
  'data/statistics/GET_CREDIT_CARDS_DELEGATE_OWNER_ME_COUNT_STATISTICS_FAILURE'

export interface GetCreditCardsDelegateOwnerMeCountStatisticsSuccessPayload {
  statistics: Array<{ value: number }>
  statisticNames: string[]
}

export interface GetCreditCardsDelegateOwnerMeCountStatisticsInitAction {
  type: typeof GET_CREDIT_CARDS_DELEGATE_OWNER_ME_COUNT_STATISTICS_INIT
}
export interface GetCreditCardsDelegateOwnerMeCountStatisticsSuccessAction {
  type: typeof GET_CREDIT_CARDS_DELEGATE_OWNER_ME_COUNT_STATISTICS_SUCCESS
  payload: GetCreditCardsDelegateOwnerMeCountStatisticsSuccessPayload
}
export interface GetCreditCardsDelegateOwnerMeCountStatisticsFailureAction {
  type: typeof GET_CREDIT_CARDS_DELEGATE_OWNER_ME_COUNT_STATISTICS_FAILURE
  payload: unknown
}

export type GetCreditCardsDelegateOwnerMeCountStatisticsActions =
  | GetCreditCardsDelegateOwnerMeCountStatisticsInitAction
  | GetCreditCardsDelegateOwnerMeCountStatisticsSuccessAction
  | GetCreditCardsDelegateOwnerMeCountStatisticsFailureAction

const createGetCreditCardsDelegateOwnerMeCountStatisticsInitAction =
  (): GetCreditCardsDelegateOwnerMeCountStatisticsInitAction => ({
    type: GET_CREDIT_CARDS_DELEGATE_OWNER_ME_COUNT_STATISTICS_INIT,
  })
const createGetCreditCardsDelegateOwnerMeCountStatisticsSuccessAction = (
  payload: GetCreditCardsDelegateOwnerMeCountStatisticsSuccessPayload,
): GetCreditCardsDelegateOwnerMeCountStatisticsSuccessAction => ({
  type: GET_CREDIT_CARDS_DELEGATE_OWNER_ME_COUNT_STATISTICS_SUCCESS,
  payload,
})
const createGetCreditCardsDelegateOwnerMeCountStatisticsFailureAction = (
  response: unknown,
): GetCreditCardsDelegateOwnerMeCountStatisticsFailureAction => ({
  type: GET_CREDIT_CARDS_DELEGATE_OWNER_ME_COUNT_STATISTICS_FAILURE,
  payload: response,
})

export const getCreditCardsDelegateOwnerMeCountStatistics = (): ThunkAction<
  Promise<
    | GetCreditCardsDelegateOwnerMeCountStatisticsFailureAction
    | GetCreditCardsDelegateOwnerMeCountStatisticsSuccessAction
  >,
  RootState,
  void,
  GetCreditCardsDelegateOwnerMeCountStatisticsActions
> => {
  return async (
    dispatch: ThunkDispatch<
      RootState,
      void,
      GetCreditCardsDelegateOwnerMeCountStatisticsActions
    >,
    getState: () => RootState,
  ): Promise<
    | GetCreditCardsDelegateOwnerMeCountStatisticsFailureAction
    | GetCreditCardsDelegateOwnerMeCountStatisticsSuccessAction
  > => {
    dispatch(createGetCreditCardsDelegateOwnerMeCountStatisticsInitAction())

    return axios
      .post<GetCreditCardsDelegateOwnerMeCountStatisticsSuccessPayload>(
        `/statistics`,
        {
          statistics: [
            {
              agg: StatisticAggregateType.Count,
              type: StatisticModelType.CreditCard,
              query: {
                type: CreditCardType.Delegate,
                owner: 'me',
              },
            },
          ],
        },
        selectAuthedJsonApiRequestConfig(getState()),
      )
      .then(
        async (response) => {
          return dispatch(
            createGetCreditCardsDelegateOwnerMeCountStatisticsSuccessAction({
              ...response.data,
            }),
          )
        },
        (err) => {
          return dispatch(
            createGetCreditCardsDelegateOwnerMeCountStatisticsFailureAction(
              err.response.data,
            ),
          )
        },
      )
  }
}

export const GET_BUDGETS_USER_ME_COUNT_STATISTICS_INIT =
  'data/statistics/GET_CREDIT_CARDS_DELEGATE_USER_ME_COUNT_STATISTICS_INIT'
export const GET_BUDGETS_USER_ME_COUNT_STATISTICS_SUCCESS =
  'data/statistics/GET_CREDIT_CARDS_DELEGATE_USER_ME_COUNT_STATISTICS_SUCCESS'
export const GET_BUDGETS_USER_ME_COUNT_STATISTICS_FAILURE =
  'data/statistics/GET_CREDIT_CARDS_DELEGATE_USER_ME_COUNT_STATISTICS_FAILURE'

export interface GetBudgetsUserMeCountStatisticsSuccessPayload {
  statistics: Array<{ value: number }>
  statisticNames: string[]
}

export interface GetBudgetsUserMeCountStatisticsInitAction {
  type: typeof GET_BUDGETS_USER_ME_COUNT_STATISTICS_INIT
}
export interface GetBudgetsUserMeCountStatisticsSuccessAction {
  type: typeof GET_BUDGETS_USER_ME_COUNT_STATISTICS_SUCCESS
  payload: GetBudgetsUserMeCountStatisticsSuccessPayload
}
export interface GetBudgetsUserMeCountStatisticsFailureAction {
  type: typeof GET_BUDGETS_USER_ME_COUNT_STATISTICS_FAILURE
  payload: unknown
}

export type GetBudgetsUserMeCountStatisticsActions =
  | GetBudgetsUserMeCountStatisticsInitAction
  | GetBudgetsUserMeCountStatisticsSuccessAction
  | GetBudgetsUserMeCountStatisticsFailureAction

const createGetBudgetsUserMeCountStatisticsInitAction =
  (): GetBudgetsUserMeCountStatisticsInitAction => ({
    type: GET_BUDGETS_USER_ME_COUNT_STATISTICS_INIT,
  })
const createGetBudgetsUserMeCountStatisticsSuccessAction = (
  payload: GetBudgetsUserMeCountStatisticsSuccessPayload,
): GetBudgetsUserMeCountStatisticsSuccessAction => ({
  type: GET_BUDGETS_USER_ME_COUNT_STATISTICS_SUCCESS,
  payload,
})
const createGetBudgetsUserMeCountStatisticsFailureAction = (
  response: unknown,
): GetBudgetsUserMeCountStatisticsFailureAction => ({
  type: GET_BUDGETS_USER_ME_COUNT_STATISTICS_FAILURE,
  payload: response,
})

export const getBudgetsUserMeCountStatistics = (): ThunkAction<
  Promise<
    | GetBudgetsUserMeCountStatisticsFailureAction
    | GetBudgetsUserMeCountStatisticsSuccessAction
  >,
  RootState,
  void,
  GetBudgetsUserMeCountStatisticsActions
> => {
  return async (
    dispatch: ThunkDispatch<
      RootState,
      void,
      GetBudgetsUserMeCountStatisticsActions
    >,
    getState: () => RootState,
  ): Promise<
    | GetBudgetsUserMeCountStatisticsFailureAction
    | GetBudgetsUserMeCountStatisticsSuccessAction
  > => {
    dispatch(createGetBudgetsUserMeCountStatisticsInitAction())

    return axios
      .post<GetBudgetsUserMeCountStatisticsSuccessPayload>(
        `/statistics`,
        {
          statistics: [
            {
              agg: StatisticAggregateType.Count,
              type: StatisticModelType.CreditCard,
              query: {
                type: CreditCardType.Delegate,
                status: CreditCardStatus.Active,
                user: 'me',
              },
            },
          ],
        },
        selectAuthedJsonApiRequestConfig(getState()),
      )
      .then(
        async (response) => {
          return dispatch(
            createGetBudgetsUserMeCountStatisticsSuccessAction({
              ...response.data,
            }),
          )
        },
        (err) => {
          return dispatch(
            createGetBudgetsUserMeCountStatisticsFailureAction(
              err.response.data || err.message,
            ),
          )
        },
      )
  }
}

export const GET_CREDIT_CARDS_SOURCE_COUNT_STATISTICS_INIT =
  'data/statistics/GET_CREDIT_CARDS_SOURCE_COUNT_STATISTICS_INIT'
export const GET_CREDIT_CARDS_SOURCE_COUNT_STATISTICS_SUCCESS =
  'data/statistics/GET_CREDIT_CARDS_SOURCE_COUNT_STATISTICS_SUCCESS'
export const GET_CREDIT_CARDS_SOURCE_COUNT_STATISTICS_FAILURE =
  'data/statistics/GET_CREDIT_CARDS_SOURCE_COUNT_STATISTICS_FAILURE'

export interface GetCreditCardsSourceCountStatisticsSuccessPayload {
  statistics: Array<{ value: number }>
  statisticNames: string[]
}

export interface GetCreditCardsSourceCountStatisticsInitAction {
  type: typeof GET_CREDIT_CARDS_SOURCE_COUNT_STATISTICS_INIT
}
export interface GetCreditCardsSourceCountStatisticsSuccessAction {
  type: typeof GET_CREDIT_CARDS_SOURCE_COUNT_STATISTICS_SUCCESS
  payload: GetCreditCardsSourceCountStatisticsSuccessPayload
}
export interface GetCreditCardsSourceCountStatisticsFailureAction {
  type: typeof GET_CREDIT_CARDS_SOURCE_COUNT_STATISTICS_FAILURE
  payload: unknown
}

export type GetCreditCardsSourceCountStatisticsActions =
  | GetCreditCardsSourceCountStatisticsInitAction
  | GetCreditCardsSourceCountStatisticsSuccessAction
  | GetCreditCardsSourceCountStatisticsFailureAction

const createGetCreditCardsSourceCountStatisticsInitAction =
  (): GetCreditCardsSourceCountStatisticsInitAction => ({
    type: GET_CREDIT_CARDS_SOURCE_COUNT_STATISTICS_INIT,
  })
const createGetCreditCardsSourceCountStatisticsSuccessAction = (
  payload: GetCreditCardsSourceCountStatisticsSuccessPayload,
): GetCreditCardsSourceCountStatisticsSuccessAction => ({
  type: GET_CREDIT_CARDS_SOURCE_COUNT_STATISTICS_SUCCESS,
  payload,
})
const createGetCreditCardsSourceCountStatisticsFailureAction = (
  response: unknown,
): GetCreditCardsSourceCountStatisticsFailureAction => ({
  type: GET_CREDIT_CARDS_SOURCE_COUNT_STATISTICS_FAILURE,
  payload: response,
})

export const getCreditCardsSourceCountStatistics = (
  cacheTimestamp?: string,
): ThunkAction<
  Promise<
    | GetCreditCardsSourceCountStatisticsFailureAction
    | GetCreditCardsSourceCountStatisticsSuccessAction
  >,
  RootState,
  void,
  GetCreditCardsSourceCountStatisticsActions
> => {
  return async (
    dispatch: ThunkDispatch<
      RootState,
      void,
      GetCreditCardsSourceCountStatisticsActions
    >,
    getState: () => RootState,
  ): Promise<
    | GetCreditCardsSourceCountStatisticsFailureAction
    | GetCreditCardsSourceCountStatisticsSuccessAction
  > => {
    dispatch(createGetCreditCardsSourceCountStatisticsInitAction())

    return axios
      .post<GetCreditCardsSourceCountStatisticsSuccessPayload>(
        `/statistics`,
        {
          statistics: [
            {
              agg: StatisticAggregateType.Count,
              type: StatisticModelType.CreditCard,
              query: {
                type: CreditCardType.Source,
                cacheTimestamp: cacheTimestamp,
              },
            },
          ],
        },
        selectAuthedJsonApiRequestConfig(getState()),
      )
      .then(
        async (response) => {
          return dispatch(
            createGetCreditCardsSourceCountStatisticsSuccessAction({
              ...response.data,
            }),
          )
        },
        (err) => {
          return dispatch(
            createGetCreditCardsSourceCountStatisticsFailureAction(
              err.response.data,
            ),
          )
        },
      )
  }
}

export const GET_CREDIT_CARDS_SOURCE_USER_OR_VIEWER_COUNT_STATISTICS_INIT =
  'data/statistics/GET_CREDIT_CARDS_SOURCE_USER_OR_VIEWER_COUNT_STATISTICS_INIT'
export const GET_CREDIT_CARDS_SOURCE_USER_OR_VIEWER_COUNT_STATISTICS_SUCCESS =
  'data/statistics/GET_CREDIT_CARDS_SOURCE_USER_OR_VIEWER_COUNT_STATISTICS_SUCCESS'
export const GET_CREDIT_CARDS_SOURCE_USER_OR_VIEWER_COUNT_STATISTICS_FAILURE =
  'data/statistics/GET_CREDIT_CARDS_SOURCE_USER_OR_VIEWER_COUNT_STATISTICS_FAILURE'

export interface GetCreditCardsSourceUserOrViewerCountStatisticsSuccessPayload {
  statistics: Array<{ value: number }>
  statisticNames: string[]
}

export interface GetCreditCardsSourceUserOrViewerCountStatisticsInitAction {
  type: typeof GET_CREDIT_CARDS_SOURCE_USER_OR_VIEWER_COUNT_STATISTICS_INIT
}
export interface GetCreditCardsSourceUserOrViewerCountStatisticsSuccessAction {
  type: typeof GET_CREDIT_CARDS_SOURCE_USER_OR_VIEWER_COUNT_STATISTICS_SUCCESS
  payload: GetCreditCardsSourceUserOrViewerCountStatisticsSuccessPayload
}
export interface GetCreditCardsSourceUserOrViewerCountStatisticsFailureAction {
  type: typeof GET_CREDIT_CARDS_SOURCE_USER_OR_VIEWER_COUNT_STATISTICS_FAILURE
  payload: unknown
}

export type GetCreditCardsSourceUserOrViewerCountStatisticsActions =
  | GetCreditCardsSourceUserOrViewerCountStatisticsInitAction
  | GetCreditCardsSourceUserOrViewerCountStatisticsSuccessAction
  | GetCreditCardsSourceUserOrViewerCountStatisticsFailureAction

const createGetCreditCardsSourceUserOrViewerCountStatisticsInitAction =
  (): GetCreditCardsSourceUserOrViewerCountStatisticsInitAction => ({
    type: GET_CREDIT_CARDS_SOURCE_USER_OR_VIEWER_COUNT_STATISTICS_INIT,
  })
const createGetCreditCardsSourceUserOrViewerCountStatisticsSuccessAction = (
  payload: GetCreditCardsSourceUserOrViewerCountStatisticsSuccessPayload,
): GetCreditCardsSourceUserOrViewerCountStatisticsSuccessAction => ({
  type: GET_CREDIT_CARDS_SOURCE_USER_OR_VIEWER_COUNT_STATISTICS_SUCCESS,
  payload,
})
const createGetCreditCardsSourceUserOrViewerCountStatisticsFailureAction = (
  response: unknown,
): GetCreditCardsSourceUserOrViewerCountStatisticsFailureAction => ({
  type: GET_CREDIT_CARDS_SOURCE_USER_OR_VIEWER_COUNT_STATISTICS_FAILURE,
  payload: response,
})

export const getCreditCardsSourceUserOrViewerCountStatistics = (): ThunkAction<
  Promise<
    | GetCreditCardsSourceUserOrViewerCountStatisticsFailureAction
    | GetCreditCardsSourceUserOrViewerCountStatisticsSuccessAction
  >,
  RootState,
  void,
  GetCreditCardsSourceUserOrViewerCountStatisticsActions
> => {
  return async (
    dispatch: ThunkDispatch<
      RootState,
      void,
      GetCreditCardsSourceUserOrViewerCountStatisticsActions
    >,
    getState: () => RootState,
  ): Promise<
    | GetCreditCardsSourceUserOrViewerCountStatisticsFailureAction
    | GetCreditCardsSourceUserOrViewerCountStatisticsSuccessAction
  > => {
    dispatch(createGetCreditCardsSourceUserOrViewerCountStatisticsInitAction())

    return axios
      .post<GetCreditCardsSourceUserOrViewerCountStatisticsSuccessPayload>(
        `/statistics`,
        {
          statistics: [
            {
              agg: StatisticAggregateType.Count,
              type: StatisticModelType.CreditCard,
              query: {
                type: CreditCardType.Source,
              },
            },
          ],
        },
        selectAuthedJsonApiRequestConfig(getState()),
      )
      .then(
        async (response) => {
          return dispatch(
            createGetCreditCardsSourceUserOrViewerCountStatisticsSuccessAction({
              ...response.data,
            }),
          )
        },
        (err) => {
          return dispatch(
            createGetCreditCardsSourceUserOrViewerCountStatisticsFailureAction(
              err.response.data,
            ),
          )
        },
      )
  }
}

export const GET_CREDIT_CARDS_COUNT_STATISTICS_INIT =
  'data/statistics/GET_CREDIT_CARDS_COUNT_STATISTICS_INIT'
export const GET_CREDIT_CARDS_COUNT_STATISTICS_SUCCESS =
  'data/statistics/GET_CREDIT_CARDS_COUNT_STATISTICS_SUCCESS'
export const GET_CREDIT_CARDS_COUNT_STATISTICS_FAILURE =
  'data/statistics/GET_CREDIT_CARDS_COUNT_STATISTICS_FAILURE'

export interface GetCreditCardsCountStatisticsSuccessPayload {
  statistics: Array<{ value: number }>
  statisticNames: string[]
}

export interface GetCreditCardsCountStatisticsInitAction {
  type: typeof GET_CREDIT_CARDS_COUNT_STATISTICS_INIT
}
export interface GetCreditCardsCountStatisticsSuccessAction {
  type: typeof GET_CREDIT_CARDS_COUNT_STATISTICS_SUCCESS
  payload: GetCreditCardsCountStatisticsSuccessPayload
}
export interface GetCreditCardsCountStatisticsFailureAction {
  type: typeof GET_CREDIT_CARDS_COUNT_STATISTICS_FAILURE
  payload: unknown
}

export type GetCreditCardsCountStatisticsActions =
  | GetCreditCardsCountStatisticsInitAction
  | GetCreditCardsCountStatisticsSuccessAction
  | GetCreditCardsCountStatisticsFailureAction

const createGetCreditCardsCountStatisticsInitAction =
  (): GetCreditCardsCountStatisticsInitAction => ({
    type: GET_CREDIT_CARDS_COUNT_STATISTICS_INIT,
  })
const createGetCreditCardsCountStatisticsSuccessAction = (
  payload: GetCreditCardsCountStatisticsSuccessPayload,
): GetCreditCardsCountStatisticsSuccessAction => ({
  type: GET_CREDIT_CARDS_COUNT_STATISTICS_SUCCESS,
  payload,
})
const createGetCreditCardsCountStatisticsFailureAction = (
  response: unknown,
): GetCreditCardsCountStatisticsFailureAction => ({
  type: GET_CREDIT_CARDS_COUNT_STATISTICS_FAILURE,
  payload: response,
})

export const getCreditCardsCountStatistics = (): ThunkAction<
  Promise<
    | GetCreditCardsCountStatisticsFailureAction
    | GetCreditCardsCountStatisticsSuccessAction
  >,
  RootState,
  void,
  GetCreditCardsCountStatisticsActions
> => {
  return async (
    dispatch: ThunkDispatch<
      RootState,
      void,
      GetCreditCardsCountStatisticsActions
    >,
    getState: () => RootState,
  ): Promise<
    | GetCreditCardsCountStatisticsFailureAction
    | GetCreditCardsCountStatisticsSuccessAction
  > => {
    dispatch(createGetCreditCardsCountStatisticsInitAction())

    return axios
      .post<GetCreditCardsCountStatisticsSuccessPayload>(
        `/statistics`,
        {
          statistics: [
            {
              agg: StatisticAggregateType.Count,
              type: StatisticModelType.CreditCard,
              query: {
                // TODO: remove this once backend has a fix for dealing with empty query objects
                neverGonnaGive: 'youUp',
                neverGonnaLet: 'youDown',
              },
            },
          ],
        },
        selectAuthedJsonApiRequestConfig(getState()),
      )
      .then(
        async (response) => {
          return dispatch(
            createGetCreditCardsCountStatisticsSuccessAction({
              ...response.data,
            }),
          )
        },
        (err) => {
          return dispatch(
            createGetCreditCardsCountStatisticsFailureAction(err.response.data),
          )
        },
      )
  }
}
